import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import Player from "./Player";
import Remote from "./Remote";

const API = "https://radioin-backend-production.up.railway.app";

// ── ESTILOS GLOBAIS ──────────────────────────────────────────────────────────
const G = () => (
  <style>{`
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    :root {
      --bg:       #080d08;
      --bg2:      #0e150e;
      --bg3:      #141e14;
      --border:   #1e2e1e;
      --green:    #00ff7f;
      --green2:   #00cc66;
      --green3:   #00ff7f22;
      --red:      #ff4466;
      --yellow:   #ffd93d;
      --text:     #e8f5e8;
      --muted:    #5a7a5a;
      --font:     'Syne', sans-serif;
      --mono:     'DM Mono', monospace;
    }
    html, body, #root { height: 100%; }
    body { font-family: var(--font); background: var(--bg); color: var(--text); overflow: hidden; }
    ::-webkit-scrollbar { width: 4px; }
    ::-webkit-scrollbar-track { background: var(--bg2); }
    ::-webkit-scrollbar-thumb { background: var(--border); border-radius: 2px; }
    input, textarea, select {
      font-family: var(--font);
      background: var(--bg3);
      border: 1px solid var(--border);
      color: var(--text);
      border-radius: 8px;
      padding: 10px 14px;
      font-size: 14px;
      outline: none;
      transition: border-color .2s;
    }
    input:focus, textarea:focus, select:focus { border-color: var(--green); }
    button { font-family: var(--font); cursor: pointer; }
    @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.4} }
    @keyframes fadeIn { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
    @keyframes spin { to{transform:rotate(360deg)} }
    @keyframes scanline {
      0%{transform:translateY(-100%)} 100%{transform:translateY(100vh)}
    }
    .fadeIn { animation: fadeIn .4s ease forwards; }
  `}</style>
);

// ── API HELPERS ───────────────────────────────────────────────────────────────
async function apiFetch(path, opts = {}, _token = null) {
  const token = _token || localStorage.getItem("radioin_token");
  const headers = { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
  const res = await fetch(API + path, { ...opts, headers: { ...headers, ...opts.headers } });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Erro desconhecido");
  return data;
}

// ── Datas com fuso horário ────────────────────────────────────────────────────
// O campo datetime-local devolve "2026-10-30T08:00" SEM fuso; o servidor entendia como UTC
// (3h de diferença). Converte para ISO com fuso antes de enviar, e de volta ao editar.
function localToISO(v) {
  if (!v) return null;
  const d = new Date(v);
  return isNaN(d) ? null : d.toISOString();
}
function isoToLocal(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const p = n => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

// ── LOGIN ─────────────────────────────────────────────────────────────────────
function Login({ onLogin }) {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "", whatsapp: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function submit() {
    setLoading(true); setError("");
    try {
      const path = mode === "login" ? "/api/auth/login" : "/api/auth/register";
      const body = mode === "login" ? { email: form.email, password: form.password } : form;
      const data = await apiFetch(path, { method: "POST", body: JSON.stringify(body) });
      onLogin(data.token, data.user);
    } catch (e) { setError(e.message); }
    setLoading(false);
  }

  return (
    <div style={{ height: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--bg)", position: "relative", overflow: "hidden" }}>
      {/* grid bg */}
      <div style={{ position: "absolute", inset: 0, backgroundImage: "linear-gradient(var(--border) 1px,transparent 1px),linear-gradient(90deg,var(--border) 1px,transparent 1px)", backgroundSize: "40px 40px", opacity: .4 }} />
      <div style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse 60% 60% at 50% 50%, #00ff7f08, transparent)" }} />

      <div className="fadeIn" style={{ position: "relative", background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 16, padding: "40px 44px", width: 400, boxShadow: "0 0 60px #00ff7f10" }}>
        {/* logo */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 32 }}>
          <div style={{ width: 10, height: 10, borderRadius: "50%", background: "var(--green)", boxShadow: "0 0 12px var(--green)", animation: "pulse 2s infinite" }} />
          <span style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.5px" }}>Radio<span style={{ color: "var(--green)" }}>IN</span></span>
        </div>

        <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 6 }}>{mode === "login" ? "Entrar na conta" : "Criar conta"}</h2>
        <p style={{ fontSize: 13, color: "var(--muted)", marginBottom: 28 }}>{mode === "login" ? "Acesse o painel de controle" : "Configure sua rádio indoor"}</p>

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {mode === "register" && (
            <input placeholder="Seu nome" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={{ width: "100%" }} />
          )}
          <input placeholder="Email" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} style={{ width: "100%" }} />
          <input placeholder="Senha" type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
            style={{ width: "100%" }} onKeyDown={e => e.key === "Enter" && submit()} />
          {mode === "register" && (
            <input placeholder="WhatsApp (opcional)" value={form.whatsapp} onChange={e => setForm(f => ({ ...f, whatsapp: e.target.value }))} style={{ width: "100%" }} />
          )}
        </div>

        {error && <div style={{ marginTop: 12, padding: "10px 14px", background: "#ff446620", border: "1px solid #ff446640", borderRadius: 8, fontSize: 13, color: "var(--red)" }}>{error}</div>}

        <button onClick={submit} disabled={loading} style={{
          marginTop: 20, width: "100%", padding: "13px", background: loading ? "var(--bg3)" : "var(--green)", color: loading ? "var(--muted)" : "#080d08",
          border: "none", borderRadius: 10, fontWeight: 700, fontSize: 15, transition: "all .2s",
          boxShadow: loading ? "none" : "0 0 20px #00ff7f30"
        }}>
          {loading ? "Aguarde..." : mode === "login" ? "Entrar" : "Criar conta"}
        </button>

        <p style={{ textAlign: "center", marginTop: 20, fontSize: 13, color: "var(--muted)" }}>
          {mode === "login" ? "Não tem conta? " : "Já tem conta? "}
          <span onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
            style={{ color: "var(--green)", cursor: "pointer", fontWeight: 600 }}>
            {mode === "login" ? "Criar grátis" : "Entrar"}
          </span>
        </p>
      </div>
    </div>
  );
}

// ── SIDEBAR ────────────────────────────────────────────────────────────────────
const NAV = [
  { id: "overview", icon: "◈", label: "Visão Geral" },
  { id: "stores",   icon: "⊡", label: "Lojas" },
  { id: "playlists", icon: "♫", label: "Playlists" },
  { id: "announcements", icon: "◎", label: "Locuções" },
  { id: "albums",   icon: "♬", label: "Biblioteca Musical", masterOnly: true },
  { id: "playergrade", icon: "◉", label: "Grade do Player" },
  { id: "admin",    icon: "⊕", label: "Clientes", masterOnly: true },
];

function Sidebar({ active, onNav, user, onLogout, newClientsCount }) {
  return (
    <div style={{ width: 220, background: "var(--bg2)", borderRight: "1px solid var(--border)", display: "flex", flexDirection: "column", height: "100vh", flexShrink: 0 }}>
      {/* logo */}
      <div style={{ padding: "24px 20px 20px", borderBottom: "1px solid var(--border)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 9, height: 9, borderRadius: "50%", background: "var(--green)", boxShadow: "0 0 10px var(--green)", animation: "pulse 2s infinite" }} />
          <span style={{ fontSize: 20, fontWeight: 800, letterSpacing: "-0.5px" }}>Radio<span style={{ color: "var(--green)" }}>IN</span></span>
        </div>
        <div style={{ marginTop: 4, fontSize: 11, color: "var(--muted)", fontFamily: "var(--mono)" }}>● AO VIVO</div>
      </div>

      {/* nav */}
      <nav style={{ flex: 1, padding: "12px 10px", overflowY: "auto" }}>
        {NAV.filter(n => !n.masterOnly || user?.plan === 'master').map(n => (
          <button key={n.id} onClick={() => onNav(n.id)} style={{
            width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "10px 12px",
            background: active === n.id ? "var(--green3)" : "transparent",
            border: active === n.id ? "1px solid #00ff7f30" : "1px solid transparent",
            borderRadius: 8, color: active === n.id ? "var(--green)" : "var(--muted)",
            fontSize: 13, fontWeight: active === n.id ? 700 : 500, cursor: "pointer",
            transition: "all .15s", marginBottom: 2, textAlign: "left"
          }}>
            <span style={{ fontSize: 16, width: 20, textAlign: "center" }}>{n.icon}</span>
            <span style={{ flex: 1 }}>{n.label}</span>
            {/* Badge de novos clientes na aba Clientes */}
            {n.id === "admin" && newClientsCount > 0 && (
              <span style={{ background: "var(--red)", color: "white", fontSize: 10, fontWeight: 800, borderRadius: 10, padding: "1px 6px", minWidth: 18, textAlign: "center", animation: "pulse 2s infinite" }}>
                {newClientsCount}
              </span>
            )}
          </button>
        ))}
      </nav>

      {/* user */}
      <div style={{ padding: "16px", borderTop: "1px solid var(--border)" }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user?.name}</div>
        <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user?.email}</div>
        <button onClick={onLogout} style={{ fontSize: 12, color: "var(--muted)", background: "none", border: "1px solid var(--border)", borderRadius: 6, padding: "6px 12px", width: "100%", transition: "all .2s" }}
          onMouseEnter={e => e.target.style.color = "var(--red)"} onMouseLeave={e => e.target.style.color = "var(--muted)"}>
          Sair
        </button>
      </div>
    </div>
  );
}

// ── STAT CARD ─────────────────────────────────────────────────────────────────
function StatCard({ label, value, sub, color = "var(--green)" }) {
  return (
    <div style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 12, padding: "20px 22px", flex: 1 }}>
      <div style={{ fontSize: 11, color: "var(--muted)", fontFamily: "var(--mono)", letterSpacing: 1, marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 30, fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 6 }}>{sub}</div>}
    </div>
  );
}

// ── OVERVIEW ──────────────────────────────────────────────────────────────────
function Overview({ token, user, onNav }) {
  const [stores, setStores] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const isMaster = user?.plan === "master";

  useEffect(() => {
    apiFetch("/api/stores", {}, token)
      .then(d => setStores(Array.isArray(d) ? d : (d.stores || [])))
      .catch(() => {});
  }, [token]);

  // Busca locuções da primeira loja (para mostrar no painel cliente)
  useEffect(() => {
    if (stores.length > 0 && !isMaster) {
      apiFetch(`/api/stores/${stores[0].id}/announcements`, {}, token)
        .then(d => setAnnouncements(Array.isArray(d) ? d : (d.announcements || [])))
        .catch(() => {});
    }
  }, [stores]);

  const planLabel = user?.plan ? user.plan.charAt(0).toUpperCase() + user.plan.slice(1) : "—";
  const planColor = isMaster ? "var(--green)" : user?.plan === "pro" ? "#a78bfa" : "var(--yellow)";
  const activeStores = stores.filter(s => s.is_active);

  // ── PAINEL MASTER ──
  if (isMaster) return (
    <div className="fadeIn" style={{ padding: "32px", overflowY: "auto", height: "100%" }}>
      <h1 style={{ fontSize: 26, fontWeight: 800, marginBottom: 6 }}>Visão Geral</h1>
      <p style={{ color: "var(--muted)", fontSize: 14, marginBottom: 28 }}>Painel de controle RadioIN Master</p>

      <div style={{ display: "flex", gap: 16, marginBottom: 28, flexWrap: "wrap" }}>
        <StatCard label="LOJAS ATIVAS" value={activeStores.length} sub="unidades online" />
        <StatCard label="TOTAL DE LOJAS" value={stores.length} sub="cadastradas" color="var(--text)" />
        <StatCard label="STATUS DA API" value="ON" sub="backend online" color="var(--green)" />
        <StatCard label="PLANO" value={planLabel} sub="RadioIN Master" color={planColor} />
      </div>

      <div style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 12, padding: "24px", marginBottom: 20 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>Lojas Cadastradas</h3>
        {stores.length === 0 ? (
          <div style={{ textAlign: "center", padding: "40px", color: "var(--muted)" }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>⊡</div>
            <p>Nenhuma loja cadastrada ainda.</p>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {stores.map(s => (
              <div key={s.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", background: "var(--bg3)", borderRadius: 8, border: "1px solid var(--border)" }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{s.name}</div>
                  <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 2 }}>{s.type}</div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <a href={`/player?store=${s.id}`} target="_blank" rel="noreferrer"
                    style={{ fontSize: 11, color: "var(--green)", background: "var(--green3)", border: "1px solid #00ff7f30", borderRadius: 6, padding: "3px 10px", textDecoration: "none", fontWeight: 600 }}>
                    ▶ Player
                  </a>
                  <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                    <div style={{ width: 7, height: 7, borderRadius: "50%", background: s.is_active ? "var(--green)" : "var(--red)", boxShadow: s.is_active ? "0 0 8px var(--green)" : "none" }} />
                    <span style={{ fontSize: 12, color: s.is_active ? "var(--green)" : "var(--red)" }}>{s.is_active ? "Ativa" : "Inativa"}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // ── PAINEL CLIENTE ──
  const readyAnn = announcements.filter(a => a.status === "ready");
  const quickActions = [
    { icon: "⊡", label: "Minhas Lojas", desc: "Gerencie suas unidades", page: "stores" },
    { icon: "◎", label: "Locuções", desc: "Crie narações de áudio", page: "announcements" },
    { icon: "⬡", label: "Grade do Player", desc: "Monte a programação", page: "playergrade" },
    { icon: "▦", label: "Grade Semanal", desc: "Horários por dia", page: "schedules" },
  ];

  return (
    <div className="fadeIn" style={{ padding: "32px", overflowY: "auto", height: "100%" }}>
      {/* Boas-vindas */}
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontSize: 26, fontWeight: 800, marginBottom: 4 }}>
          Olá, {user?.name?.split(" ")[0]} 👋
        </h1>
        <p style={{ color: "var(--muted)", fontSize: 14 }}>Bem-vindo ao seu painel RadioIN</p>
      </div>

      {/* Cards de status */}
      <div style={{ display: "flex", gap: 16, marginBottom: 28, flexWrap: "wrap" }}>
        <StatCard label="LOJAS ATIVAS" value={activeStores.length} sub={`de ${stores.length} cadastradas`} />
        <StatCard label="LOCUÇÕES PRONTAS" value={readyAnn.length} sub="áudios disponíveis" color="#00d4ff" />
        <StatCard label="PLANO" value={planLabel} sub={`RadioIN ${planLabel}`} color={planColor} />
      </div>

      {/* Ações rápidas */}
      <div style={{ marginBottom: 28 }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: "var(--muted)", letterSpacing: 1, marginBottom: 14, textTransform: "uppercase" }}>Ações Rápidas</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 12 }}>
          {quickActions.map(a => (
            <button key={a.page} onClick={() => onNav(a.page)} style={{
              background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 12,
              padding: "18px 20px", textAlign: "left", cursor: "pointer", transition: "all .2s",
              color: "var(--text)",
            }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = "#00ff7f40"; e.currentTarget.style.background = "var(--bg3)"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--border)"; e.currentTarget.style.background = "var(--bg2)"; }}>
              <div style={{ fontSize: 24, marginBottom: 10 }}>{a.icon}</div>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4, color: "var(--text)" }}>{a.label}</div>
              <div style={{ fontSize: 12, color: "var(--muted)" }}>{a.desc}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Suas lojas com link do player */}
      <div style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 12, padding: "24px" }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>Suas Lojas</h3>
        {stores.length === 0 ? (
          <div style={{ textAlign: "center", padding: "32px", color: "var(--muted)" }}>
            <div style={{ fontSize: 32, marginBottom: 10 }}>⊡</div>
            <p style={{ marginBottom: 8 }}>Nenhuma loja cadastrada ainda.</p>
            <button onClick={() => onNav("stores")} style={{ fontSize: 13, color: "var(--green)", background: "var(--green3)", border: "1px solid #00ff7f30", borderRadius: 8, padding: "8px 18px", cursor: "pointer", fontWeight: 600 }}>
              + Adicionar Loja
            </button>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {stores.map(s => (
              <div key={s.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px", background: "var(--bg3)", borderRadius: 10, border: "1px solid var(--border)" }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{s.name}</div>
                  <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 2 }}>{s.type}</div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <a href={`/player?store=${s.id}`} target="_blank" rel="noreferrer"
                    style={{ fontSize: 12, color: "var(--green)", background: "var(--green3)", border: "1px solid #00ff7f30", borderRadius: 6, padding: "5px 12px", textDecoration: "none", fontWeight: 700 }}>
                    ▶ Abrir Player
                  </a>
                  <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                    <div style={{ width: 7, height: 7, borderRadius: "50%", background: s.is_active ? "var(--green)" : "var(--red)", boxShadow: s.is_active ? "0 0 8px var(--green)" : "none" }} />
                    <span style={{ fontSize: 12, color: s.is_active ? "var(--green)" : "var(--red)" }}>{s.is_active ? "Ativa" : "Inativa"}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── STORES ────────────────────────────────────────────────────────────────────
// ── PLAYER MONITOR ────────────────────────────────────────────────────────────
function PlayerMonitor({ token, isMaster }) {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [historyStore, setHistoryStore] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const refreshRef = useRef(null);

  function fmtTime(ts) {
    if (!ts) return "—";
    return new Date(ts).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  }
  function fmtDate(ts) {
    if (!ts) return "—";
    return new Date(ts).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
  }
  function fmtDuration(seconds) {
    if (!seconds) return "0min";
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    if (h > 0) return `${h}h ${m}min`;
    return `${m}min`;
  }
  function timeAgo(ts) {
    if (!ts) return "—";
    const diff = Math.floor((Date.now() - new Date(ts)) / 1000);
    if (diff < 60) return `${diff}s atrás`;
    if (diff < 3600) return `${Math.floor(diff/60)}min atrás`;
    if (diff < 86400) return `${Math.floor(diff/3600)}h atrás`;
    return fmtDate(ts);
  }

  const load = useCallback(() => {
    const path = isMaster ? "/api/monitor/stores" : "/api/monitor/my-stores";
    apiFetch(path, {}, token)
      .then(d => { setStores(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [token, isMaster]);

  useEffect(() => {
    load();
    refreshRef.current = setInterval(load, 30000); // atualiza a cada 30s
    return () => clearInterval(refreshRef.current);
  }, [load]);

  // ── Histórico de execução (aba "Execução") ──
  const hojeBR = () => new Date().toLocaleDateString("sv-SE", { timeZone: "America/Sao_Paulo" });
  const [histTab, setHistTab] = useState("exec");       // exec | conexoes
  const [playDate, setPlayDate] = useState(hojeBR());
  const [playData, setPlayData] = useState(null);
  const [loadingPlay, setLoadingPlay] = useState(false);
  const KIND_LABEL = { musica: "🎵 Música", locucao: "🎙 Locução", aviso: "⏰ Aviso", tts: "🗣 TTS ao vivo", veiculo: "🚗 Veículo", voz: "🎤 Voz ao vivo", pausa: "⏸ Pausa" };
  async function loadPlayLog(store, date) {
    setLoadingPlay(true); setPlayData(null);
    const d = await apiFetch(`/api/monitor/stores/${store.id}/playlog?date=${date}`, {}, token).catch(() => null);
    setPlayData(d); setLoadingPlay(false);
  }
  useEffect(() => { if (historyStore && histTab === "exec") loadPlayLog(historyStore, playDate); }, [historyStore, histTab, playDate]); // eslint-disable-line
  function dayOffset(n) { const d = new Date(); d.setDate(d.getDate() - n); return d.toLocaleDateString("sv-SE", { timeZone: "America/Sao_Paulo" }); }
  function fmtHMS(ts) { return ts ? new Date(ts).toLocaleTimeString("pt-BR", { timeZone: "America/Sao_Paulo" }) : "—"; }
  function fmtMinSec(sec) { if (sec == null) return "—"; const m = Math.floor(sec / 60), ss = sec % 60; return `${m}:${String(ss).padStart(2, "0")}`; }
  async function downloadPlayCsv() {
    const res = await fetch(`${API}/api/monitor/stores/${historyStore.id}/playlog?date=${playDate}&format=csv`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) return alert("Não foi possível gerar a planilha");
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `historico_${historyStore.name.replace(/[^\w\- ]/g, "").trim().replace(/\s+/g, "_")}_${playDate}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
  }
  function printPlayLog() {
    if (!playData) return;
    const esc = (v) => String(v == null ? "" : v).replace(/[&<>]/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
    const sm = playData.summary;
    const rowsHtml = playData.items.map(r => `<tr><td>${fmtHMS(r.started_at)}</td><td>${fmtHMS(r.ended_at)}</td><td>${esc((KIND_LABEL[r.kind] || r.kind).replace(/^\S+\s/, ""))}</td><td>${esc(r.title)}</td><td>${esc(r.artist || "")}</td><td>${fmtMinSec(r.played_s)}</td><td>${r.kind === "pausa" ? "" : (r.completed ? "Sim" : "Não")}</td></tr>`).join("");
    const w = window.open("", "_blank");
    if (!w) return alert("Libere as janelas pop-up para imprimir");
    w.document.write(`<html><head><title>Histórico ${esc(playData.store)} ${playData.date}</title><style>body{font-family:Arial,sans-serif;font-size:11px;margin:20px}h2{margin:0 0 4px}table{border-collapse:collapse;width:100%;margin-top:10px}th,td{border:1px solid #ccc;padding:3px 5px;text-align:left}th{background:#eee}</style></head><body>
      <h2>Histórico de execução — ${esc(playData.store)}</h2>
      <div>Data: ${new Date(playData.date + "T12:00:00").toLocaleDateString("pt-BR")} · Músicas: ${sm.musicas} · Locuções: ${sm.locucoes} · Tempo tocando: ${fmtDuration(sm.tocando_s)} · Pausas: ${sm.pausas} (${fmtDuration(sm.pausado_s)})</div>
      <table><tr><th>Início</th><th>Fim</th><th>Tipo</th><th>Título</th><th>Artista</th><th>Tocado</th><th>Até o fim</th></tr>${rowsHtml}</table>
      <script>window.onload=()=>window.print()<\/script></body></html>`);
    w.document.close();
  }

  async function loadHistory(store) {
    setHistTab("exec"); setPlayDate(hojeBR());
    setHistoryStore(store);
    setLoadingHistory(true);
    const d = await apiFetch(`/api/monitor/stores/${store.id}/history`, {}, token).catch(() => []);
    setHistory(Array.isArray(d) ? d : []);
    setLoadingHistory(false);
  }

  const online = stores.filter(s => s.is_online);
  const offline = stores.filter(s => !s.is_online);

  return (
    <div>
      {/* Modal histórico */}
      {historyStore && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.78)", zIndex: 10000, display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={() => setHistoryStore(null)}>
          <div style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 16, padding: "28px", width: histTab === "exec" ? 860 : 520, maxWidth: "94vw", maxHeight: "85vh", overflowY: "auto", boxShadow: "0 24px 60px rgba(0,0,0,0.6)" }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
              <div>
                <div style={{ fontSize: 12, color: "var(--green)", fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", marginBottom: 4 }}>{histTab === "exec" ? "Histórico de execução" : "Conexões — 7 dias"}</div>
                <div style={{ fontWeight: 700, fontSize: 16 }}>{historyStore.name}</div>
              </div>
              <button onClick={() => setHistoryStore(null)} style={{ background: "none", border: "none", color: "var(--muted)", fontSize: 20, cursor: "pointer" }}>✕</button>
            </div>
            <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
              {[["exec", "🎵 Execução"], ["conexoes", "📡 Conexões"]].map(([k, l]) => (
                <button key={k} onClick={() => setHistTab(k)} style={{ padding: "6px 14px", borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: "pointer",
                  background: histTab === k ? "var(--green3)" : "var(--bg3)", color: histTab === k ? "var(--green)" : "var(--muted)", border: `1px solid ${histTab === k ? "#00ff7f30" : "var(--border)"}` }}>{l}</button>
              ))}
            </div>
            {histTab === "exec" ? (
              <div>
                <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
                  {[[0, "Hoje"], [1, "Ontem"], [2, "Anteontem"]].map(([n, l]) => (
                    <button key={n} onClick={() => setPlayDate(dayOffset(n))} style={{ padding: "5px 10px", borderRadius: 6, fontSize: 12, cursor: "pointer",
                      background: playDate === dayOffset(n) ? "var(--green3)" : "var(--bg3)", color: playDate === dayOffset(n) ? "var(--green)" : "var(--text)", border: "1px solid var(--border)" }}>{l}</button>
                  ))}
                  <input type="date" value={playDate} max={hojeBR()} onChange={e => e.target.value && setPlayDate(e.target.value)} style={{ fontSize: 12, padding: "4px 6px" }} />
                  <div style={{ flex: 1 }} />
                  <button onClick={downloadPlayCsv} disabled={!playData?.items?.length} style={{ padding: "6px 12px", borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: "pointer", background: "var(--bg3)", color: "var(--text)", border: "1px solid var(--border)" }}>⬇ Planilha (Excel)</button>
                  <button onClick={printPlayLog} disabled={!playData?.items?.length} style={{ padding: "6px 12px", borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: "pointer", background: "var(--bg3)", color: "var(--text)", border: "1px solid var(--border)" }}>🖨 Imprimir / PDF</button>
                </div>
                {loadingPlay ? (
                  <div style={{ textAlign: "center", padding: 40, color: "var(--muted)" }}>Carregando...</div>
                ) : !playData || !playData.items.length ? (
                  <div style={{ textAlign: "center", padding: 40, color: "var(--muted)" }}>
                    <div style={{ fontSize: 28, marginBottom: 8 }}>🎵</div>
                    <p>Nada registrado neste dia.</p>
                    <p style={{ fontSize: 12, marginTop: 6 }}>O histórico de execução começa a ser gravado a partir da atualização do player (as lojas precisam recarregar a página do player).</p>
                  </div>
                ) : (
                  <>
                    <div style={{ display: "flex", gap: 16, flexWrap: "wrap", padding: "10px 12px", background: "var(--bg3)", borderRadius: 8, marginBottom: 10, fontSize: 12 }}>
                      <span>🎵 <b>{playData.summary.musicas}</b> músicas</span>
                      <span>🎙 <b>{playData.summary.locucoes}</b> locuções/anúncios</span>
                      <span style={{ color: "var(--green)" }}>⏱ tocando <b>{fmtDuration(playData.summary.tocando_s)}</b></span>
                      <span style={{ color: "var(--yellow)" }}>⏸ {playData.summary.pausas} pausas ({fmtDuration(playData.summary.pausado_s)})</span>
                      <span style={{ color: "var(--muted)" }}>{fmtHMS(playData.summary.primeiro)} → {fmtHMS(playData.summary.ultimo)}</span>
                    </div>
                    <div style={{ fontSize: 12 }}>
                      {playData.items.map((r, i) => (
                        <div key={i} style={{ display: "grid", gridTemplateColumns: "70px 70px 110px 1fr 60px 24px", gap: 8, alignItems: "center", padding: "5px 8px", borderBottom: "1px solid rgba(255,255,255,0.05)", background: r.kind === "pausa" ? "#ffd93d0d" : r.kind !== "musica" ? "#00ff7f08" : "transparent" }}>
                          <span style={{ fontFamily: "var(--mono)", color: "var(--green)" }}>{fmtHMS(r.started_at)}</span>
                          <span style={{ fontFamily: "var(--mono)", color: "var(--muted)" }}>{fmtHMS(r.ended_at)}</span>
                          <span style={{ color: "var(--muted)" }}>{KIND_LABEL[r.kind] || r.kind}</span>
                          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={[r.title, r.artist, r.album].filter(Boolean).join(" · ")}>
                            {r.title}{r.artist ? <span style={{ color: "var(--muted)" }}> — {r.artist}</span> : null}
                          </span>
                          <span style={{ fontFamily: "var(--mono)", color: "var(--muted)" }}>{fmtMinSec(r.played_s)}</span>
                          <span title={r.completed ? "Tocou até o fim" : "Interrompida"}>{r.kind === "pausa" ? "" : r.completed ? "✓" : "↷"}</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            ) : loadingHistory ? (
              <div style={{ textAlign: "center", padding: 40, color: "var(--muted)" }}>Carregando...</div>
            ) : history.length === 0 ? (
              <div style={{ textAlign: "center", padding: 40, color: "var(--muted)" }}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>📊</div>
                <p>Nenhuma sessão nos últimos 7 dias.</p>
              </div>
            ) : history.map(day => (
              <div key={day.date} style={{ marginBottom: 16 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", background: "var(--bg3)", borderRadius: 8, marginBottom: 6 }}>
                  <span style={{ fontWeight: 700, fontSize: 13 }}>{new Date(day.date + "T12:00:00").toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "2-digit" })}</span>
                  <div style={{ display: "flex", gap: 12 }}>
                    <span style={{ fontSize: 11, color: "var(--green)" }}>⏱ {fmtDuration(day.total_seconds)}</span>
                    {day.reconnections > 0 && <span style={{ fontSize: 11, color: "var(--yellow)" }}>↺ {day.reconnections} reconexões</span>}
                  </div>
                </div>
                {day.sessions.map(s => (
                  <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 12px", marginBottom: 3, background: "rgba(255,255,255,0.02)", borderRadius: 6, border: "1px solid rgba(255,255,255,0.05)" }}>
                    <span style={{ fontSize: 11, color: "var(--green)", fontFamily: "var(--mono)", minWidth: 40 }}>{fmtTime(s.connected_at)}</span>
                    <span style={{ fontSize: 11, color: "var(--muted)" }}>→</span>
                    <span style={{ fontSize: 11, color: s.disconnected_at ? "var(--muted)" : "var(--green)", fontFamily: "var(--mono)", minWidth: 40 }}>{s.disconnected_at ? fmtTime(s.disconnected_at) : "online"}</span>
                    <span style={{ fontSize: 11, color: "var(--muted)" }}>·</span>
                    <span style={{ fontSize: 11, color: "var(--muted)" }}>{fmtDuration(s.duration_seconds)}</span>
                    {s.last_track && (
                      <>
                        <span style={{ fontSize: 11, color: "var(--muted)" }}>·</span>
                        <span style={{ fontSize: 11, color: "var(--muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>🎵 {s.last_track}</span>
                      </>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <h2 style={{ fontSize: 16, fontWeight: 700 }}>📡 Monitoramento de Players</h2>
          <span style={{ fontSize: 11, padding: "2px 8px", background: "var(--green3)", border: "1px solid #00ff7f30", borderRadius: 20, color: "var(--green)" }}>{online.length} online</span>
          {offline.length > 0 && <span style={{ fontSize: 11, padding: "2px 8px", background: "#ff446620", border: "1px solid #ff446640", borderRadius: 20, color: "var(--red)" }}>{offline.length} offline</span>}
        </div>
        <button onClick={load} style={{ background: "none", border: "1px solid var(--border)", borderRadius: 8, color: "var(--muted)", fontSize: 11, padding: "4px 10px", cursor: "pointer" }}>↻ Atualizar</button>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 32, color: "var(--muted)", fontSize: 13 }}>Carregando...</div>
      ) : stores.length === 0 ? (
        <div style={{ textAlign: "center", padding: 32, color: "var(--muted)", fontSize: 13 }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>📡</div>
          <p>Nenhuma loja cadastrada.</p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {stores.map(s => (
            <div key={s.id} style={{ background: "var(--bg2)", border: `1px solid ${s.is_online ? "#00ff7f30" : "var(--border)"}`, borderRadius: 10, padding: "12px 16px", display: "flex", alignItems: "center", gap: 12 }}>
              {/* Status dot */}
              <div style={{ width: 10, height: 10, borderRadius: "50%", background: s.is_online ? "var(--green)" : "#ff4466", boxShadow: s.is_online ? "0 0 8px var(--green)" : "none", flexShrink: 0, animation: s.is_online ? "pulse 2s infinite" : "none" }} />

              {/* Info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
                  <span style={{ fontWeight: 700, fontSize: 13 }}>{s.name}</span>
                  {isMaster && s.owner_name && <span style={{ fontSize: 11, color: "var(--muted)" }}>· {s.owner_name}</span>}
                  <span style={{ fontSize: 10, padding: "1px 6px", background: s.is_online ? "var(--green3)" : "#ff446620", border: `1px solid ${s.is_online ? "#00ff7f30" : "#ff446640"}`, borderRadius: 10, color: s.is_online ? "var(--green)" : "var(--red)", fontWeight: 700 }}>
                    {s.is_online ? "ONLINE" : "OFFLINE"}
                  </span>
                </div>
                <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
                  {s.is_online ? (
                    <>
                      <span style={{ fontSize: 11, color: "var(--muted)" }}>⏱ Online há {timeAgo(s.session_started)}</span>
                      <span style={{ fontSize: 11, color: "var(--green)" }}>⏰ Hoje: {fmtDuration(s.seconds_online_today)}</span>
                      {s.reconnections_today > 0 && <span style={{ fontSize: 11, color: "var(--yellow)" }}>↺ {s.reconnections_today} reconexões hoje</span>}
                      {s.last_track && <span style={{ fontSize: 11, color: "var(--muted)" }}>🎵 {s.last_track}{s.last_artist ? ` — ${s.last_artist}` : ""}</span>}
                    </>
                  ) : (
                    <>
                      <span style={{ fontSize: 11, color: "var(--muted)" }}>Último acesso: {timeAgo(s.last_seen)}</span>
                      {s.seconds_online_today > 0 && <span style={{ fontSize: 11, color: "var(--muted)" }}>⏰ Hoje: {fmtDuration(s.seconds_online_today)}</span>}
                      {s.last_track && <span style={{ fontSize: 11, color: "var(--muted)" }}>🎵 Última: {s.last_track}</span>}
                    </>
                  )}
                </div>
              </div>

              {/* Botão histórico */}
              <button onClick={() => loadHistory(s)}
                style={{ background: "var(--bg3)", border: "1px solid var(--border)", borderRadius: 8, color: "var(--muted)", fontSize: 11, padding: "5px 10px", cursor: "pointer", flexShrink: 0, whiteSpace: "nowrap" }}
                onMouseEnter={e => e.currentTarget.style.borderColor = "var(--green)"}
                onMouseLeave={e => e.currentTarget.style.borderColor = "var(--border)"}>
                📊 Histórico
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── STORES ────────────────────────────────────────────────────────────────────
function Stores({ token }) {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", type: "", description: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [pinEditing, setPinEditing] = useState({});
  const [pinSaving, setPinSaving] = useState({});
  const [pinMsg, setPinMsg] = useState({});
  const [volEditing, setVolEditing] = useState({});   // { [storeId]: { volume_music, volume_announcements } }
  const [volSaving, setVolSaving] = useState({});
  const [volMsg, setVolMsg] = useState({});
  // Remoto de várias lojas (gerente que cobre folgas)
  const [multiRemote, setMultiRemote] = useState(null); // null = fechado | [ids marcados]
  const [multiCopied, setMultiCopied] = useState(false);
  const multiLink = multiRemote && multiRemote.length
    ? `${window.location.origin}/remote?stores=${stores.filter(x => multiRemote.includes(x.id)).map(x => x.id).join(",")}` : "";

  // Copiar configurações entre lojas
  const [copySrc, setCopySrc] = useState(null);
  const [copyOpts, setCopyOpts] = useState({ volumes: true, hours: true, pin: false, announcements: true, grades: true });
  const [copyTargets, setCopyTargets] = useState([]);
  const [copying, setCopying] = useState(false);
  const [copyResult, setCopyResult] = useState(null);
  const [copyErr, setCopyErr] = useState("");

  // Horário de funcionamento (fora dele o player pausa e não consome Bunny)
  const [hoursEditing, setHoursEditing] = useState({}); // { [storeId]: { start, end, days: [..] } }
  const [hoursMsg, setHoursMsg] = useState({});
  const DIAS_LOJA = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"]; // índice = 0..6 (0=domingo)
  function getHours(s) {
    if (hoursEditing[s.id]) return hoursEditing[s.id];
    const st = s.settings || {};
    return {
      start: st.operating_start || "", end: st.operating_end || "",
      days: (st.operating_days || "0,1,2,3,4,5,6").split(",").filter(Boolean),
    };
  }
  function editHours(s, patch) { setHoursEditing(m => ({ ...m, [s.id]: { ...getHours(s), ...patch } })); }
  async function saveHours(s, clear = false) {
    const h = getHours(s);
    if (!clear && (!h.start || !h.end)) return setHoursMsg(m => ({ ...m, [s.id]: "✗ Informe abertura e fechamento" }));
    if (!clear && h.days.length === 0) return setHoursMsg(m => ({ ...m, [s.id]: "✗ Selecione pelo menos um dia" }));
    try {
      await apiFetch(`/api/stores/${s.id}/hours`, {
        method: "PUT",
        body: JSON.stringify(clear ? { operating_start: null, operating_end: null }
          : { operating_start: h.start, operating_end: h.end, operating_days: h.days.join(",") })
      }, token);
      setHoursEditing(m => { const n = { ...m }; delete n[s.id]; return n; });
      setHoursMsg(m => ({ ...m, [s.id]: clear ? "✓ Horário removido (toca 24h)" : "✓ Horário salvo!" }));
      setTimeout(() => setHoursMsg(m => ({ ...m, [s.id]: "" })), 3000);
      load();
    } catch (e) { setHoursMsg(m => ({ ...m, [s.id]: "✗ " + e.message })); }
  }

  function openCopy(s) {
    setCopySrc(s); setCopyTargets([]); setCopyResult(null); setCopyErr("");
    setCopyOpts({ volumes: true, hours: true, pin: false, announcements: true, grades: true });
  }

  async function doCopy() {
    if (!copyTargets.length) return setCopyErr("Selecione pelo menos uma loja de destino");
    if (!Object.values(copyOpts).some(Boolean)) return setCopyErr("Escolha pelo menos uma configuração");
    if (copyOpts.grades && !window.confirm(`As grades do player das ${copyTargets.length} loja(s) selecionada(s) serão SUBSTITUÍDAS pelas grades de "${copySrc.name}". Continuar?`)) return;
    setCopying(true); setCopyErr(""); setCopyResult(null);
    try {
      const d = await apiFetch(`/api/stores/${copySrc.id}/copy-settings`, {
        method: "POST", body: JSON.stringify({ target_ids: copyTargets, options: copyOpts })
      }, token);
      setCopyResult(d.results || []);
      load();
    } catch (e) { setCopyErr(e.message); }
    setCopying(false);
  }

  const TYPES =["Loja de Roupas", "Restaurante", "Farmácia", "Supermercado", "Academia", "Salão de Beleza", "Clínica", "Hotel", "Outro"];

  const load = useCallback(() => {
    setLoading(true);
    apiFetch("/api/stores", {}, token).then(d => { setStores(Array.isArray(d) ? d : (d.stores || [])); setLoading(false); }).catch(() => setLoading(false));
  }, [token]);

  useEffect(() => { load(); }, [load]);

  async function save() {
    if (!form.name || !form.type) return setError("Nome e tipo são obrigatórios");
    setSaving(true); setError("");
    try {
      await apiFetch("/api/stores", { method: "POST", body: JSON.stringify(form) }, token);
      setForm({ name: "", type: "", description: "" });
      setShowForm(false);
      load();
    } catch (e) { setError(e.message); }
    setSaving(false);
  }

  async function remove(id) {
    if (!window.confirm("Remover esta loja?")) return;
    await apiFetch(`/api/stores/${id}`, { method: "DELETE" }, token);
    load();
  }

  async function savePin(storeId) {
    const pin = pinEditing[storeId] || "";
    if (!/^\d{4}$/.test(pin)) return setPinMsg(m => ({ ...m, [storeId]: "✗ PIN deve ter 4 dígitos numéricos" }));
    setPinSaving(m => ({ ...m, [storeId]: true }));
    try {
      await apiFetch(`/api/stores/${storeId}/pin`, { method: "PUT", body: JSON.stringify({ pin }) }, token);
      setPinMsg(m => ({ ...m, [storeId]: "✓ PIN salvo!" }));
      setTimeout(() => setPinMsg(m => ({ ...m, [storeId]: "" })), 3000);
    } catch (e) { setPinMsg(m => ({ ...m, [storeId]: "✗ " + e.message })); }
    setPinSaving(m => ({ ...m, [storeId]: false }));
  }

  function getVol(s, key) {
    if (volEditing[s.id] && volEditing[s.id][key] !== undefined) return volEditing[s.id][key];
    return s.settings?.[key] ?? (key === "volume_music" ? 80 : 100);
  }

  async function saveVolume(storeId) {
    const vol = volEditing[storeId] || {};
    const store = stores.find(s => s.id === storeId);
    const volume_music = vol.volume_music ?? store?.settings?.volume_music ?? 80;
    const volume_announcements = vol.volume_announcements ?? store?.settings?.volume_announcements ?? 100;
    setVolSaving(m => ({ ...m, [storeId]: true }));
    try {
      await apiFetch(`/api/stores/${storeId}/settings`, { method: "PUT", body: JSON.stringify({ volume_music, volume_announcements }) }, token);
      setVolMsg(m => ({ ...m, [storeId]: "✓ Volumes salvos!" }));
      setTimeout(() => setVolMsg(m => ({ ...m, [storeId]: "" })), 3000);
      load(); // atualiza settings na lista
    } catch (e) { setVolMsg(m => ({ ...m, [storeId]: "✗ " + e.message })); }
    setVolSaving(m => ({ ...m, [storeId]: false }));
  }

  return (
    <div className="fadeIn" style={{ padding: "32px", overflowY: "auto", height: "100%" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 28 }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 800, marginBottom: 4 }}>Lojas</h1>
          <p style={{ color: "var(--muted)", fontSize: 14 }}>Gerencie suas unidades</p>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
        {stores.length > 1 && (
          <button onClick={() => { setMultiRemote(stores.map(x => x.id)); setMultiCopied(false); }} style={{
            padding: "10px 16px", background: "var(--bg3)", color: "var(--text)", border: "1px solid var(--border)",
            borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: "pointer"
          }}>📱 Remoto de várias lojas</button>
        )}
        <button onClick={() => setShowForm(!showForm)} style={{
          padding: "10px 20px", background: showForm ? "var(--bg3)" : "var(--green)", color: showForm ? "var(--muted)" : "#080d08",
          border: "1px solid var(--border)", borderRadius: 10, fontWeight: 700, fontSize: 14, transition: "all .2s"
        }}>
          {showForm ? "Cancelar" : "+ Nova Loja"}
        </button>
        </div>
      </div>

      {/* ── Modal: remoto de várias lojas ── */}
      {multiRemote && (
        <div onClick={() => setMultiRemote(null)} style={{ position: "fixed", inset: 0, background: "#000a", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: "var(--bg2)", border: "1px solid var(--green)", borderRadius: 14, padding: 24, width: "100%", maxWidth: 480, maxHeight: "90vh", overflowY: "auto" }}>
            <h3 style={{ fontSize: 16, fontWeight: 800, marginBottom: 4 }}>📱 Remoto de várias lojas</h3>
            <p style={{ fontSize: 13, color: "var(--muted)", marginBottom: 14, lineHeight: 1.5 }}>
              Gera um link único para quem cobre várias lojas (ex.: gerente de folga). No remoto aparece um seletor de loja; ao trocar, ele usa o mesmo PIN automaticamente (se a loja tiver outro PIN, ele pede).
            </p>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <div style={{ fontSize: 11, color: "var(--muted)", letterSpacing: 1 }}>LOJAS NO LINK</div>
              <button onClick={() => { setMultiRemote(m => m.length === stores.length ? [] : stores.map(x => x.id)); setMultiCopied(false); }}
                style={{ background: "none", border: "none", color: "var(--green)", fontSize: 12, cursor: "pointer", fontWeight: 700 }}>
                {multiRemote.length === stores.length ? "Desmarcar todas" : "Selecionar todas"}
              </button>
            </div>
            <div style={{ maxHeight: 240, overflowY: "auto", border: "1px solid var(--border)", borderRadius: 8 }}>
              {stores.map(x => (
                <label key={x.id} style={{ display: "flex", gap: 10, alignItems: "center", padding: "8px 12px", borderBottom: "1px solid var(--border)", cursor: "pointer", fontSize: 13 }}>
                  <input type="checkbox" checked={multiRemote.includes(x.id)}
                    onChange={e => { setMultiRemote(m => e.target.checked ? [...m, x.id] : m.filter(i => i !== x.id)); setMultiCopied(false); }} />
                  {x.name} {!x.remote_pin && <span style={{ fontSize: 11, color: "var(--yellow)" }}>(sem PIN)</span>}
                </label>
              ))}
            </div>
            {multiRemote.length > 1 ? (
              <div style={{ marginTop: 14 }}>
                <div style={{ fontSize: 11, color: "var(--muted)", wordBreak: "break-all", background: "var(--bg3)", borderRadius: 8, padding: "8px 10px", fontFamily: "var(--mono)" }}>{multiLink}</div>
                <button onClick={() => { navigator.clipboard.writeText(multiLink); setMultiCopied(true); }}
                  style={{ marginTop: 10, width: "100%", padding: "11px 0", background: "var(--green)", border: "none", borderRadius: 8, color: "#080d08", fontWeight: 800, cursor: "pointer" }}>
                  {multiCopied ? "✓ Link copiado! Envie para o gerente" : `Copiar link (${multiRemote.length} lojas)`}
                </button>
              </div>
            ) : (
              <div style={{ marginTop: 12, fontSize: 12, color: "var(--muted)" }}>Marque pelo menos 2 lojas.</div>
            )}
            <button onClick={() => setMultiRemote(null)} style={{ marginTop: 10, width: "100%", padding: "10px 0", background: "var(--bg3)", border: "1px solid var(--border)", borderRadius: 8, color: "var(--muted)", fontWeight: 700, cursor: "pointer" }}>Fechar</button>
          </div>
        </div>
      )}

      {/* ── Modal: copiar configurações ── */}
      {copySrc && (
        <div onClick={() => !copying && setCopySrc(null)} style={{ position: "fixed", inset: 0, background: "#000a", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: "var(--bg2)", border: "1px solid var(--green)", borderRadius: 14, padding: 24, width: "100%", maxWidth: 480, maxHeight: "90vh", overflowY: "auto" }}>
            <h3 style={{ fontSize: 16, fontWeight: 800, marginBottom: 4 }}>📋 Copiar configurações</h3>
            <p style={{ fontSize: 13, color: "var(--muted)", marginBottom: 16 }}>De: <b style={{ color: "var(--green)" }}>{copySrc.name}</b></p>

            <div style={{ fontSize: 11, color: "var(--muted)", letterSpacing: 1, marginBottom: 8 }}>O QUE COPIAR</div>
            {[
              { k: "volumes", label: "🔊 Volumes", hint: "Volume de músicas e locuções" },
              { k: "hours", label: "🕒 Horário de funcionamento", hint: "Abertura, fechamento e dias" },
              { k: "pin", label: "🎛 PIN do remoto", hint: "Mesmo PIN do controle remoto" },
              { k: "announcements", label: "🎙 Locuções", hint: "Adiciona as locuções prontas (sem duplicar, não conta no limite mensal)" },
              { k: "grades", label: "📅 Grades do player", hint: "Substitui as grades da loja de destino" },
            ].map(o => (
              <label key={o.k} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "8px 10px", marginBottom: 6, background: "var(--bg3)", border: "1px solid var(--border)", borderRadius: 8, cursor: "pointer" }}>
                <input type="checkbox" checked={copyOpts[o.k]} onChange={e => setCopyOpts(c => ({ ...c, [o.k]: e.target.checked }))} style={{ marginTop: 3 }} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>{o.label}</div>
                  <div style={{ fontSize: 11, color: "var(--muted)" }}>{o.hint}</div>
                </div>
              </label>
            ))}

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "16px 0 8px" }}>
              <div style={{ fontSize: 11, color: "var(--muted)", letterSpacing: 1 }}>PARA AS LOJAS</div>
              <button onClick={() => {
                const all = stores.filter(x => x.id !== copySrc.id).map(x => x.id);
                setCopyTargets(t => t.length === all.length ? [] : all);
              }} style={{ background: "none", border: "none", color: "var(--green)", fontSize: 12, cursor: "pointer", fontWeight: 700 }}>
                {copyTargets.length === stores.length - 1 ? "Desmarcar todas" : "Selecionar todas"}
              </button>
            </div>
            <div style={{ maxHeight: 200, overflowY: "auto", border: "1px solid var(--border)", borderRadius: 8 }}>
              {stores.filter(x => x.id !== copySrc.id).map(x => (
                <label key={x.id} style={{ display: "flex", gap: 10, alignItems: "center", padding: "8px 12px", borderBottom: "1px solid var(--border)", cursor: "pointer", fontSize: 13 }}>
                  <input type="checkbox" checked={copyTargets.includes(x.id)}
                    onChange={e => setCopyTargets(t => e.target.checked ? [...t, x.id] : t.filter(i => i !== x.id))} />
                  {x.name}
                </label>
              ))}
            </div>

            {copyErr && <div style={{ marginTop: 10, fontSize: 13, color: "var(--red)" }}>{copyErr}</div>}

            {copyResult && (
              <div style={{ marginTop: 12, padding: 10, background: "var(--bg3)", borderRadius: 8, fontSize: 12 }}>
                {copyResult.map(r => (
                  <div key={r.store_id} style={{ color: r.ok ? "var(--green)" : "var(--red)", marginBottom: 3 }}>
                    {r.ok ? "✓" : "✗"} {r.name}
                    {r.ok ? ` — ${r.announcements} locução(ões) nova(s), ${r.grades} grade(s)` : ` — ${r.error || "erro"}`}
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
              <button onClick={() => setCopySrc(null)} disabled={copying}
                style={{ flex: 1, padding: "10px 0", background: "var(--bg3)", border: "1px solid var(--border)", borderRadius: 8, color: "var(--muted)", fontWeight: 700, cursor: "pointer" }}>
                {copyResult ? "Fechar" : "Cancelar"}
              </button>
              <button onClick={doCopy} disabled={copying}
                style={{ flex: 2, padding: "10px 0", background: copying ? "var(--bg3)" : "var(--green)", border: "none", borderRadius: 8, color: copying ? "var(--muted)" : "#080d08", fontWeight: 800, cursor: "pointer" }}>
                {copying ? "Copiando..." : `Copiar para ${copyTargets.length} loja(s)`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Monitoramento de Players ── */}
      <div style={{ marginBottom: 32, background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 14, padding: "20px 24px" }}>
        <PlayerMonitor token={token} isMaster={false} />
      </div>

      {showForm && (
        <div className="fadeIn" style={{ background: "var(--bg2)", border: "1px solid var(--green)", borderRadius: 12, padding: "24px", marginBottom: 24 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 16, color: "var(--green)" }}>Nova Loja</h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
            <input placeholder="Nome da loja *" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={{ width: "100%" }} />
            <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))} style={{ width: "100%" }}>
              <option value="">Tipo de estabelecimento *</option>
              {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <textarea placeholder="Descrição (opcional)" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
            style={{ width: "100%", height: 80, resize: "vertical" }} />
          {error && <div style={{ marginTop: 8, fontSize: 13, color: "var(--red)" }}>{error}</div>}
          <button onClick={save} disabled={saving} style={{
            marginTop: 14, padding: "10px 24px", background: "var(--green)", color: "#080d08",
            border: "none", borderRadius: 8, fontWeight: 700, fontSize: 14
          }}>
            {saving ? "Salvando..." : "Salvar Loja"}
          </button>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: "center", padding: 60, color: "var(--muted)" }}>Carregando...</div>
      ) : stores.length === 0 ? (
        <div style={{ textAlign: "center", padding: 60, color: "var(--muted)" }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>⊡</div>
          <p>Nenhuma loja cadastrada.</p>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16 }}>
          {stores.map(s => (
            <div key={s.id} style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 12, padding: "20px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                <div style={{ width: 36, height: 36, background: "var(--green3)", border: "1px solid #00ff7f30", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>⊡</div>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <div style={{ width: 7, height: 7, borderRadius: "50%", background: s.is_active ? "var(--green)" : "var(--red)", boxShadow: s.is_active ? "0 0 8px var(--green)" : "none" }} />
                  <button onClick={() => remove(s.id)} style={{ background: "none", border: "none", color: "var(--muted)", fontSize: 16, cursor: "pointer", padding: 2 }}
                    onMouseEnter={e => e.target.style.color = "var(--red)"} onMouseLeave={e => e.target.style.color = "var(--muted)"}>×</button>
                </div>
              </div>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>{s.name}</div>
              <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 8 }}>{s.type}</div>
              {s.description && <div style={{ fontSize: 12, color: "var(--muted)", borderTop: "1px solid var(--border)", paddingTop: 10, marginBottom: 10 }}>{s.description}</div>}
              <div style={{ borderTop: "1px solid var(--border)", paddingTop: 10, display: "flex", gap: 6 }}>
                <a href={`/player?store=${s.id}`} target="_blank" rel="noreferrer"
                  style={{ flex: 1, textAlign: "center", padding: "6px 0", background: "var(--green3)", border: "1px solid #00ff7f30", borderRadius: 6, color: "var(--green)", fontSize: 12, fontWeight: 700, textDecoration: "none" }}>
                  ▶ Abrir Player
                </a>
                <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/player?store=${s.id}`); }}
                  style={{ padding: "6px 10px", background: "var(--bg3)", border: "1px solid var(--border)", borderRadius: 6, color: "var(--muted)", fontSize: 12, cursor: "pointer" }}
                  title="Copiar link do player">
                  🔗
                </button>
                {stores.length > 1 && (
                  <button onClick={() => openCopy(s)}
                    style={{ padding: "6px 10px", background: "var(--bg3)", border: "1px solid var(--border)", borderRadius: 6, color: "var(--muted)", fontSize: 12, cursor: "pointer" }}
                    title="Copiar configurações desta loja para outras">
                    📋
                  </button>
                )}
              </div>
              {/* Horário de funcionamento */}
              {(() => { const h = getHours(s); const saved = s.settings?.operating_start; return (
              <div style={{ borderTop: "1px solid var(--border)", paddingTop: 12, marginTop: 4 }}>
                <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 8, letterSpacing: 1 }}>
                  🕒 HORÁRIO DE FUNCIONAMENTO {!saved && <span style={{ color: "var(--muted)", letterSpacing: 0 }}>(não definido — toca 24h)</span>}
                </div>
                <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 8 }}>
                  <input type="time" value={h.start} onChange={e => editHours(s, { start: e.target.value })} style={{ flex: 1, padding: "5px 6px", fontSize: 13 }} />
                  <span style={{ fontSize: 12, color: "var(--muted)" }}>às</span>
                  <input type="time" value={h.end} onChange={e => editHours(s, { end: e.target.value })} style={{ flex: 1, padding: "5px 6px", fontSize: 13 }} />
                </div>
                <div style={{ display: "flex", gap: 3, marginBottom: 8 }}>
                  {DIAS_LOJA.map((d, i) => { const on = h.days.includes(String(i)); return (
                    <button key={i} onClick={() => editHours(s, { days: on ? h.days.filter(x => x !== String(i)) : [...h.days, String(i)] })}
                      style={{ flex: 1, padding: "4px 0", fontSize: 10, fontWeight: 700, borderRadius: 4, cursor: "pointer",
                        background: on ? "var(--green3)" : "var(--bg3)", color: on ? "var(--green)" : "var(--muted)",
                        border: on ? "1px solid #00ff7f50" : "1px solid var(--border)" }}>{d}</button>
                  ); })}
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  <button onClick={() => saveHours(s)}
                    style={{ flex: 1, padding: "6px 0", background: "var(--green3)", border: "1px solid #00ff7f30", borderRadius: 6, color: "var(--green)", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                    Salvar Horário
                  </button>
                  {saved && (
                    <button onClick={() => window.confirm("Remover o horário? O player volta a tocar 24h.") && saveHours(s, true)}
                      style={{ padding: "6px 10px", background: "var(--bg3)", border: "1px solid var(--border)", borderRadius: 6, color: "var(--muted)", fontSize: 12, cursor: "pointer" }}
                      title="Remover horário (tocar 24h)">✕</button>
                  )}
                </div>
                {hoursMsg[s.id] && <div style={{ fontSize: 11, marginTop: 6, color: hoursMsg[s.id].startsWith("✓") ? "var(--green)" : "var(--red)" }}>{hoursMsg[s.id]}</div>}
              </div>
              ); })()}

              {/* PIN do Controle Remoto */}
              <div style={{ borderTop: "1px solid var(--border)", paddingTop: 12, marginTop: 4 }}>
                <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 8, letterSpacing: 1 }}>🎛 CONTROLE REMOTO — PIN</div>
                <div style={{ display: "flex", gap: 6 }}>
                  <input type="text" maxLength={4} placeholder="4 dígitos"
                    value={pinEditing[s.id] ?? (s.remote_pin || "")}
                    onChange={e => setPinEditing(m => ({ ...m, [s.id]: e.target.value.replace(/\D/g, "").slice(0, 4) }))}
                    style={{ flex: 1, padding: "6px 10px", fontSize: 14, letterSpacing: 6, fontFamily: "var(--mono)", textAlign: "center" }} />
                  <button onClick={() => savePin(s.id)} disabled={pinSaving[s.id]}
                    style={{ padding: "6px 12px", background: "var(--green3)", border: "1px solid #00ff7f30", borderRadius: 6, color: "var(--green)", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                    {pinSaving[s.id] ? "..." : "Salvar"}
                  </button>
                  <button onClick={() => navigator.clipboard.writeText(`${window.location.origin}/remote?store=${s.id}`)}
                    style={{ padding: "6px 10px", background: "var(--bg3)", border: "1px solid var(--border)", borderRadius: 6, color: "var(--muted)", fontSize: 12, cursor: "pointer" }}
                    title="Copiar link do remoto">📱</button>
                </div>
                {pinMsg[s.id] && <div style={{ fontSize: 11, marginTop: 6, color: pinMsg[s.id].startsWith("✓") ? "var(--green)" : "var(--red)" }}>{pinMsg[s.id]}</div>}
              </div>

              {/* Volume por tipo */}
              <div style={{ borderTop: "1px solid var(--border)", paddingTop: 12, marginTop: 4 }}>
                <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 10, letterSpacing: 1 }}>🔊 VOLUME</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {[
                    { key: "volume_music", label: "🎵 Músicas" },
                    { key: "volume_announcements", label: "🎙 Locuções" },
                  ].map(({ key, label }) => (
                    <div key={key}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                        <span style={{ fontSize: 12, color: "var(--muted)" }}>{label}</span>
                        <span style={{ fontSize: 12, fontWeight: 700, fontFamily: "var(--mono)", color: "var(--green)" }}>{getVol(s, key)}%</span>
                      </div>
                      <input type="range" min={0} max={100} step={1}
                        value={getVol(s, key)}
                        onChange={e => setVolEditing(m => ({ ...m, [s.id]: { ...(m[s.id] || {}), [key]: Number(e.target.value) } }))}
                        style={{ width: "100%", accentColor: "var(--green)" }} />
                    </div>
                  ))}
                </div>
                <button onClick={() => saveVolume(s.id)} disabled={volSaving[s.id]} style={{
                  marginTop: 10, width: "100%", padding: "7px 0",
                  background: volSaving[s.id] ? "var(--bg3)" : "var(--green3)",
                  border: "1px solid #00ff7f30", borderRadius: 6,
                  color: volSaving[s.id] ? "var(--muted)" : "var(--green)",
                  fontSize: 12, fontWeight: 700, cursor: "pointer"
                }}>
                  {volSaving[s.id] ? "Salvando..." : "Salvar Volumes"}
                </button>
                {volMsg[s.id] && <div style={{ fontSize: 11, marginTop: 6, color: volMsg[s.id].startsWith("✓") ? "var(--green)" : "var(--red)" }}>{volMsg[s.id]}</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── AI PLAYLISTS ───────────────────────────────────────────────────────────────
function AIPlaylists({ token }) {
  const [stores, setStores] = useState([]);
  const [mode, setMode] = useState("create"); // create | suggest
  const [form, setForm] = useState({ storeId: "", timeOfDay: "tarde", genre: "", mood: "", era: "", playlistName: "", qty: 15 });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [created, setCreated] = useState(null); // playlist criada de verdade
  const [error, setError] = useState("");
  const [addingToGrade, setAddingToGrade] = useState(false);
  const [gradeMsg, setGradeMsg] = useState("");

  useEffect(() => {
    apiFetch("/api/stores", {}, token).then(d => setStores(Array.isArray(d) ? d : (d.stores || []))).catch(() => {});
  }, [token]);

  async function generate() {
    if (!form.storeId) return setError("Selecione uma loja");
    setLoading(true); setError(""); setResult(null); setCreated(null);
    try {
      if (mode === "create") {
        // Cria playlist real com faixas da biblioteca
        const data = await apiFetch("/api/ai/create-playlist", {
          method: "POST",
          body: JSON.stringify({ store_id: form.storeId, time_of_day: form.timeOfDay, genre: form.genre || undefined, mood: form.mood || undefined, era: form.era || undefined, playlist_name: form.playlistName || undefined, qty: form.qty || 15 })
        }, token);
        setCreated(data);
      } else {
        // Sugestão textual (modo antigo)
        const store = stores.find(s => s.id === form.storeId);
        const data = await apiFetch("/api/ai/suggest-playlist", {
          method: "POST",
          body: JSON.stringify({ storeType: store.type, timeOfDay: form.timeOfDay, mood: form.mood, description: store.description })
        }, token);
        setResult(data);
      }
    } catch (e) { setError(e.message); }
    setLoading(false);
  }

  const fmtDuration = (s) => { if (!s) return ""; const m = Math.floor(s/60), sec = s%60; return `${m}:${String(sec).padStart(2,"0")}`; };

  return (
    <div className="fadeIn" style={{ padding: "32px", overflowY: "auto", height: "100%" }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 26, fontWeight: 800, marginBottom: 4 }}>IA · Playlists</h1>
        <p style={{ color: "var(--muted)", fontSize: 14 }}>Crie playlists inteligentes usando sua biblioteca musical</p>
      </div>

      {/* Seletor de modo */}
      <div style={{ display: "flex", gap: 8, marginBottom: 24, background: "var(--bg2)", borderRadius: 10, padding: 4, width: "fit-content" }}>
        {[["create","✦ Criar Playlist Real"],["suggest","💡 Só Sugestão"]].map(([id, label]) => (
          <button key={id} onClick={() => { setMode(id); setResult(null); setCreated(null); setError(""); }} style={{
            padding: "8px 20px", background: mode === id ? "var(--green3)" : "transparent",
            border: mode === id ? "1px solid #00ff7f30" : "1px solid transparent",
            borderRadius: 8, color: mode === id ? "var(--green)" : "var(--muted)",
            fontSize: 13, fontWeight: 600, cursor: "pointer", transition: "all .2s",
          }}>{label}</button>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "360px 1fr", gap: 20 }}>
        {/* form */}
        <div style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 12, padding: "24px", height: "fit-content" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 20 }}>
            <span style={{ fontSize: 20, color: "var(--green)" }}>✦</span>
            <span style={{ fontWeight: 700, fontSize: 15 }}>{mode === "create" ? "Criar com sua Biblioteca" : "Configurar Sugestão"}</span>
          </div>

          {mode === "create" && (
            <div style={{ marginBottom: 12, padding: "10px 12px", background: "var(--green3)", border: "1px solid #00ff7f20", borderRadius: 8, fontSize: 12, color: "var(--green)" }}>
              ✦ A IA vai escolher as melhores faixas da sua biblioteca e criar a playlist automaticamente!
            </div>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div>
              <label style={{ fontSize: 12, color: "var(--muted)", display: "block", marginBottom: 6 }}>LOJA</label>
              <select value={form.storeId} onChange={e => setForm(f => ({ ...f, storeId: e.target.value }))} style={{ width: "100%" }}>
                <option value="">Selecionar loja...</option>
                {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, color: "var(--muted)", display: "block", marginBottom: 6 }}>HORÁRIO</label>
              <select value={form.timeOfDay} onChange={e => setForm(f => ({ ...f, timeOfDay: e.target.value }))} style={{ width: "100%" }}>
                <option value="manhã">Manhã</option>
                <option value="tarde">Tarde</option>
                <option value="noite">Noite</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, color: "var(--muted)", display: "block", marginBottom: 6 }}>GÊNERO (opcional)</label>
              <input placeholder="Ex: sertanejo, pagode, pop, rock..." value={form.genre} onChange={e => setForm(f => ({ ...f, genre: e.target.value }))} style={{ width: "100%" }} />
            </div>
            <div>
              <label style={{ fontSize: 12, color: "var(--muted)", display: "block", marginBottom: 6 }}>MOOD (opcional)</label>
              <input placeholder="Ex: animado, relaxante, romântico..." value={form.mood} onChange={e => setForm(f => ({ ...f, mood: e.target.value }))} style={{ width: "100%" }} />
            </div>
            <div>
              <label style={{ fontSize: 12, color: "var(--muted)", display: "block", marginBottom: 6 }}>ÉPOCA (opcional)</label>
              <select value={form.era} onChange={e => setForm(f => ({ ...f, era: e.target.value }))} style={{ width: "100%" }}>
                <option value="">Qualquer época</option>
                <option value="70s">Anos 70</option>
                <option value="80s">Anos 80</option>
                <option value="90s">Anos 90</option>
                <option value="2000s">Anos 2000</option>
                <option value="2010s">Anos 2010</option>
                <option value="2020s">Anos 2020 (atual)</option>
              </select>
            </div>
              <>
                <div>
                  <label style={{ fontSize: 12, color: "var(--muted)", display: "block", marginBottom: 6 }}>NOME DA PLAYLIST (opcional)</label>
                  <input placeholder="A IA vai sugerir um nome..." value={form.playlistName} onChange={e => setForm(f => ({ ...f, playlistName: e.target.value }))} style={{ width: "100%" }} />
                </div>
                <div>
                  <label style={{ fontSize: 12, color: "var(--muted)", display: "block", marginBottom: 6 }}>QUANTIDADE DE FAIXAS</label>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <input type="range" min={5} max={50} value={form.qty} onChange={e => setForm(f => ({ ...f, qty: Number(e.target.value) }))} style={{ flex: 1 }} />
                    <span style={{ fontSize: 14, fontWeight: 700, color: "var(--green)", fontFamily: "var(--mono)", minWidth: 30 }}>{form.qty}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "var(--muted)", marginTop: 2 }}>
                    <span>5 mín</span><span>50 máx</span>
                  </div>
                </div>
              </>
            )}
          </div>

          {error && <div style={{ marginTop: 12, fontSize: 13, color: "var(--red)" }}>{error}</div>}

          <button onClick={generate} disabled={loading} style={{
            marginTop: 20, width: "100%", padding: "12px", background: loading ? "var(--bg3)" : "var(--green)",
            color: loading ? "var(--muted)" : "#080d08", border: "none", borderRadius: 8, fontWeight: 700, fontSize: 14,
            boxShadow: loading ? "none" : "0 0 20px #00ff7f20"
          }}>
            {loading ? (
              <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                <span style={{ display: "inline-block", width: 14, height: 14, border: "2px solid var(--muted)", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 1s linear infinite" }} />
                {mode === "create" ? "Criando playlist..." : "Gerando sugestão..."}
              </span>
            ) : mode === "create" ? "✦ Criar Playlist com IA" : "✦ Gerar Sugestão"}
          </button>
        </div>

        {/* result */}
        <div>
          {!result && !created && !loading && (
            <div style={{ background: "var(--bg2)", border: "1px dashed var(--border)", borderRadius: 12, padding: 60, textAlign: "center", color: "var(--muted)" }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>✦</div>
              <p>{mode === "create" ? "A IA vai montar a playlist usando as faixas da sua biblioteca" : "Configure os parâmetros e clique em Gerar"}</p>
            </div>
          )}

          {/* Playlist criada de verdade */}
          {created && (
            <div className="fadeIn" style={{ background: "var(--bg2)", border: "1px solid var(--green)", borderRadius: 12, padding: "24px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
                <div>
                  <div style={{ fontSize: 11, color: "var(--green)", letterSpacing: 2, marginBottom: 4 }}>✅ PLAYLIST CRIADA COM SUCESSO</div>
                  <h3 style={{ fontSize: 18, fontWeight: 800, color: "var(--green)", marginBottom: 4 }}>{created.playlist?.name}</h3>
                  <p style={{ fontSize: 13, color: "var(--muted)" }}>{created.ai_description}</p>
                </div>
                <div style={{ display: "flex", gap: 8, flexDirection: "column", alignItems: "flex-end" }}>
                  <span style={{ fontSize: 11, padding: "4px 10px", background: "var(--green3)", border: "1px solid #00ff7f30", borderRadius: 20, color: "var(--green)" }}>{created.playlist?.mood}</span>
                  <span style={{ fontSize: 11, padding: "4px 10px", background: "var(--bg3)", border: "1px solid var(--border)", borderRadius: 20 }}>{created.total_tracks} faixas selecionadas</span>
                </div>
              </div>

              <h4 style={{ fontSize: 13, fontWeight: 700, marginBottom: 12, color: "var(--muted)", letterSpacing: 1 }}>FAIXAS SELECIONADAS PELA IA</h4>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {created.tracks?.map((t, i) => (
                  <div key={t.id} style={{ display: "flex", gap: 14, padding: "10px 14px", background: "var(--bg3)", borderRadius: 8, border: "1px solid var(--border)", alignItems: "center" }}>
                    <span style={{ fontSize: 12, color: "var(--muted)", fontFamily: "var(--mono)", minWidth: 24 }}>{String(i + 1).padStart(2, "0")}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{t.title}</div>
                      <div style={{ fontSize: 11, color: "var(--muted)" }}>{t.artist || "—"} · {t.album_name}</div>
                    </div>
                    {t.duration_seconds && <span style={{ fontSize: 11, color: "var(--muted)", fontFamily: "var(--mono)" }}>{fmtDuration(t.duration_seconds)}</span>}
                  </div>
                ))}
              </div>

              {/* Botões de ação */}
              <div style={{ marginTop: 16, display: "flex", gap: 10 }}>
                <button onClick={async () => {
                  if (!created?.tracks?.length || !form.storeId) return;
                  setAddingToGrade(true); setGradeMsg("");
                  try {
                    // Busca grade atual
                    const grade = await apiFetch(`/api/stores/${form.storeId}/player-schedule`, {}, token);
                    const blocks = grade.blocks || [];
                    // Usa o album criado pela IA (album.id)
                    const albumId = created.album?.id || created.playlist?.id;
                    const newBlock = { type: "album", album_id: albumId, name: created.playlist?.name, qty: created.total_tracks };
                    const newBlocks = [...blocks, newBlock];
                    await apiFetch(`/api/stores/${form.storeId}/player-schedule`, { method: "POST", body: JSON.stringify({ blocks: newBlocks }) }, token);
                    setGradeMsg("✅ Adicionado à grade do player!");
                    setTimeout(() => setGradeMsg(""), 4000);
                  } catch (e) { setGradeMsg("✗ Erro: " + e.message); }
                  setAddingToGrade(false);
                }} disabled={addingToGrade} style={{
                  flex: 1, padding: "12px", background: "var(--green)", color: "#080d08",
                  border: "none", borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: "pointer",
                }}>
                  {addingToGrade ? "Adicionando..." : "⬡ Adicionar à Grade do Player"}
                </button>
                <button onClick={() => { setCreated(null); setGradeMsg(""); }} style={{
                  padding: "12px 16px", background: "var(--bg3)", border: "1px solid var(--border)",
                  borderRadius: 8, color: "var(--muted)", fontSize: 13, cursor: "pointer",
                }}>
                  Nova Playlist
                </button>
              </div>
              {gradeMsg && (
                <div style={{ marginTop: 10, padding: "10px 14px", background: gradeMsg.startsWith("✅") ? "var(--green3)" : "#ff446620", border: `1px solid ${gradeMsg.startsWith("✅") ? "#00ff7f30" : "#ff446640"}`, borderRadius: 8, fontSize: 13, color: gradeMsg.startsWith("✅") ? "var(--green)" : "var(--red)", textAlign: "center" }}>
                  {gradeMsg}
                </div>
              )}
              <div style={{ marginTop: 10, padding: "12px 16px", background: "var(--bg3)", borderRadius: 8, fontSize: 12, color: "var(--muted)" }}>
                💡 Playlist também salva em <strong style={{ color: "var(--text)" }}>Playlists</strong> para uso futuro.
              </div>
            </div>
          )}

          {/* Sugestão textual (modo antigo) */}
          {result && (
            <div className="fadeIn" style={{ background: "var(--bg2)", border: "1px solid var(--green)", borderRadius: 12, padding: "24px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
                <div>
                  <h3 style={{ fontSize: 18, fontWeight: 800, color: "var(--green)", marginBottom: 4 }}>{result.playlist_name}</h3>
                  <p style={{ fontSize: 13, color: "var(--muted)" }}>{result.description}</p>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <span style={{ fontSize: 11, padding: "4px 10px", background: "var(--green3)", border: "1px solid #00ff7f30", borderRadius: 20, color: "var(--green)" }}>{result.mood}</span>
                  <span style={{ fontSize: 11, padding: "4px 10px", background: "var(--bg3)", border: "1px solid var(--border)", borderRadius: 20 }}>{result.genre}</span>
                </div>
              </div>
              <h4 style={{ fontSize: 13, fontWeight: 700, marginBottom: 12, color: "var(--muted)" }}>SUGESTÕES (não estão na biblioteca)</h4>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {result.music_suggestions?.map((m, i) => (
                  <div key={i} style={{ display: "flex", gap: 14, padding: "12px 14px", background: "var(--bg3)", borderRadius: 8, border: "1px solid var(--border)" }}>
                    <span style={{ fontSize: 12, color: "var(--muted)", fontFamily: "var(--mono)", minWidth: 20 }}>{String(i + 1).padStart(2, "0")}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{m.title}</div>
                      <div style={{ fontSize: 12, color: "var(--muted)" }}>{m.artist}</div>
                    </div>
                    <div style={{ fontSize: 11, color: "var(--muted)", maxWidth: 200, textAlign: "right" }}>{m.reason}</div>
                  </div>
                ))}
              </div>
              {result.tips && result.tips.length > 0 && (
                <div style={{ marginTop: 16, padding: "14px", background: "var(--green3)", border: "1px solid #00ff7f20", borderRadius: 8 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "var(--green)", marginBottom: 8 }}>DICAS</div>
                  {result.tips.map((t, i) => <div key={i} style={{ fontSize: 12, color: "var(--text)", marginBottom: 4 }}>· {t}</div>)}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── ANNOUNCEMENTS ─────────────────────────────────────────────────────────────
// Limites diários de geração por plano
const DAILY_LIMITS = { starter: 3, pro: 10, enterprise: 30, master: 999 };

const PREFS_KEY = "radioin_announcement_prefs";
function loadPrefs() {
  try { return JSON.parse(localStorage.getItem(PREFS_KEY)) || {}; } catch { return {}; }
}
function savePrefs(prefs) {
  try { localStorage.setItem(PREFS_KEY, JSON.stringify(prefs)); } catch {}
}

function Announcements({ token, user }) {
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState("");
  const [announcements, setAnnouncements] = useState([]);
  const [voices, setVoices] = useState([]);
  const [styles, setStyles] = useState([]);
  const [annCats, setAnnCats] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [editingAnn, setEditingAnn] = useState(null);
  const prefs = loadPrefs();
  const [form, setForm] = useState({ title: "", text_content: "", voice_name: prefs.voice_name || "Aoede", style_id: prefs.style_id || "warm", starts_at: "", ends_at: "", announcement_category_id: "" });
  const [uploadForm, setUploadForm] = useState({ title: "", announcement_category_id: "", starts_at: "", ends_at: "", file: null });
  const [editingUpload, setEditingUpload] = useState(null); // upload sendo editado (só validade)
  const [saving, setSaving] = useState(false);
  const [uploadSaving, setUploadSaving] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [error, setError] = useState("");
  const pollingRef = useRef(null);

  // ── Horário fixo (avisos de encerramento, abertura etc) ──────────────
  const [schedAnn, setSchedAnn] = useState(null);           // locução sendo configurada
  const [schedTimes, setSchedTimes] = useState([""]);       // ["21:45", "21:50"]
  const [schedDays, setSchedDays] = useState([1,2,3,4,5,6,0]);
  const [schedSaving, setSchedSaving] = useState(false);
  const [schedError, setSchedError] = useState("");
  const DIAS_SEMANA = [[1,"Seg"],[2,"Ter"],[3,"Qua"],[4,"Qui"],[5,"Sex"],[6,"Sáb"],[0,"Dom"]];

  function openSchedule(a) {
    setSchedAnn(a);
    setSchedTimes(a.schedule_times ? a.schedule_times.split(",") : [""]);
    setSchedDays(a.schedule_days ? a.schedule_days.split(",").map(Number) : [1,2,3,4,5,6,0]);
    setSchedError("");
  }

  async function saveSchedule(clear = false) {
    const times = clear ? [] : schedTimes.map(t => t.trim()).filter(Boolean);
    if (!clear && times.length && schedDays.length === 0) return setSchedError("Selecione pelo menos um dia.");
    setSchedSaving(true); setSchedError("");
    try {
      await apiFetch(`/api/stores/${selectedStore}/announcements/${schedAnn.id}/schedule`,
        { method: "PUT", body: JSON.stringify({ times, days: schedDays }) }, token);
      setSchedAnn(null);
      fetchAnnouncements();
    } catch (e) { setSchedError(e.message); }
    setSchedSaving(false);
  }

  const [storeList, setStoreList] = useState([]); // lojas do cliente — seletor no topo
  useEffect(() => {
    apiFetch("/api/stores", {}, token).then(d => {
      const list = Array.isArray(d) ? d : (d.stores || []);
      setStoreList(list);
      if (list.length > 0) setSelectedStore(list[0].id);
    }).catch(() => {});
    apiFetch("/api/ai/voices", {}, token).then(d => setVoices(d.voices || [])).catch(() => {});
    apiFetch("/api/ai/styles", {}, token).then(d => setStyles(d.styles || [])).catch(() => {});
    apiFetch("/api/announcement-categories", {}, token).then(d => setAnnCats(d.categories || [])).catch(() => {});
  }, [token]);

  const [monthlyCount, setMonthlyCount] = useState(0);

  function fetchAnnouncements() {
    if (!selectedStore) return;
    apiFetch(`/api/stores/${selectedStore}/announcements`, {}, token)
      .then(d => {
        setAnnouncements(Array.isArray(d) ? d : (d.announcements || []));
        if (d.monthly_count !== undefined) setMonthlyCount(d.monthly_count);
      })
      .catch(() => {});
  }

  useEffect(() => {
    if (!selectedStore) return;
    fetchAnnouncements();
  }, [selectedStore, token]);

  // Polling automático: verifica a cada 5s se há locuções em processamento
  useEffect(() => {
    const hasProcessing = announcements.some(a => a.status === "processing");
    if (hasProcessing && !pollingRef.current) {
      pollingRef.current = setInterval(() => {
        apiFetch(`/api/stores/${selectedStore}/announcements`, {}, token)
          .then(d => {
            const list = Array.isArray(d) ? d : (d.announcements || []);
            setAnnouncements(list);
            if (d.monthly_count !== undefined) setMonthlyCount(d.monthly_count);
            if (!list.some(a => a.status === "processing")) {
              clearInterval(pollingRef.current);
              pollingRef.current = null;
            }
          }).catch(() => {});
      }, 5000);
    }
    if (!hasProcessing && pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    return () => { if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; } };
  }, [announcements, selectedStore, token]);

  // Contagem de locuções geradas este mês — vem do servidor (inclui apagadas)
  const MAX_CHARS = 750;
  const MONTHLY_LIMITS = { starter: 90, pro: 300, enterprise: 900, master: Infinity };
  const monthlyLimit = MONTHLY_LIMITS[user?.plan] ?? 90;
  const generatedThisMonth = monthlyCount; // do servidor — não cai ao apagar
  const limitReached = user?.plan !== "master" && generatedThisMonth >= monthlyLimit;
  const nextReset = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1).toLocaleDateString('pt-BR');

  async function save() {
    if (!selectedStore) return setError("Selecione uma loja");
    if (!form.title || !form.text_content) return setError("Título e texto são obrigatórios");
    if (form.text_content.length > MAX_CHARS) return setError(`Texto muito longo. Máximo ${MAX_CHARS} caracteres.`);
    if (!editingAnn && limitReached) return setError(`Limite mensal atingido (${monthlyLimit} locuções/mês no plano ${user?.plan}). Renova em ${nextReset}.`);
    setSaving(true); setError("");
    try {
      if (editingAnn) {
        // Edição — chama PUT, gera nova e apaga antiga
        await apiFetch(`/api/stores/${selectedStore}/announcements/${editingAnn.id}`, { method: "PUT", body: JSON.stringify({ ...form, starts_at: localToISO(form.starts_at), ends_at: localToISO(form.ends_at) }) }, token);
        setEditingAnn(null);
      } else {
        await apiFetch(`/api/stores/${selectedStore}/announcements`, { method: "POST", body: JSON.stringify({ ...form, starts_at: localToISO(form.starts_at), ends_at: localToISO(form.ends_at) }) }, token);
      }
      savePrefs({ voice_name: form.voice_name, style_id: form.style_id });
      setForm({ title: "", text_content: "", voice_name: form.voice_name, style_id: form.style_id, starts_at: "", ends_at: "", announcement_category_id: "" });
      setShowForm(false);
      fetchAnnouncements();
    } catch (e) { setError(e.message); }
    setSaving(false);
  }

  async function saveUploadEdit() {
    if (!editingUpload) return;
    setUploadSaving(true); setUploadError("");
    try {
      await apiFetch(`/api/stores/${selectedStore}/announcements/${editingUpload.id}`, {
        method: "PUT",
        body: JSON.stringify({
          title: uploadForm.title,
          text_content: editingUpload.text_content || "(comercial)",
          voice_name: "upload",
          style_id: "upload",
          announcement_category_id: uploadForm.announcement_category_id || null,
          starts_at: localToISO(uploadForm.starts_at),
          ends_at: localToISO(uploadForm.ends_at),
        })
      }, token);
      setEditingUpload(null);
      setShowUploadForm(false);
      fetchAnnouncements();
    } catch (e) { setUploadError(e.message); }
    setUploadSaving(false);
  }

  async function saveUpload() {
    if (!selectedStore) return setUploadError("Selecione uma loja");
    if (!uploadForm.title) return setUploadError("Título é obrigatório");
    if (!uploadForm.file) return setUploadError("Selecione um arquivo de áudio");
    setUploadSaving(true); setUploadError("");
    try {
      const fd = new FormData();
      fd.append("audio", uploadForm.file);
      fd.append("title", uploadForm.title);
      if (uploadForm.announcement_category_id) fd.append("announcement_category_id", uploadForm.announcement_category_id);
      if (uploadForm.starts_at) fd.append("starts_at", localToISO(uploadForm.starts_at));
      if (uploadForm.ends_at) fd.append("ends_at", localToISO(uploadForm.ends_at));
      const res = await fetch(`${API}/api/stores/${selectedStore}/announcements/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error || "Erro ao enviar"); }
      setUploadForm({ title: "", announcement_category_id: "", starts_at: "", ends_at: "", file: null });
      setShowUploadForm(false);
      fetchAnnouncements();
    } catch (e) { setUploadError(e.message); }
    setUploadSaving(false);
  }

  async function remove(id) {
    if (!window.confirm("Apagar esta locução?")) return;
    try {
      await apiFetch(`/api/stores/${selectedStore}/announcements/${id}`, { method: "DELETE" }, token);
      setAnnouncements(prev => prev.filter(a => a.id !== id));
    } catch (e) { alert("Erro ao apagar locução"); }
  }

  // Agrupa vozes por gênero para o <select>
  const femaleVoices = voices.filter(v => v.gender === "F");
  const maleVoices   = voices.filter(v => v.gender === "M");
  const fallbackVoices = [
    { name: "Aoede", gender: "F", description: "Feminina, natural e descontraída" },
    { name: "Kore",  gender: "F", description: "Feminina, firme e confiante" },
    { name: "Zephyr",gender: "F", description: "Feminina, brilhante e alegre" },
    { name: "Charon",gender: "M", description: "Masculina, informativa e clara" },
    { name: "Fenrir",gender: "M", description: "Masculina, animada e dinâmica" },
    { name: "Puck",  gender: "M", description: "Masculina, animada e energética" },
  ];
  const hasFemale = femaleVoices.length > 0;
  const hasMale   = maleVoices.length > 0;
  const hasProcessing = announcements.some(a => a.status === "processing");
  const hasProcessingUpload = announcements.some(a => a.status === "processing" && a.source === "upload");
  const hasProcessingAI = announcements.some(a => a.status === "processing" && a.source !== "upload");

  return (
    <div className="fadeIn" style={{ padding: "32px", overflowY: "auto", height: "100%" }}>
      {/* ── Modal: horário fixo da locução ── */}
      {schedAnn && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.78)", zIndex: 10000, display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={() => setSchedAnn(null)}>
          <div style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 16, padding: 28, width: 440, maxWidth: "92vw", boxShadow: "0 24px 60px rgba(0,0,0,0.6)" }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 12, color: "#ffb02e", fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", marginBottom: 4 }}>⏰ Tocar em horário fixo</div>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 6 }}>{schedAnn.title}</div>
            <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 18, lineHeight: 1.5 }}>
              No horário, o player interrompe a música, toca esta locução e volta a música de onde parou.
              Locuções com horário fixo saem do rodízio aleatório.
            </div>

            <div style={{ fontSize: 11, color: "var(--muted)", letterSpacing: 1, marginBottom: 8 }}>HORÁRIOS</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 8 }}>
              {schedTimes.map((t, i) => (
                <div key={i} style={{ display: "flex", gap: 6 }}>
                  <input type="time" value={t}
                    onChange={e => setSchedTimes(ts => ts.map((x, j) => j === i ? e.target.value : x))}
                    style={{ flex: 1, padding: "8px 10px", fontSize: 14 }} />
                  {schedTimes.length > 1 && (
                    <button onClick={() => setSchedTimes(ts => ts.filter((_, j) => j !== i))}
                      style={{ background: "none", border: "1px solid #ff446640", borderRadius: 6, color: "var(--red)", width: 34, cursor: "pointer" }}>✕</button>
                  )}
                </div>
              ))}
            </div>
            <button onClick={() => setSchedTimes(ts => [...ts, ""])}
              style={{ background: "none", border: "1px dashed var(--border)", borderRadius: 6, color: "var(--muted)", fontSize: 12, padding: "6px 10px", cursor: "pointer", marginBottom: 18 }}>
              + Adicionar horário
            </button>

            <div style={{ fontSize: 11, color: "var(--muted)", letterSpacing: 1, marginBottom: 8 }}>DIAS DA SEMANA</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 18 }}>
              {DIAS_SEMANA.map(([v, l]) => {
                const on = schedDays.includes(v);
                return (
                  <button key={v} onClick={() => setSchedDays(ds => on ? ds.filter(d => d !== v) : [...ds, v])}
                    style={{ padding: "6px 11px", fontSize: 12, fontWeight: 700, borderRadius: 6, cursor: "pointer",
                      background: on ? "var(--green3)" : "var(--bg3)", color: on ? "var(--green)" : "var(--muted)",
                      border: `1px solid ${on ? "#00ff7f30" : "var(--border)"}` }}>{l}</button>
                );
              })}
            </div>

            {schedError && <div style={{ fontSize: 13, color: "var(--red)", marginBottom: 12 }}>{schedError}</div>}

            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => saveSchedule(false)} disabled={schedSaving}
                style={{ flex: 1, padding: 11, background: "var(--green)", color: "#080d08", border: "none", borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: "pointer" }}>
                {schedSaving ? "Salvando..." : "Salvar"}
              </button>
              {schedAnn.schedule_times && (
                <button onClick={() => saveSchedule(true)} disabled={schedSaving}
                  style={{ padding: "11px 14px", background: "var(--bg3)", color: "var(--red)", border: "1px solid #ff446640", borderRadius: 10, fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
                  Remover horário
                </button>
              )}
              <button onClick={() => setSchedAnn(null)}
                style={{ padding: "11px 14px", background: "var(--bg3)", color: "var(--muted)", border: "1px solid var(--border)", borderRadius: 10, fontSize: 13, cursor: "pointer" }}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 800, marginBottom: 4 }}>Locuções</h1>
          <p style={{ color: "var(--muted)", fontSize: 14 }}>Crie anúncios com voz por IA ou faça upload de comerciais</p>
          {storeList.length > 1 && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
              <span style={{ fontSize: 13, color: "var(--muted)" }}>Loja:</span>
              <select value={selectedStore}
                onChange={e => { setSelectedStore(e.target.value); setShowForm(false); setShowUploadForm(false); setEditingAnn(null); setEditingUpload(null); setSchedAnn(null); }}
                style={{ minWidth: 240, fontWeight: 700 }}>
                {storeList.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          {/* Botão Upload Comercial */}
          <button onClick={() => { setShowUploadForm(!showUploadForm); setShowForm(false); setUploadError(""); setEditingUpload(null); setUploadForm({ title: "", announcement_category_id: "", starts_at: "", ends_at: "", file: null }); }}
            style={{ padding: "10px 18px", background: showUploadForm ? "var(--bg3)" : "#3b82f620", color: showUploadForm ? "var(--muted)" : "#60a5fa", border: "1px solid #3b82f640", borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
            {showUploadForm ? "Cancelar" : "⬆ Upload Comercial"}
          </button>
          {/* Botão Nova Locução IA */}
          <button onClick={() => { setShowForm(!showForm); setShowUploadForm(false); setEditingAnn(null); setForm({ title: "", text_content: "", voice_name: form.voice_name, style_id: form.style_id, starts_at: "", ends_at: "", announcement_category_id: "" }); }} disabled={limitReached && !showForm} style={{
            padding: "10px 18px", background: showForm ? "var(--bg3)" : limitReached ? "var(--bg3)" : "var(--green)",
            color: showForm || limitReached ? "var(--muted)" : "#080d08",
            border: "1px solid var(--border)", borderRadius: 10, fontWeight: 700, fontSize: 13,
            cursor: limitReached && !showForm ? "not-allowed" : "pointer"
          }}>
            {showForm ? "Cancelar" : "+ Nova Locução IA"}
          </button>
        </div>
      </div>

      {/* Formulário de Upload Comercial / Edição de Validade */}
      {showUploadForm && (
        <div className="fadeIn" style={{ background: "var(--bg2)", border: "1px solid #3b82f640", borderRadius: 12, padding: "24px", marginBottom: 24 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 16, color: "#60a5fa" }}>
            {editingUpload ? "✏️ Editar Comercial" : "⬆ Upload de Comercial"}
          </h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
            <input placeholder="Título do comercial *" value={uploadForm.title}
              onChange={e => setUploadForm(f => ({ ...f, title: e.target.value }))} style={{ width: "100%" }} />
            <select value={uploadForm.announcement_category_id}
              onChange={e => setUploadForm(f => ({ ...f, announcement_category_id: e.target.value }))} style={{ width: "100%" }}>
              <option value="">Categoria (opcional)</option>
              {annCats.map(c => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
            </select>
            <div>
              <label style={{ fontSize: 11, color: "var(--muted)", display: "block", marginBottom: 4 }}>Válido a partir de (opcional)</label>
              <input type="datetime-local" value={uploadForm.starts_at}
                onChange={e => setUploadForm(f => ({ ...f, starts_at: e.target.value }))} style={{ width: "100%" }} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: "var(--muted)", display: "block", marginBottom: 4 }}>Válido até (opcional)</label>
              <input type="datetime-local" value={uploadForm.ends_at}
                onChange={e => setUploadForm(f => ({ ...f, ends_at: e.target.value }))} style={{ width: "100%" }} />
            </div>
          </div>
          {/* Área de upload de arquivo — só para novo upload, não para edição */}
          {!editingUpload && (
            <div style={{ border: "2px dashed #3b82f640", borderRadius: 10, padding: "20px", textAlign: "center", marginBottom: 12, cursor: "pointer", background: uploadForm.file ? "rgba(59,130,246,0.05)" : "transparent" }}
              onClick={() => document.getElementById('upload-audio-input').click()}
              onDragOver={e => e.preventDefault()}
              onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) setUploadForm(prev => ({ ...prev, file: f, title: prev.title || f.name.replace(/\.[^.]+$/, "").replace(/[_]+/g, " ").trim().slice(0, 120) })); }}>
              <input id="upload-audio-input" type="file" accept=".mp3,.wav,.m4a,.aac,audio/*" style={{ display: "none" }}
                onChange={e => { const f = e.target.files[0]; if (f) setUploadForm(prev => ({ ...prev, file: f, title: prev.title || f.name.replace(/\.[^.]+$/, "").replace(/[_]+/g, " ").trim().slice(0, 120) })); }} />
              {uploadForm.file ? (
                <div>
                  <div style={{ fontSize: 24, marginBottom: 4 }}>🎵</div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: "#60a5fa" }}>{uploadForm.file.name}</div>
                  <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 2 }}>{(uploadForm.file.size / 1024 / 1024).toFixed(1)} MB</div>
                </div>
              ) : (
                <div>
                  <div style={{ fontSize: 28, marginBottom: 6 }}>⬆</div>
                  <div style={{ fontSize: 14, color: "var(--muted)" }}>Clique ou arraste o arquivo aqui</div>
                  <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 4 }}>MP3, WAV, M4A ou AAC · máx 50MB</div>
                </div>
              )}
            </div>
          )}
          {uploadError && <div style={{ fontSize: 13, color: "var(--red)", marginBottom: 10 }}>{uploadError}</div>}
          <button onClick={editingUpload ? saveUploadEdit : saveUpload} disabled={uploadSaving}
            style={{ padding: "10px 24px", background: "#3b82f6", color: "#fff", border: "none", borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: "pointer" }}>
            {uploadSaving ? "Salvando..." : editingUpload ? "✓ Salvar" : "⬆ Enviar Comercial"}
          </button>
        </div>
      )}

      {/* Contador mensal */}
      {user?.plan !== "master" && selectedStore && (
        <div style={{ marginBottom: 16, padding: "10px 16px", background: limitReached ? "#ff446615" : "var(--bg2)", border: `1px solid ${limitReached ? "#ff446640" : "var(--border)"}`, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontSize: 13, color: limitReached ? "var(--red)" : "var(--muted)" }}>
            {limitReached ? "⛔ Limite mensal atingido." : `◎ Locuções este mês: `}
            {!limitReached && <strong style={{ color: "var(--text)" }}>{generatedThisMonth} / {monthlyLimit}</strong>}
            {limitReached && <span> Renova em {nextReset}.</span>}
          </span>
          {limitReached && <span style={{ fontSize: 11, color: "var(--muted)" }}>Plano {user?.plan} · {monthlyLimit}/mês</span>}
        </div>
      )}

      {/* Aviso de processamento */}
      {hasProcessing && (
        <div style={{ marginBottom: 16, padding: "10px 16px", background: "#ffd93d10", border: "1px solid #ffd93d30", borderRadius: 8, display: "flex", flexDirection: "column", gap: 6 }}>
          {hasProcessingAI && (
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 18, animation: "spin 1.5s linear infinite", display: "inline-block" }}>⏳</span>
              <span style={{ fontSize: 13, color: "var(--yellow)" }}>Locução sendo gerada pela IA… Isso pode levar alguns segundos. A página atualiza automaticamente.</span>
            </div>
          )}
          {hasProcessingUpload && (
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 18, animation: "spin 1.5s linear infinite", display: "inline-block" }}>⏳</span>
              <span style={{ fontSize: 13, color: "var(--yellow)" }}>Processando comercial… Normalizando áudio e enviando ao CDN. A página atualiza automaticamente.</span>
            </div>
          )}
        </div>
      )}

      {showForm && (
        <div className="fadeIn" style={{ background: "var(--bg2)", border: "1px solid var(--green)", borderRadius: 12, padding: "24px", marginBottom: 24 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: "var(--green)", marginBottom: 16 }}>
            {editingAnn ? "✏️ Editar Locução" : "Nova Locução"}
          </h3>
          {editingAnn && (
            <div style={{ fontSize: 12, color: "var(--yellow)", marginBottom: 12, padding: "8px 12px", background: "#ffd93d10", border: "1px solid #ffd93d30", borderRadius: 8 }}>
              ⚠️ Ao salvar, uma nova locução será gerada e a anterior apagada. Isso conta como uma nova geração.
            </div>
          )}
          {prefs.voice_name && (
            <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
              <span>⭐</span> Usando suas preferências salvas —
              <strong style={{ color: "var(--text)" }}>{prefs.voice_name}</strong> · <strong style={{ color: "var(--text)" }}>{styles.find(s => s.id === prefs.style_id)?.label || prefs.style_id}</strong>
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <input placeholder="Título *" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} style={{ width: "100%" }} />
            <textarea placeholder="Texto da locução *" value={form.text_content} onChange={e => setForm(f => ({ ...f, text_content: e.target.value }))}
              maxLength={MAX_CHARS}
              style={{ width: "100%", height: 100, resize: "vertical", borderColor: form.text_content.length > MAX_CHARS * 0.9 ? "var(--yellow)" : undefined }} />
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: -8 }}>
              <span style={{ fontSize: 11, fontFamily: "var(--mono)", color: form.text_content.length >= MAX_CHARS ? "var(--red)" : form.text_content.length > MAX_CHARS * 0.9 ? "var(--yellow)" : "var(--muted)" }}>
                {form.text_content.length} / {MAX_CHARS} caracteres
              </span>
            </div>

            {/* Seletor de Voz agrupado por gênero */}
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <label style={{ fontSize: 12, color: "var(--muted)", fontWeight: 600 }}>Voz</label>
              <select value={form.voice_name} onChange={e => setForm(f => ({ ...f, voice_name: e.target.value }))} style={{ width: 300 }}>
                {hasFemale || hasMale ? (
                  <>
                    {hasFemale && <optgroup label="── Femininas ──">{femaleVoices.map(v => <option key={v.name} value={v.name}>{v.name} — {v.description}</option>)}</optgroup>}
                    {hasMale && <optgroup label="── Masculinas ──">{maleVoices.map(v => <option key={v.name} value={v.name}>{v.name} — {v.description}</option>)}</optgroup>}
                  </>
                ) : (
                  <>
                    <optgroup label="── Femininas ──">{fallbackVoices.filter(v => v.gender === "F").map(v => <option key={v.name} value={v.name}>{v.name} — {v.description}</option>)}</optgroup>
                    <optgroup label="── Masculinas ──">{fallbackVoices.filter(v => v.gender === "M").map(v => <option key={v.name} value={v.name}>{v.name} — {v.description}</option>)}</optgroup>
                  </>
                )}
              </select>
            </div>

            {/* Seletor de Tom / Estilo */}
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <label style={{ fontSize: 12, color: "var(--muted)", fontWeight: 600 }}>Tom da locução</label>
              <select value={form.style_id} onChange={e => setForm(f => ({ ...f, style_id: e.target.value }))} style={{ width: 300 }}>
                {styles.length > 0 ? styles.map(s => <option key={s.id} value={s.id}>{s.label}</option>) : (
                  <>
                    <option value="warm">Caloroso e amigável</option>
                    <option value="professional">Profissional e neutro</option>
                    <option value="energetic">Animado e energético</option>
                    <option value="calm">Calmo e tranquilo</option>
                    <option value="luxury">Sofisticado e elegante</option>
                    <option value="promotional">Promocional e empolgante</option>
                  </>
                )}
              </select>
            </div>
            {/* Categoria da locução */}
            {annCats.length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <label style={{ fontSize: 12, color: "var(--muted)", fontWeight: 600 }}>Categoria <span style={{ fontWeight: 400 }}>(opcional)</span></label>
                <select value={form.announcement_category_id} onChange={e => setForm(f => ({ ...f, announcement_category_id: e.target.value }))} style={{ width: 300 }}>
                  <option value="">— Sem categoria —</option>
                  {annCats.map(c => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
                </select>
              </div>
            )}

            {/* Validade — início e fim */}
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <label style={{ fontSize: 12, color: "var(--muted)", fontWeight: 600 }}>⏰ Validade <span style={{ fontWeight: 400, color: "var(--muted)" }}>(opcional — sem data = permanente)</span></label>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ fontSize: 12, color: "var(--muted)", minWidth: 24 }}>de</span>
                  <input type="datetime-local" value={form.starts_at} onChange={e => setForm(f => ({ ...f, starts_at: e.target.value }))}
                    style={{ fontSize: 12, padding: "5px 8px", background: "var(--bg3)", border: "1px solid var(--border)", borderRadius: 6, color: "var(--text)" }} />
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ fontSize: 12, color: "var(--muted)", minWidth: 24 }}>até</span>
                  <input type="datetime-local" value={form.ends_at} onChange={e => setForm(f => ({ ...f, ends_at: e.target.value }))}
                    style={{ fontSize: 12, padding: "5px 8px", background: "var(--bg3)", border: "1px solid var(--border)", borderRadius: 6, color: "var(--text)" }} />
                </div>
                {(form.starts_at || form.ends_at) && (
                  <button onClick={() => setForm(f => ({ ...f, starts_at: "", ends_at: "" }))}
                    style={{ fontSize: 11, padding: "3px 10px", background: "none", border: "1px solid var(--border)", borderRadius: 6, color: "var(--muted)", cursor: "pointer" }}>
                    limpar
                  </button>
                )}
              </div>
            </div>
          </div>
          <button onClick={save} disabled={saving || (limitReached && !editingAnn)} style={{
            marginTop: 16, padding: "10px 24px", background: "var(--green)", color: "#080d08", border: "none", borderRadius: 8, fontWeight: 700,
            opacity: saving || (limitReached && !editingAnn) ? 0.5 : 1
          }}>
            {saving ? "⏳ Enviando para IA..." : editingAnn ? "◎ Gerar Nova Versão" : "◎ Gerar Locução"}
          </button>
        </div>
      )}

      {selectedStore && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {announcements.length === 0 ? (
            <div style={{ textAlign: "center", padding: 60, color: "var(--muted)", background: "var(--bg2)", borderRadius: 12 }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>◎</div>
              <p>Nenhuma locução para esta loja.</p>
            </div>
          ) : announcements.map(a => {
            const now = new Date();
            const expired   = a.ends_at   && now > new Date(a.ends_at);
            const notStarted = a.starts_at && now < new Date(a.starts_at);
            const hasValidity = a.starts_at || a.ends_at;
            return (
            <div key={a.id} style={{ background: "var(--bg2)", border: `1px solid ${expired ? "#ff446640" : a.source === "upload" ? "#3b82f630" : "var(--border)"}`, borderRadius: 10, padding: "16px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", opacity: expired ? 0.6 : 1 }}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                  <span style={{ fontWeight: 600, fontSize: 14 }}>{a.title}</span>
                  {a.source === "upload" && <span style={{ fontSize: 10, padding: "1px 7px", background: "#3b82f620", border: "1px solid #3b82f640", borderRadius: 10, color: "#60a5fa", fontWeight: 700 }}>📁 Upload</span>}
                </div>
                {a.source !== "upload" && <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 4 }}>{a.text_content?.slice(0, 80)}...</div>}
                {a.status === "error" && a.error_message && <div style={{ fontSize: 12, color: "var(--red)", marginBottom: 4 }}>⚠ {a.error_message}</div>}
                <div style={{ fontSize: 11, color: "var(--muted)", display: "flex", gap: 8, flexWrap: "wrap", marginTop: 2 }}>
                  {a.source !== "upload" && a.voice_name && <span>🎙 {a.voice_name}</span>}
                  {a.source !== "upload" && a.style_id && <span>🎭 {a.style_id}</span>}
                  {a.duration_seconds && <span>⏱ {a.duration_seconds}s</span>}
                  {a.announcement_category_id && (() => { const cat = annCats.find(c => c.id === a.announcement_category_id); return cat ? <span style={{ color: "var(--yellow)" }}>{cat.icon} {cat.name}</span> : null; })()}
                  {expired    && <span style={{ color: "var(--red)" }}>● expirada</span>}
                  {notStarted && <span style={{ color: "var(--yellow)" }}>● agendada · início {new Date(a.starts_at).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>}
                  {hasValidity && !expired && !notStarted && a.ends_at && (
                    <span style={{ color: "var(--green)" }}>● válida até {new Date(a.ends_at).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>
                  )}
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                {a.status === "processing" ? (
                  <span style={{ fontSize: 12, padding: "3px 10px", background: "#ffd93d15", border: "1px solid #ffd93d30", borderRadius: 20, color: "var(--yellow)", display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ animation: "spin 1.5s linear infinite", display: "inline-block" }}>⏳</span> Gerando…
                  </span>
                ) : (
                  <span style={{ fontSize: 11, padding: "3px 10px", background: a.status === "ready" ? "var(--green3)" : "var(--bg3)", border: `1px solid ${a.status === "ready" ? "#00ff7f30" : "var(--border)"}`, borderRadius: 20, color: a.status === "ready" ? "var(--green)" : "var(--muted)" }}>
                    {a.status === "ready" ? "✓ Pronto" : a.status}
                  </span>
                )}
                {a.bunny_url && <audio controls src={a.bunny_url} style={{ height: 28 }} />}
                {a.status === "ready" && a.source !== "upload" && (
                  <button onClick={() => {
                    setEditingAnn(a);
                    setForm({ title: a.title, text_content: a.text_content, voice_name: a.voice_name || "Aoede", style_id: a.style_id || "warm", starts_at: isoToLocal(a.starts_at), ends_at: isoToLocal(a.ends_at), announcement_category_id: a.announcement_category_id || "" });
                    setShowForm(true);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }} style={{ background: "none", border: "1px solid var(--border)", borderRadius: 6, color: "var(--muted)", fontSize: 14, width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}
                    title="Editar locução"
                    onMouseEnter={e => { e.currentTarget.style.background = "var(--bg3)"; e.currentTarget.style.color = "var(--green)"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = "none"; e.currentTarget.style.color = "var(--muted)"; }}>✏️</button>
                )}
                {a.status === "ready" && a.source === "upload" && (
                  <button onClick={() => {
                    setEditingUpload(a);
                    setUploadForm({ title: a.title, announcement_category_id: a.announcement_category_id || "", starts_at: isoToLocal(a.starts_at), ends_at: isoToLocal(a.ends_at), file: null });
                    setShowUploadForm(true);
                    setShowForm(false);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }} style={{ background: "none", border: "1px solid var(--border)", borderRadius: 6, color: "var(--muted)", fontSize: 14, width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}
                    title="Editar validade"
                    onMouseEnter={e => { e.currentTarget.style.background = "var(--bg3)"; e.currentTarget.style.color = "#60a5fa"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = "none"; e.currentTarget.style.color = "var(--muted)"; }}>✏️</button>
                )}
                {a.status === "ready" && (
                  <button onClick={() => openSchedule(a)}
                    style={{ background: a.schedule_times ? "#ffb02e20" : "none", border: `1px solid ${a.schedule_times ? "#ffb02e80" : "var(--border)"}`, borderRadius: 6, color: a.schedule_times ? "#ffb02e" : "var(--muted)", fontSize: a.schedule_times ? 11 : 14, fontWeight: 700, minWidth: 30, height: 30, padding: a.schedule_times ? "0 8px" : 0, display: "flex", alignItems: "center", justifyContent: "center", gap: 4, cursor: "pointer", flexShrink: 0, whiteSpace: "nowrap" }}
                    title={a.schedule_times ? `Toca às ${a.schedule_times.replace(/,/g, ", ")}` : "Tocar em horário fixo (ex: aviso de encerramento)"}>
                    ⏰{a.schedule_times ? " " + a.schedule_times.split(",")[0] + (a.schedule_times.includes(",") ? "+" : "") : ""}
                  </button>
                )}
                <button onClick={() => remove(a.id)} style={{ background: "none", border: "1px solid #ff446640", borderRadius: 6, color: "var(--red)", fontSize: 16, width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", transition: "all .2s", flexShrink: 0 }}
                  title="Apagar locução"
                  onMouseEnter={e => e.currentTarget.style.background = "#ff446620"}
                  onMouseLeave={e => e.currentTarget.style.background = "none"}>✕</button>
              </div>
            </div>
          );})}
        </div>
      )}
    </div>
  );
}

// ── SCHEDULES ─────────────────────────────────────────────────────────────────
function Schedules({ token }) {
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState("");
  const [schedules, setSchedules] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", type: "playlist", start_time: "08:00", end_time: "18:00", days_of_week: [1, 2, 3, 4, 5] });
  const [saving, setSaving] = useState(false);

  const DAYS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

  useEffect(() => {
    apiFetch("/api/stores", {}, token).then(d => {
      const list = Array.isArray(d) ? d : (d.stores || []);
      if (list.length > 0) setSelectedStore(list[0].id);
    }).catch(() => {});
  }, [token]);

  useEffect(() => {
    if (!selectedStore) return;
    apiFetch(`/api/stores/${selectedStore}/schedules`, {}, token).then(d => setSchedules(Array.isArray(d) ? d : (d.schedules || []))).catch(() => {});
  }, [selectedStore, token]);

  function toggleDay(d) {
    setForm(f => ({ ...f, days_of_week: f.days_of_week.includes(d) ? f.days_of_week.filter(x => x !== d) : [...f.days_of_week, d] }));
  }

  async function save() {
    if (!selectedStore || !form.title) return;
    setSaving(true);
    try {
      await apiFetch(`/api/stores/${selectedStore}/schedules`, { method: "POST", body: JSON.stringify(form) }, token);
      setShowForm(false);
      apiFetch(`/api/stores/${selectedStore}/schedules`, {}, token).then(d => setSchedules(Array.isArray(d) ? d : (d.schedules || [])));
    } catch (e) { console.error(e); }
    setSaving(false);
  }

  return (
    <div className="fadeIn" style={{ padding: "32px", overflowY: "auto", height: "100%" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 800, marginBottom: 4 }}>Grade Semanal</h1>
          <p style={{ color: "var(--muted)", fontSize: 14 }}>Programe sua rádio por horário</p>
        </div>
        <button onClick={() => setShowForm(!showForm)} style={{
          padding: "10px 20px", background: showForm ? "var(--bg3)" : "var(--green)", color: showForm ? "var(--muted)" : "#080d08",
          border: "1px solid var(--border)", borderRadius: 10, fontWeight: 700, fontSize: 14
        }}>
          {showForm ? "Cancelar" : "+ Novo Agendamento"}
        </button>
      </div>

      {showForm && (
        <div className="fadeIn" style={{ background: "var(--bg2)", border: "1px solid var(--green)", borderRadius: 12, padding: "24px", marginBottom: 24 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: "var(--green)", marginBottom: 16 }}>Novo Agendamento</h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 16 }}>
            <input placeholder="Título *" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
            <input type="time" value={form.start_time} onChange={e => setForm(f => ({ ...f, start_time: e.target.value }))} />
            <input type="time" value={form.end_time} onChange={e => setForm(f => ({ ...f, end_time: e.target.value }))} />
          </div>
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 8 }}>DIAS DA SEMANA</div>
            <div style={{ display: "flex", gap: 8 }}>
              {DAYS.map((d, i) => (
                <button key={i} onClick={() => toggleDay(i)} style={{
                  padding: "6px 12px", borderRadius: 6, fontSize: 12, fontWeight: 600,
                  background: form.days_of_week.includes(i) ? "var(--green)" : "var(--bg3)",
                  color: form.days_of_week.includes(i) ? "#080d08" : "var(--muted)",
                  border: `1px solid ${form.days_of_week.includes(i) ? "var(--green)" : "var(--border)"}`,
                  cursor: "pointer", transition: "all .15s"
                }}>{d}</button>
              ))}
            </div>
          </div>
          <button onClick={save} disabled={saving} style={{
            padding: "10px 24px", background: "var(--green)", color: "#080d08", border: "none", borderRadius: 8, fontWeight: 700
          }}>
            {saving ? "Salvando..." : "Salvar"}
          </button>
        </div>
      )}

      {selectedStore && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {schedules.length === 0 ? (
            <div style={{ textAlign: "center", padding: 60, color: "var(--muted)", background: "var(--bg2)", borderRadius: 12 }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>▦</div>
              <p>Nenhum agendamento para esta loja.</p>
            </div>
          ) : schedules.map(s => (
            <div key={s.id} style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 10, padding: "16px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 6 }}>{s.title}</div>
                <div style={{ display: "flex", gap: 6 }}>
                  {DAYS.map((d, i) => (
                    <span key={i} style={{ fontSize: 11, padding: "2px 8px", borderRadius: 4, background: s.days_of_week?.includes(i) ? "var(--green3)" : "var(--bg3)", color: s.days_of_week?.includes(i) ? "var(--green)" : "var(--muted)", border: `1px solid ${s.days_of_week?.includes(i) ? "#00ff7f20" : "var(--border)"}` }}>{d}</span>
                  ))}
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontFamily: "var(--mono)", fontSize: 15, color: "var(--green)", fontWeight: 600 }}>{s.start_time} — {s.end_time}</div>
                <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>{s.type}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── PLAYLISTS ─────────────────────────────────────────────────────────────────
function PlaylistEditor({ token, playlist, storeId, onBack }) {
  const [tracks, setTracks] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [categories, setCategories] = useState([]);
  const [openCats, setOpenCats] = useState({});         // acordeão — fechado por padrão
  const [albumTracks, setAlbumTracks] = useState({});
  const [playing, setPlaying] = useState(null);
  const [previewProgress, setPreviewProgress] = useState(0);   // 0–100
  const [previewTime, setPreviewTime] = useState(0);
  const [previewDuration, setPreviewDuration] = useState(0);
  const [previewError, setPreviewError] = useState(false); // URL existe mas áudio falhou
  const [loading, setLoading] = useState(true);
  const [searchLib, setSearchLib] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const audioRef = useRef(null);
  const progressInterval = useRef(null);
  const [trackPopup, setTrackPopup] = useState(null); // faixa aberta no popup
  const [lastSelectedTrack, setLastSelectedTrack] = useState(null); // última faixa clicada
  const [justAdded, setJustAdded] = useState(null); // feedback visual ao adicionar
  const libScrollRef = useRef(null); // ref do container scrollável da biblioteca
  const lastScrollPos = useRef(0); // posição do scroll antes de abrir popup

  const loadTracks = () => {
    apiFetch(`/api/stores/${storeId}/playlists/${playlist.id}/album-tracks`, {}, token)
      .then(d => { setTracks(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    loadTracks();
    apiFetch("/api/albums", {}, token).then(d => setAlbums((Array.isArray(d) ? d : []).filter(a => !a.is_playlist))).catch(() => {});
    apiFetch("/api/categories", {}, token).then(d => setCategories(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);

  async function loadAlbumTracks(albumId) {
    if (albumTracks[albumId]) return;
    const d = await apiFetch(`/api/albums/${albumId}/tracks`, {}, token).catch(() => []);
    setAlbumTracks(p => ({ ...p, [albumId]: Array.isArray(d) ? d : [] }));
  }

  // Busca faixas na biblioteca por título ou artista
  useEffect(() => {
    if (!searchLib.trim()) { setSearchResults([]); return; }
    setSearching(true);
    const q = searchLib.toLowerCase();
    // Busca em todos os albumTracks já carregados + carrega novos se necessário
    const results = [];
    Object.entries(albumTracks).forEach(([albumId, tracks]) => {
      const album = albums.find(a => a.id === albumId);
      tracks.forEach(t => {
        if (t.title?.toLowerCase().includes(q) || t.artist?.toLowerCase().includes(q)) {
          results.push({ ...t, album_name: album?.name || "" });
        }
      });
    });
    setSearchResults(results);
    setSearching(false);
  }, [searchLib, albumTracks]);

  async function searchAndLoad(q) {
    // Carrega faixas de todos os álbuns que ainda não foram carregados
    for (const a of albums) {
      if (!albumTracks[a.id]) await loadAlbumTracks(a.id);
    }
  }

  async function removeTrack(trackId) {
    await apiFetch(`/api/stores/${storeId}/playlists/${playlist.id}/tracks/${trackId}`, { method: "DELETE" }, token).catch(() => {});
    loadTracks();
  }

  // ── Aviso de faixa repetida ────────────────────────────────────────────
  // Repetida = mesmo arquivo de áudio OU mesmo título + artista já na playlist
  const normTA = (t) => ((t?.title || "").trim().toLowerCase() + "|" + (t?.artist || "").trim().toLowerCase());
  const plKeys = useMemo(() => {
    const files = new Set(), names = new Set();
    tracks.forEach(t => { if (t.bunny_url) files.add(t.bunny_url); if (t.title) names.add(normTA(t)); });
    return { files, names };
  }, [tracks]);
  const isDup = (t) => !!t && ((t.bunny_url && plKeys.files.has(t.bunny_url)) || (t.title && plKeys.names.has(normTA(t))));
  function findLibTrack(trackId) {
    for (const list of Object.values(albumTracks)) {
      const f = (list || []).find(x => x.id === trackId);
      if (f) return f;
    }
    return searchResults.find(x => x.id === trackId) || (trackPopup?.id === trackId ? trackPopup : null);
  }

  async function addTrack(trackId) {
    const t = findLibTrack(trackId);
    if (isDup(t) && !window.confirm(`"${t.title}"${t.artist ? " — " + t.artist : ""} já está nesta playlist.\n\nAdicionar mesmo assim?`)) return false;
    await apiFetch(`/api/stores/${storeId}/playlists/${playlist.id}/tracks`, { method: "POST", body: JSON.stringify({ track_id: trackId }) }, token).catch(() => {});
    loadTracks();
    return true;
  }

  const [addingAlbum, setAddingAlbum] = useState(null); // albumId em progresso

  async function addAlbum(albumId, albumName) {
    setAddingAlbum(albumId);
    // Busca faixas direto da API
    let faixas = albumTracks[albumId];
    if (!faixas) {
      const d = await apiFetch(`/api/albums/${albumId}/tracks`, {}, token).catch(() => []);
      faixas = Array.isArray(d) ? d : [];
      setAlbumTracks(p => ({ ...p, [albumId]: faixas }));
    }
    if (faixas.length === 0) { setAddingAlbum(null); return; }

    // Aviso de repetidas: oferece adicionar só as novas
    let track_ids = faixas.map(t => t.id);
    const dups = faixas.filter(isDup);
    if (dups.length > 0) {
      const novas = faixas.filter(t => !isDup(t));
      if (novas.length === 0) {
        if (!window.confirm(`Todas as ${faixas.length} faixas de "${albumName}" já estão nesta playlist.\n\nAdicionar repetidas mesmo assim?`)) { setAddingAlbum(null); return; }
      } else if (window.confirm(`${dups.length} de ${faixas.length} faixas de "${albumName}" já estão nesta playlist.\n\nOK = adicionar só as ${novas.length} novas\nCancelar = outras opções`)) {
        track_ids = novas.map(t => t.id);
      } else if (!window.confirm(`Adicionar TODAS as ${faixas.length} faixas, inclusive as ${dups.length} repetidas?`)) {
        setAddingAlbum(null); return;
      }
    }

    // Usa rota batch — uma única requisição com todos os IDs
    await apiFetch(
      `/api/stores/${storeId}/playlists/${playlist.id}/tracks/batch`,
      { method: "POST", body: JSON.stringify({ track_ids }) },
      token
    ).catch(() => {});
    loadTracks();
    setAddingAlbum(null);
    setJustAdded("album_" + albumId);
    setTimeout(() => setJustAdded(null), 2000);
  }

  async function move(i, dir) {
    const newTracks = [...tracks];
    const j = i + dir;
    if (j < 0 || j >= newTracks.length) return;
    [newTracks[i], newTracks[j]] = [newTracks[j], newTracks[i]];
    setTracks(newTracks);
    await apiFetch(`/api/stores/${storeId}/playlists/${playlist.id}/tracks/reorder`, { method: "PUT", body: JSON.stringify({ track_ids: newTracks.map(t => t.id) }) }, token).catch(() => {});
  }

  function playPreview(url, id) {
    const audio = audioRef.current;
    if (!audio) return;
    if (!url) return;
    if (playing === id) {
      audio.pause();
      setPlaying(null);
      clearInterval(progressInterval.current);
      return;
    }
    setPreviewError(false);
    audio.src = url;
    audio.play().catch(() => { setPreviewError(true); setPlaying(null); });
    audio.onerror = () => { setPreviewError(true); setPlaying(null); clearInterval(progressInterval.current); };
    setPlaying(id);
    setPreviewProgress(0);
    setPreviewTime(0);
    setPreviewDuration(0);
    clearInterval(progressInterval.current);
    progressInterval.current = setInterval(() => {
      if (!audio.duration) return;
      setPreviewTime(audio.currentTime);
      setPreviewDuration(audio.duration);
      setPreviewProgress((audio.currentTime / audio.duration) * 100);
    }, 200);
    audio.onended = () => {
      setPlaying(null);
      setPreviewProgress(0);
      clearInterval(progressInterval.current);
    };
  }

  function seekPreview(pct) {
    const audio = audioRef.current;
    if (!audio || !audio.duration) return;
    audio.currentTime = (pct / 100) * audio.duration;
    setPreviewProgress(pct);
  }

  const fmtP = (s) => { if (!s || isNaN(s)) return "0:00"; const m = Math.floor(s/60), sec = Math.floor(s%60); return `${m}:${String(sec).padStart(2,"0")}`; };

  const fmtDur = (s) => { if (!s) return ""; const m = Math.floor(s/60), sec = s%60; return `${m}:${String(sec).padStart(2,"0")}`; };

  // ── Fecha popup ao pressionar ESC ──
  useEffect(() => {
    const onKey = e => { if (e.key === "Escape") { setTrackPopup(null); if (playing) { audioRef.current?.pause(); setPlaying(null); clearInterval(progressInterval.current); } } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [playing]);

  // ── Restaura posição do scroll após fechar popup ──
  useEffect(() => {
    if (!trackPopup && libScrollRef.current && lastScrollPos.current > 0) {
      // Usa interval curto para lutar contra qualquer reset pós-render
      const pos = lastScrollPos.current;
      let attempts = 0;
      const restore = setInterval(() => {
        if (libScrollRef.current) libScrollRef.current.scrollTop = pos;
        attempts++;
        if (attempts >= 5) clearInterval(restore);
      }, 10);
      return () => clearInterval(restore);
    }
  }, [trackPopup]);


  // ── Navega entre faixas do álbum no popup ──
  function navigateTrack(dir) {
    if (!trackPopup?.albumId) return;
    const tracks = albumTracks[trackPopup.albumId] || [];
    const idx = tracks.findIndex(t => t.id === trackPopup.id);
    const next = tracks[idx + dir];
    if (!next) return;
    audioRef.current?.pause();
    setPlaying(null);
    clearInterval(progressInterval.current);
    setPreviewProgress(0); setPreviewTime(0); setPreviewDuration(0);
    setLastSelectedTrack(next.id);
    setPreviewError(false);
    setTrackPopup({ ...next, albumName: trackPopup.albumName, albumId: trackPopup.albumId });
    setTimeout(() => playPreview(next.bunny_url, next.id + "_popup"), 50);
  }

  return (
    <div className="fadeIn" style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      <audio ref={audioRef} style={{ display: "none" }} />

      {/* ── Popup de preview de faixa ── */}
      {trackPopup && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={() => { setTrackPopup(null); audioRef.current?.pause(); setPlaying(null); clearInterval(progressInterval.current); }}>
          <div style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 16, padding: "28px", width: 380, maxWidth: "90vw", boxShadow: "0 24px 60px rgba(0,0,0,0.6)" }}
            onClick={e => e.stopPropagation()}>
            {/* Cabeçalho */}
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 20 }}>
              <div style={{ flex: 1, overflow: "hidden", paddingRight: 12 }}>
                <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{trackPopup.title}</div>
                <div style={{ fontSize: 13, color: "var(--muted)" }}>{trackPopup.artist || "—"}</div>
                <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 4 }}>
                  📁 {trackPopup.albumName}{trackPopup.duration_seconds ? ` · ${fmtDur(trackPopup.duration_seconds)}` : ""}
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                {(() => {
                  const ts = albumTracks[trackPopup.albumId] || [];
                  const idx = ts.findIndex(t => t.id === trackPopup.id);
                  return (<>
                    <button onClick={() => navigateTrack(-1)} disabled={idx <= 0}
                      title="Faixa anterior"
                      style={{ background: "none", border: "1px solid var(--border)", borderRadius: 6, color: idx <= 0 ? "var(--bg3)" : "var(--muted)", fontSize: 12, width: 28, height: 28, cursor: idx <= 0 ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>&#9664;</button>
                    <span style={{ fontSize: 11, color: "var(--muted)", fontFamily: "var(--mono)", minWidth: 44, textAlign: "center" }}>{idx + 1}/{ts.length}</span>
                    <button onClick={() => navigateTrack(1)} disabled={idx >= ts.length - 1}
                      title="Próxima faixa"
                      style={{ background: "none", border: "1px solid var(--border)", borderRadius: 6, color: idx >= ts.length - 1 ? "var(--bg3)" : "var(--muted)", fontSize: 12, width: 28, height: 28, cursor: idx >= ts.length - 1 ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>&#9654;</button>
                    <button onClick={() => { setTrackPopup(null); audioRef.current?.pause(); setPlaying(null); clearInterval(progressInterval.current); }}
                      style={{ background: "none", border: "none", color: "var(--muted)", fontSize: 20, cursor: "pointer", lineHeight: 1, marginLeft: 4 }}>✕</button>
                  </>);
                })()}
              </div>
            </div>

            {/* Player de preview */}
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
              {(!trackPopup.bunny_url || previewError) ? (
                <div style={{ flex: 1, padding: "10px 14px", background: "#ff446615", border: "1px solid #ff446640", borderRadius: 10, fontSize: 12, color: "#ff9999", textAlign: "center" }}>
                  ⚠️ {previewError ? "Arquivo de áudio não encontrado no CDN" : "Áudio não disponível para esta faixa"}
                </div>
              ) : (
              <><button onClick={() => playPreview(trackPopup.bunny_url, trackPopup.id + "_popup")}
                style={{ width: 48, height: 48, borderRadius: "50%", background: playing === trackPopup.id + "_popup" ? "var(--green)" : "rgba(0,255,127,0.1)", border: "1px solid #00ff7f40", color: playing === trackPopup.id + "_popup" ? "#080d08" : "var(--green)", fontSize: 18, cursor: "pointer", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                {playing === trackPopup.id + "_popup" ? "⏸" : "▶"}
              </button>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                  <span style={{ fontSize: 11, color: "var(--muted)", fontFamily: "var(--mono)", minWidth: 32 }}>{fmtP(previewTime)}</span>
                  <div style={{ flex: 1, height: 20, display: "flex", alignItems: "center", cursor: "pointer" }}
                    onMouseDown={e => { e.preventDefault(); const r = e.currentTarget.getBoundingClientRect(); seekPreview(((e.clientX-r.left)/r.width)*100); }}
                    onTouchStart={e => { e.preventDefault(); const t = e.touches[0]; const r = e.currentTarget.getBoundingClientRect(); seekPreview(((t.clientX-r.left)/r.width)*100); }}>
                    <div style={{ width: "100%", height: 6, background: "rgba(255,255,255,0.12)", borderRadius: 3, overflow: "hidden" }}>
                      <div style={{ height: "100%", width: `${playing === trackPopup.id + "_popup" ? previewProgress : 0}%`, background: "var(--green)", borderRadius: 3, transition: "width 0.2s" }} />
                    </div>
                  </div>
                  <span style={{ fontSize: 11, color: "var(--muted)", fontFamily: "var(--mono)", minWidth: 32, textAlign: "right" }}>{fmtP(previewDuration)}</span>
                </div>
              </div>
              </>)}
            </div>

            {/* Botão adicionar */}
            <button onClick={() => { const id = trackPopup.id; setLastSelectedTrack(id); addTrack(id).then(ok => { if (ok) { setJustAdded(id); setTimeout(() => setJustAdded(null), 1500); } }); }}
              style={{ width: "100%", padding: "12px", background: justAdded === trackPopup.id ? "#00cc66" : "var(--green)", color: "#080d08", border: "none", borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: "pointer", transition: "background 0.3s" }}>
              {justAdded === trackPopup.id ? "✓ Adicionada!" : "+ Adicionar à Playlist"}
            </button>
          </div>
        </div>
      )}
      <div style={{ padding: "24px 32px 16px", borderBottom: "1px solid var(--border)", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <button onClick={onBack} style={{ padding: "8px 16px", background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 8, color: "var(--muted)", fontSize: 13, cursor: "pointer" }}>← Voltar</button>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 800, marginBottom: 2 }}>{playlist.name}</h1>
            <p style={{ fontSize: 13, color: "var(--muted)" }}>{tracks.length} faixas {playlist.mood ? `· ${playlist.mood}` : ""}</p>
          </div>
          {playlist.created_by_ai && <span style={{ fontSize: 11, padding: "3px 10px", background: "#a78bfa20", border: "1px solid #a78bfa40", borderRadius: 20, color: "#a78bfa" }}>✦ IA</span>}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, flex: 1, overflow: "hidden", padding: "24px 32px" }}>

        {/* Coluna esquerda: Álbuns da Biblioteca */}
        <div ref={libScrollRef} style={{ overflowY: trackPopup ? "hidden" : "auto", paddingRight: 4 }} onScroll={e => { if (!trackPopup) lastScrollPos.current = e.currentTarget.scrollTop; }}>
          <div style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 12, padding: "20px" }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, color: "var(--muted)", marginBottom: 14, letterSpacing: 1 }}>🎵 ÁLBUNS DA BIBLIOTECA</h3>
            <div style={{ position: "relative", marginBottom: 14 }}>
              <input placeholder="🔍 Buscar faixa ou artista..." value={searchLib}
                onChange={e => { setSearchLib(e.target.value); if (e.target.value.trim()) searchAndLoad(); }}
                style={{ width: "100%", paddingRight: searchLib ? 32 : 14 }} />
              {searchLib && <button onClick={() => { setSearchLib(""); setSearchResults([]); }}
                style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "var(--muted)", cursor: "pointer", fontSize: 14 }}>✕</button>}
            </div>
            {searchLib.trim() ? (
              <div>
                <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 8 }}>
                  {searching ? "Buscando..." : `${searchResults.length} resultado${searchResults.length!==1?"s":""} para "${searchLib}"`}
                </div>
                {searchResults.length === 0 && !searching
                  ? <div style={{ textAlign: "center", padding: "20px", color: "var(--muted)", fontSize: 13 }}>Nenhuma faixa encontrada</div>
                  : searchResults.map(t => (
                    <div key={t.id} style={{ marginBottom: 4 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", background: "var(--bg3)", borderRadius: 6, border: "1px solid var(--border)" }}>
                        <button onClick={() => playPreview(t.bunny_url, t.id+"_s")} style={{ width: 24, height: 24, borderRadius: "50%", background: playing===t.id+"_s"?"var(--green)":"rgba(0,255,127,0.1)", border: "1px solid #00ff7f30", color: playing===t.id+"_s"?"#080d08":"var(--green)", fontSize: 10, cursor: "pointer", flexShrink: 0 }}>
                          {playing===t.id+"_s"?"⏸":"▶"}
                        </button>
                        <div style={{ flex: 1, overflow: "hidden" }}>
                          <div style={{ fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.title}</div>
                          <div style={{ fontSize: 11, color: "var(--muted)" }}>{t.artist||"—"} · {t.album_name}</div>
                        </div>
                        {isDup(t) && <span title="Esta faixa já está na playlist" style={{ fontSize: 10, color: "var(--yellow)", border: "1px solid #ffd93d50", borderRadius: 4, padding: "1px 5px", flexShrink: 0, whiteSpace: "nowrap" }}>✓ na playlist</span>}
                        <button onClick={() => addTrack(t.id)} style={{ padding: "3px 10px", background: "var(--green3)", border: "1px solid #00ff7f30", borderRadius: 6, color: "var(--green)", fontSize: 11, fontWeight: 700, cursor: "pointer", flexShrink: 0 }}>+</button>
                      </div>
                      {playing===t.id+"_s" && (
                        <div style={{ padding: "4px 10px 6px", background: "var(--bg3)", borderRadius: "0 0 6px 6px", borderTop: "none" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <span style={{ fontSize: 10, color: "var(--muted)", fontFamily: "var(--mono)", minWidth: 30 }}>{fmtP(previewTime)}</span>
                            <div style={{ flex: 1, height: 20, display: "flex", alignItems: "center", cursor: "pointer" }}
                              onMouseDown={e => { e.preventDefault(); const r = e.currentTarget.getBoundingClientRect(); seekPreview(((e.clientX-r.left)/r.width)*100); }} onTouchStart={e => { e.preventDefault(); const t = e.touches[0]; const r = e.currentTarget.getBoundingClientRect(); seekPreview(((t.clientX-r.left)/r.width)*100); }}>
                              <div style={{ width: "100%", height: 6, background: "rgba(255,255,255,0.12)", borderRadius: 3, overflow: "hidden" }}>
                                <div style={{ height: "100%", width: `${previewProgress}%`, background: "var(--green)", borderRadius: 3, transition: "width 0.2s" }} />
                              </div>
                            </div>
                            <span style={{ fontSize: 10, color: "var(--muted)", fontFamily: "var(--mono)", minWidth: 30, textAlign: "right" }}>{fmtP(previewDuration)}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                }
              </div>
            ) : (() => {
              // Acordeão por categoria — fechado por padrão
              const porCategoria = categories.map(cat => ({
                ...cat, albums: albums.filter(a => a.category_id === cat.id),
              })).filter(cat => cat.albums.length > 0);
              const semCategoria = albums.filter(a => !a.category_id);

              const TrackItem = ({ t, suffix }) => (
                <div style={{ marginBottom: 4 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", background: "var(--bg3)", borderRadius: playing===t.id+suffix ? "6px 6px 0 0" : 6, border: "1px solid var(--border)" }}>
                    <button onClick={() => playPreview(t.bunny_url, t.id+suffix)} style={{ width: 24, height: 24, borderRadius: "50%", background: playing===t.id+suffix?"var(--green)":"rgba(0,255,127,0.1)", border: "1px solid #00ff7f30", color: playing===t.id+suffix?"#080d08":"var(--green)", fontSize: 10, cursor: "pointer", flexShrink: 0 }}>
                      {playing===t.id+suffix?"⏸":"▶"}
                    </button>
                    <div style={{ flex: 1, overflow: "hidden" }}>
                      <div style={{ fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.title}</div>
                      <div style={{ fontSize: 11, color: "var(--muted)" }}>{t.artist||"—"} {t.duration_seconds?`· ${fmtDur(t.duration_seconds)}`:""}</div>
                    </div>
                    {isDup(t) && <span title="Esta faixa já está na playlist" style={{ fontSize: 10, color: "var(--yellow)", border: "1px solid #ffd93d50", borderRadius: 4, padding: "1px 5px", flexShrink: 0, whiteSpace: "nowrap" }}>✓ na playlist</span>}
                    <button onClick={() => addTrack(t.id)} style={{ padding: "3px 10px", background: "var(--green3)", border: "1px solid #00ff7f30", borderRadius: 6, color: "var(--green)", fontSize: 11, fontWeight: 700, cursor: "pointer", flexShrink: 0 }}>+</button>
                  </div>
                  {playing===t.id+suffix && (
                    <div style={{ padding: "4px 10px 6px", background: "var(--bg3)", borderRadius: "0 0 6px 6px", borderTop: "1px solid rgba(0,255,127,0.1)" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ fontSize: 10, color: "var(--muted)", fontFamily: "var(--mono)", minWidth: 30 }}>{fmtP(previewTime)}</span>
                        <div style={{ flex: 1, height: 20, display: "flex", alignItems: "center", cursor: "pointer" }}
                          onMouseDown={e => { e.preventDefault(); const r = e.currentTarget.getBoundingClientRect(); seekPreview(((e.clientX-r.left)/r.width)*100); }} onTouchStart={e => { e.preventDefault(); const t = e.touches[0]; const r = e.currentTarget.getBoundingClientRect(); seekPreview(((t.clientX-r.left)/r.width)*100); }}>
                          <div style={{ width: "100%", height: 6, background: "rgba(255,255,255,0.12)", borderRadius: 3, overflow: "hidden" }}>
                            <div style={{ height: "100%", width: `${previewProgress}%`, background: "var(--green)", borderRadius: 3, transition: "width 0.2s" }} />
                          </div>
                        </div>
                        <span style={{ fontSize: 10, color: "var(--muted)", fontFamily: "var(--mono)", minWidth: 30, textAlign: "right" }}>{fmtP(previewDuration)}</span>
                      </div>
                    </div>
                  )}
                </div>
              );

              const AlbumAccordion = ({ a }) => {
                const isOpen = openCats[a.id] === true;
                const isAdding = addingAlbum === a.id;
                const justAddedAlbum = justAdded === "album_" + a.id;
                return (
                  <div style={{ marginBottom: 6 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 12px", background: "var(--bg3)", borderRadius: 8, border: "1px solid var(--border)" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, cursor: "pointer" }}
                        onClick={() => { setOpenCats(p => ({ ...p, [a.id]: !isOpen })); loadAlbumTracks(a.id); }}>
                        <span style={{ fontSize: 13 }}>💿</span>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 12, color: "var(--text)" }}>{a.name}</div>
                          <div style={{ fontSize: 10, color: "var(--muted)" }}>{a.track_count || 0} faixas{a.genre ? ` · ${a.genre}` : ""}</div>
                        </div>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                        <button
                          onClick={e => { e.stopPropagation(); addAlbum(a.id, a.name); }}
                          disabled={isAdding}
                          title="Adicionar todas as faixas à playlist"
                          style={{ padding: "3px 10px", background: justAddedAlbum ? "#00cc6620" : "var(--green3)", border: `1px solid ${justAddedAlbum ? "#00cc66" : "#00ff7f30"}`, borderRadius: 6, color: justAddedAlbum ? "#00cc66" : "var(--green)", fontSize: 11, fontWeight: 700, cursor: isAdding ? "wait" : "pointer", whiteSpace: "nowrap" }}>
                          {isAdding ? "⏳" : justAddedAlbum ? "✓ Adicionado!" : "+ Álbum"}
                        </button>
                        <span onClick={() => { setOpenCats(p => ({ ...p, [a.id]: !isOpen })); loadAlbumTracks(a.id); }}
                          style={{ color: "var(--muted)", fontSize: 11, cursor: "pointer", padding: "4px" }}>{isOpen ? "▲" : "▼"}</span>
                      </div>
                    </div>
                    {isOpen && (
                      <div style={{ paddingLeft: 8, paddingTop: 4, maxHeight: 280, overflowY: "auto" }}>
                        {(albumTracks[a.id] || []).map(t => (
                          <div key={t.id}
                            onClick={() => { lastScrollPos.current = libScrollRef.current?.scrollTop || 0; setLastSelectedTrack(t.id); setTrackPopup({ ...t, albumName: a.name, albumId: a.id }); setTimeout(() => playPreview(t.bunny_url, t.id + "_popup"), 50); }}
                            style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 10px", background: lastSelectedTrack === t.id ? "rgba(0,255,127,0.08)" : "rgba(255,255,255,0.02)", borderRadius: 6, marginBottom: 3, border: `1px solid ${lastSelectedTrack === t.id ? "rgba(0,255,127,0.3)" : "rgba(255,255,255,0.05)"}`, cursor: "pointer" }}>
                            <span style={{ fontSize: 11, color: "var(--muted)", flexShrink: 0 }}>🎵</span>
                            <div style={{ flex: 1, overflow: "hidden" }}>
                              <div style={{ fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: lastSelectedTrack === t.id ? "var(--green)" : "var(--text)" }}>{t.title}</div>
                              <div style={{ fontSize: 10, color: "var(--muted)" }}>{t.artist || "—"}{t.duration_seconds ? ` · ${fmtDur(t.duration_seconds)}` : ""}</div>
                            </div>
                            {isDup(t) && <span title="Esta faixa já está na playlist" style={{ fontSize: 10, color: "var(--yellow)", border: "1px solid #ffd93d50", borderRadius: 4, padding: "1px 5px", flexShrink: 0, whiteSpace: "nowrap" }}>✓ na playlist</span>}
                            <button onClick={e => { e.stopPropagation(); addTrack(t.id); }}
                              style={{ padding: "3px 10px", background: "var(--green3)", border: "1px solid #00ff7f30", borderRadius: 6, color: "var(--green)", fontSize: 11, fontWeight: 700, cursor: "pointer", flexShrink: 0 }}>+</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              };

              return (
                <div>
                  {porCategoria.map(cat => {
                    const isCatOpen = openCats["cat_"+cat.id] === true;
                    return (
                      <div key={cat.id} style={{ marginBottom: 8 }}>
                        <div onClick={() => setOpenCats(p => ({ ...p, ["cat_"+cat.id]: !isCatOpen }))}
                          style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", background: "linear-gradient(90deg, rgba(0,255,127,0.08), rgba(0,255,127,0.03))", borderRadius: 8, border: "1px solid rgba(0,255,127,0.2)", cursor: "pointer", userSelect: "none", marginBottom: 6 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ fontSize: 14 }}>📁</span>
                            <span style={{ fontWeight: 800, fontSize: 12, letterSpacing: 1, color: "var(--green)", textTransform: "uppercase" }}>{cat.name}</span>
                            <span style={{ fontSize: 10, color: "var(--muted)", background: "rgba(0,255,127,0.1)", border: "1px solid rgba(0,255,127,0.2)", borderRadius: 10, padding: "1px 7px" }}>{cat.albums.length} álbun{cat.albums.length !== 1 ? "s" : ""}</span>
                          </div>
                          <span style={{ color: "var(--green)", fontSize: 11, opacity: 0.6 }}>{isCatOpen ? "▲" : "▼"}</span>
                        </div>
                        {isCatOpen && (
                          <div style={{ paddingLeft: 8, paddingTop: 6 }}>
                            {cat.albums.map(a => <AlbumAccordion key={a.id} a={a} />)}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {semCategoria.length > 0 && (
                    <div>
                      {porCategoria.length > 0 && <div style={{ fontSize: 11, color: "var(--muted)", letterSpacing: 1, margin: "8px 0 6px" }}>SEM CATEGORIA</div>}
                      {semCategoria.map(a => <AlbumAccordion key={a.id} a={a} />)}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>

        {/* Coluna direita: Faixas da Playlist */}
        <div style={{ overflowY: "auto", paddingRight: 4 }}>
          <div style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 12, padding: "20px" }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, color: "var(--green)", marginBottom: 14, letterSpacing: 1 }}>♫ {playlist.name.toUpperCase()}</h3>
            {loading ? <div style={{ color: "var(--muted)", fontSize: 13 }}>Carregando...</div> :
             tracks.length === 0 ? (
              <div style={{ textAlign: "center", padding: "32px", color: "var(--muted)" }}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>♫</div>
                <p style={{ fontSize: 13 }}>Nenhuma faixa ainda.<br/>← Adicione da biblioteca</p>
              </div>
            ) : tracks.map((t, i) => (
              <div key={t.id} style={{ marginBottom: 6 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", background: "var(--bg3)", borderRadius: playing===t.id ? "8px 8px 0 0" : 8, border: "1px solid var(--border)" }}>
                  <span style={{ fontSize: 11, color: "var(--muted)", fontFamily: "var(--mono)", minWidth: 22 }}>{String(i+1).padStart(2,"0")}</span>
                  <button onClick={() => playPreview(t.bunny_url, t.id)} style={{ width: 28, height: 28, borderRadius: "50%", background: playing===t.id?"var(--green)":"rgba(0,255,127,0.1)", border: "1px solid #00ff7f30", color: playing===t.id?"#080d08":"var(--green)", fontSize: 12, cursor: "pointer", flexShrink: 0 }}>
                    {playing===t.id?"⏸":"▶"}
                  </button>
                  <div style={{ flex: 1, overflow: "hidden" }}>
                    <div style={{ fontWeight: 600, fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.title}</div>
                    <div style={{ fontSize: 11, color: "var(--muted)" }}>{t.artist||"—"} {t.duration_seconds?`· ${fmtDur(t.duration_seconds)}`:""}</div>
                  </div>
                  <div style={{ display: "flex", gap: 2, flexShrink: 0 }}>
                    <button onClick={() => move(i,-1)} disabled={i===0} style={{ background: "none", border: "none", color: "var(--muted)", cursor: "pointer", fontSize: 12, padding: "2px 4px" }}>▲</button>
                    <button onClick={() => move(i,1)} disabled={i===tracks.length-1} style={{ background: "none", border: "none", color: "var(--muted)", cursor: "pointer", fontSize: 12, padding: "2px 4px" }}>▼</button>
                    <button onClick={() => removeTrack(t.id)} style={{ background: "none", border: "1px solid #ff446640", borderRadius: 4, color: "var(--red)", fontSize: 11, padding: "2px 6px", cursor: "pointer" }}>✕</button>
                  </div>
                </div>
                {playing===t.id && (
                  <div style={{ padding: "4px 10px 6px", background: "var(--bg3)", borderRadius: "0 0 8px 8px", borderTop: "1px solid rgba(0,255,127,0.1)" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ fontSize: 10, color: "var(--muted)", fontFamily: "var(--mono)", minWidth: 30 }}>{fmtP(previewTime)}</span>
                      <div style={{ flex: 1, height: 20, display: "flex", alignItems: "center", cursor: "pointer" }}
                        onMouseDown={e => { e.preventDefault(); const r = e.currentTarget.getBoundingClientRect(); seekPreview(((e.clientX-r.left)/r.width)*100); }} onTouchStart={e => { e.preventDefault(); const t = e.touches[0]; const r = e.currentTarget.getBoundingClientRect(); seekPreview(((t.clientX-r.left)/r.width)*100); }}>
                        <div style={{ width: "100%", height: 6, background: "rgba(255,255,255,0.12)", borderRadius: 3, overflow: "hidden" }}>
                          <div style={{ height: "100%", width: `${previewProgress}%`, background: "var(--green)", borderRadius: 3, transition: "width 0.2s" }} />
                        </div>
                      </div>
                      <span style={{ fontSize: 10, color: "var(--muted)", fontFamily: "var(--mono)", minWidth: 30, textAlign: "right" }}>{fmtP(previewDuration)}</span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}

function Playlists({ token, user }) {
  const [selectedStore, setSelectedStore] = useState("");
  const [playlists, setPlaylists] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", mood: "", genre: "" });
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [openPlaylist, setOpenPlaylist] = useState(null);

  // ── 📥 Importar lista de músicas (de outro sistema) ──
  const [imp, setImp] = useState(false);            // modal aberto
  const [impText, setImpText] = useState("");
  const [impBusy, setImpBusy] = useState(false);
  const [impRes, setImpRes] = useState(null);       // resultado da busca
  const [impAccept, setImpAccept] = useState([]);   // idx das "parecidas" aceitas
  const [impName, setImpName] = useState("");
  const [impTarget, setImpTarget] = useState("");   // "" = nova playlist | id de uma existente
  const [impMsg, setImpMsg] = useState("");
  const [impOpen, setImpOpen] = useState({ found: false, similar: true, missing: false });
  function openImport() { setImp(true); setImpText(""); setImpRes(null); setImpAccept([]); setImpName(""); setImpTarget(""); setImpMsg(""); }
  async function runImportMatch() {
    if (!impText.trim()) return setImpMsg("✗ Cole a lista ou escolha o arquivo .txt");
    setImpBusy(true); setImpMsg(""); setImpRes(null);
    try {
      const d = await apiFetch(`/api/stores/${selectedStore}/playlists/import/match`, { method: "POST", body: JSON.stringify({ text: impText }) }, token);
      setImpRes(d); setImpAccept([]);
    } catch (e) { setImpMsg("✗ " + e.message); }
    setImpBusy(false);
  }
  const impIds = impRes ? impRes.items.filter(i => i.status === "found" || (i.status === "similar" && impAccept.includes(i.idx))).map(i => i.match.id) : [];
  async function runImportCreate() {
    if (!impIds.length) return;
    if (!impTarget && !impName.trim()) return setImpMsg("✗ Dê um nome para a playlist");
    setImpBusy(true); setImpMsg("");
    try {
      const d = await apiFetch(`/api/stores/${selectedStore}/playlists/import/create`, { method: "POST", body: JSON.stringify({ name: impName, playlistId: impTarget || null, track_ids: impIds }) }, token);
      setImpMsg(`✓ ${d.added} músicas adicionadas` + (d.skipped ? ` (${d.skipped} já estavam na playlist)` : "") + ". Agora é só colocar a playlist na Grade do Player.");
      loadPlaylists();
    } catch (e) { setImpMsg("✗ " + e.message); }
    setImpBusy(false);
  }
  function downloadMissing() {
    if (!impRes) return;
    const esc = (v) => `"${String(v || "").replace(/"/g, '""')}"`;
    const lines = [["Título", "Artista", "Duração"].map(esc).join(";")]
      .concat(impRes.items.filter(i => i.status === "missing").map(i => [i.title, i.artist, i.duration].map(esc).join(";")));
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob(["\uFEFF" + lines.join("\r\n")], { type: "text/csv;charset=utf-8" }));
    a.download = "musicas_nao_encontradas.csv";
    document.body.appendChild(a); a.click(); a.remove();
  }

  useEffect(() => {
    // Todos carregam a primeira loja automaticamente — sem seletor
    apiFetch("/api/stores", {}, token).then(d => {
      const list = Array.isArray(d) ? d : (d.stores || []);
      if (list.length > 0) setSelectedStore(list[0].id);
    }).catch(() => {});
  }, [token]);

  const loadPlaylists = () => {
    if (!selectedStore) return;
    apiFetch(`/api/stores/${selectedStore}/playlists`, {}, token).then(d => setPlaylists(Array.isArray(d) ? d : (d.playlists || []))).catch(() => {});
  };

  useEffect(() => { loadPlaylists(); }, [selectedStore, token]);

  async function save() {
    if (!selectedStore || !form.name) return;
    setSaving(true);
    try {
      await apiFetch(`/api/stores/${selectedStore}/playlists`, { method: "POST", body: JSON.stringify(form) }, token);
      setForm({ name: "", description: "", mood: "", genre: "" });
      setShowForm(false);
      loadPlaylists();
    } catch (e) { console.error(e); }
    setSaving(false);
  }

  async function remove(id) {
    if (!window.confirm("Remover esta playlist?")) return;
    try {
      await apiFetch(`/api/stores/${selectedStore}/playlists/${id}`, { method: "DELETE" }, token);
      loadPlaylists();
    } catch (e) { console.error(e); }
  }

  async function saveEdit(id) {
    try {
      await apiFetch(`/api/stores/${selectedStore}/playlists/${id}`, { method: "PUT", body: JSON.stringify(editForm) }, token);
      setEditingId(null);
      loadPlaylists();
    } catch (e) { console.error(e); }
  }

  // Tela de edição de faixas
  if (openPlaylist) return (
    <PlaylistEditor token={token} playlist={openPlaylist} storeId={selectedStore}
      onBack={() => { setOpenPlaylist(null); loadPlaylists(); }} />
  );

  return (
    <div className="fadeIn" style={{ padding: "32px", overflowY: "auto", height: "100%" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 800, marginBottom: 4 }}>Playlists</h1>
          <p style={{ color: "var(--muted)", fontSize: 14 }}>Organize suas músicas por ambiente</p>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
        <button onClick={openImport} disabled={!selectedStore} style={{
          padding: "10px 16px", background: "var(--bg3)", color: "var(--text)", border: "1px solid var(--border)", borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: "pointer"
        }}>📥 Importar lista</button>
        <button onClick={() => setShowForm(!showForm)} style={{
          padding: "10px 20px", background: showForm ? "var(--bg3)" : "var(--green)", color: showForm ? "var(--muted)" : "#080d08",
          border: "1px solid var(--border)", borderRadius: 10, fontWeight: 700, fontSize: 14
        }}>
          {showForm ? "Cancelar" : "+ Nova Playlist"}
        </button>
        </div>
      </div>

      {/* ── Modal: importar lista de músicas ── */}
      {imp && (
        <div onClick={() => !impBusy && setImp(false)} style={{ position: "fixed", inset: 0, background: "#000a", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: "var(--bg2)", border: "1px solid var(--green)", borderRadius: 14, padding: 24, width: "100%", maxWidth: 820, maxHeight: "90vh", overflowY: "auto" }}>
            <h3 style={{ fontSize: 16, fontWeight: 800, marginBottom: 4 }}>📥 Importar lista de músicas</h3>
            <p style={{ fontSize: 13, color: "var(--muted)", marginBottom: 14, lineHeight: 1.5 }}>
              Cole ou envie a lista exportada de outro sistema (Título / Artista / Duração em blocos, ou "Artista - Título" por linha).
              O RadioIN procura cada música no acervo e monta a playlist.
            </p>
            {!impRes ? (
              <>
                <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 8 }}>
                  <label style={{ padding: "7px 14px", background: "#3b82f620", border: "1px solid #3b82f640", borderRadius: 8, color: "#60a5fa", fontWeight: 800, fontSize: 12, cursor: "pointer" }}>
                    📄 Escolher arquivo .txt
                    <input type="file" accept=".txt,.csv,text/plain" style={{ display: "none" }}
                      onChange={e => { const f = e.target.files[0]; if (f) { f.text().then(t => setImpText(t)); if (!impName) setImpName(f.name.replace(/\.[^.]+$/, "").replace(/^Documento\s+/i, "").slice(0, 80)); } e.target.value = ""; }} />
                  </label>
                  <span style={{ fontSize: 12, color: "var(--muted)" }}>{impText ? `${impText.split(/\n/).length} linhas carregadas` : "ou cole abaixo"}</span>
                </div>
                <textarea value={impText} onChange={e => setImpText(e.target.value)} placeholder={"No Meio do Rolo (Funk Remix)\nDj Lucas Beat, Luan Pereira\n02:19\n\nDois Enganados\nMurilo Huff, Marília Mendonça\n02:51"}
                  style={{ width: "100%", height: 220, resize: "vertical", fontFamily: "var(--mono)", fontSize: 12 }} />
                <button onClick={runImportMatch} disabled={impBusy} style={{ marginTop: 12, width: "100%", padding: "11px 0", background: "var(--green)", border: "none", borderRadius: 8, color: "#080d08", fontWeight: 800, cursor: "pointer" }}>
                  {impBusy ? "⏳ Procurando no acervo..." : "🔎 Procurar no acervo"}
                </button>
              </>
            ) : (
              <>
                <div style={{ display: "flex", gap: 14, flexWrap: "wrap", padding: "10px 12px", background: "var(--bg3)", borderRadius: 8, marginBottom: 12, fontSize: 13 }}>
                  <span>Lista: <b>{impRes.total}</b></span>
                  <span style={{ color: "var(--green)" }}>✅ encontradas: <b>{impRes.found}</b></span>
                  <span style={{ color: "var(--yellow)" }}>🟡 parecidas: <b>{impRes.similar}</b></span>
                  <span style={{ color: "var(--red)" }}>❌ não encontradas: <b>{impRes.missing}</b></span>
                  <span style={{ color: "var(--muted)" }}>(acervo: {impRes.library} faixas)</span>
                </div>
                {[["found", "✅ Encontradas", "var(--green)"], ["similar", "🟡 Parecidas — marque as que são a mesma música", "var(--yellow)"], ["missing", "❌ Não encontradas", "var(--red)"]].map(([k, label, color]) => {
                  const list = impRes.items.filter(i => i.status === k);
                  if (!list.length) return null;
                  return (
                    <div key={k} style={{ marginBottom: 10 }}>
                      <button onClick={() => setImpOpen(o => ({ ...o, [k]: !o[k] }))} style={{ width: "100%", textAlign: "left", padding: "8px 12px", background: "var(--bg3)", border: "1px solid var(--border)", borderRadius: 8, color, fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                        {impOpen[k] ? "▼" : "▶"} {label} ({list.length})
                        {k === "similar" && <span onClick={e => { e.stopPropagation(); setImpAccept(a => a.length === list.length ? [] : list.map(i => i.idx)); }} style={{ float: "right", fontSize: 12, color: "var(--green)" }}>{impAccept.length === list.length ? "desmarcar todas" : "marcar todas"}</span>}
                        {k === "missing" && <span onClick={e => { e.stopPropagation(); downloadMissing(); }} style={{ float: "right", fontSize: 12, color: "#60a5fa" }}>⬇ baixar planilha</span>}
                      </button>
                      {impOpen[k] && (
                        <div style={{ maxHeight: 260, overflowY: "auto", fontSize: 12, padding: "4px 0" }}>
                          {list.map(i => (
                            <div key={i.idx} style={{ display: "flex", gap: 8, alignItems: "center", padding: "4px 10px", borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                              {k === "similar" && <input type="checkbox" checked={impAccept.includes(i.idx)} onChange={e => setImpAccept(a => e.target.checked ? [...a, i.idx] : a.filter(x => x !== i.idx))} />}
                              <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{i.title} <span style={{ color: "var(--muted)" }}>— {i.artist}</span></span>
                              {i.match && <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "var(--muted)" }} title={i.match.album}>⇒ {i.match.title}{i.match.artist ? ` — ${i.match.artist}` : ""} <i>({i.match.album})</i></span>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginTop: 14 }}>
                  <select value={impTarget} onChange={e => setImpTarget(e.target.value)} style={{ fontSize: 13 }}>
                    <option value="">Criar playlist nova</option>
                    {playlists.map(p => <option key={p.id} value={p.id}>Completar: {p.name}</option>)}
                  </select>
                  {!impTarget && <input value={impName} onChange={e => setImpName(e.target.value)} placeholder="Nome da playlist (ex.: Favoritas Ibifestas)" maxLength={120} style={{ flex: 1, minWidth: 220, fontSize: 13 }} />}
                  <button onClick={runImportCreate} disabled={impBusy || !impIds.length} style={{ padding: "9px 16px", background: impIds.length ? "var(--green)" : "var(--bg3)", border: "none", borderRadius: 8, color: impIds.length ? "#080d08" : "var(--muted)", fontWeight: 800, fontSize: 13, cursor: impIds.length ? "pointer" : "default" }}>
                    {impBusy ? "⏳ Salvando..." : `✓ ${impTarget ? "Adicionar" : "Criar playlist com"} ${impIds.length} músicas`}
                  </button>
                  <button onClick={() => { setImpRes(null); setImpMsg(""); }} disabled={impBusy} style={{ padding: "9px 14px", background: "var(--bg3)", border: "1px solid var(--border)", borderRadius: 8, color: "var(--muted)", fontSize: 13, cursor: "pointer" }}>↺ Nova busca</button>
                </div>
              </>
            )}
            {impMsg && <div style={{ marginTop: 10, fontSize: 13, color: impMsg.startsWith("✓") ? "var(--green)" : "var(--red)" }}>{impMsg}</div>}
            <button onClick={() => setImp(false)} disabled={impBusy} style={{ marginTop: 14, width: "100%", padding: "10px 0", background: "var(--bg3)", border: "1px solid var(--border)", borderRadius: 8, color: "var(--muted)", fontWeight: 700, cursor: "pointer" }}>Fechar</button>
          </div>
        </div>
      )}

      {showForm && (
        <div className="fadeIn" style={{ background: "var(--bg2)", border: "1px solid var(--green)", borderRadius: 12, padding: "24px", marginBottom: 24 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: "var(--green)", marginBottom: 16 }}>Nova Playlist</h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <input placeholder="Nome *" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            <input placeholder="Gênero" value={form.genre} onChange={e => setForm(f => ({ ...f, genre: e.target.value }))} />
            <input placeholder="Mood" value={form.mood} onChange={e => setForm(f => ({ ...f, mood: e.target.value }))} />
            <input placeholder="Descrição" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
          </div>
          <button onClick={save} disabled={saving} style={{ marginTop: 16, padding: "10px 24px", background: "var(--green)", color: "#080d08", border: "none", borderRadius: 8, fontWeight: 700 }}>
            {saving ? "Salvando..." : "Criar Playlist"}
          </button>
        </div>
      )}

      {selectedStore ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 16 }}>
          {playlists.length === 0 ? (
            <div style={{ gridColumn: "1/-1", textAlign: "center", padding: 60, color: "var(--muted)", background: "var(--bg2)", borderRadius: 12 }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>♫</div>
              <p>Nenhuma playlist criada.</p>
            </div>
          ) : playlists.map(p => (
            <div key={p.id} style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 12, padding: "20px", cursor: "pointer" }}
              onClick={() => setOpenPlaylist(p)}>
              {editingId === p.id ? (
                <div onClick={e => e.stopPropagation()} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <input value={editForm.name || ""} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} placeholder="Nome" style={{ width: "100%" }} />
                  <input value={editForm.genre || ""} onChange={e => setEditForm(f => ({ ...f, genre: e.target.value }))} placeholder="Gênero" style={{ width: "100%" }} />
                  <input value={editForm.mood || ""} onChange={e => setEditForm(f => ({ ...f, mood: e.target.value }))} placeholder="Mood" style={{ width: "100%" }} />
                  <input value={editForm.description || ""} onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} placeholder="Descrição" style={{ width: "100%" }} />
                  <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                    <button onClick={() => saveEdit(p.id)} style={{ flex: 1, padding: "8px", background: "var(--green)", color: "#080d08", border: "none", borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: "pointer" }}>Salvar</button>
                    <button onClick={() => setEditingId(null)} style={{ padding: "8px 12px", background: "var(--bg3)", border: "1px solid var(--border)", borderRadius: 8, color: "var(--muted)", fontSize: 13, cursor: "pointer" }}>Cancelar</button>
                  </div>
                </div>
              ) : (
                <>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                    <div style={{ width: 40, height: 40, background: p.created_by_ai ? "#a78bfa20" : "var(--green3)", border: `1px solid ${p.created_by_ai ? "#a78bfa40" : "#00ff7f30"}`, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>
                      {p.created_by_ai ? "✦" : "♫"}
                    </div>
                    <div style={{ display: "flex", gap: 4 }} onClick={e => e.stopPropagation()}>
                      <button onClick={() => { setEditingId(p.id); setEditForm({ name: p.name, genre: p.genre || "", mood: p.mood || "", description: p.description || "" }); }}
                        style={{ padding: "4px 10px", background: "var(--bg3)", border: "1px solid var(--border)", borderRadius: 6, color: "var(--muted)", fontSize: 12, cursor: "pointer" }}>✏️</button>
                      <button onClick={() => remove(p.id)}
                        style={{ padding: "4px 10px", background: "none", border: "1px solid #ff446640", borderRadius: 6, color: "var(--red)", fontSize: 12, cursor: "pointer" }}>✕</button>
                    </div>
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 6 }}>{p.name}</div>
                  <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                    {p.genre && <span style={{ fontSize: 11, padding: "2px 8px", background: "var(--bg3)", border: "1px solid var(--border)", borderRadius: 20, color: "var(--muted)" }}>{p.genre}</span>}
                    {p.mood && <span style={{ fontSize: 11, padding: "2px 8px", background: "var(--green3)", border: "1px solid #00ff7f20", borderRadius: 20, color: "var(--green)" }}>{p.mood}</span>}
                    {p.created_by_ai && <span style={{ fontSize: 11, padding: "2px 8px", background: "#a78bfa20", border: "1px solid #a78bfa40", borderRadius: 20, color: "#a78bfa" }}>✦ IA</span>}
                  </div>
                  {p.description && <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 8 }}>{p.description}</div>}
                  <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 8 }}>{p.track_count || 0} faixas · clique para editar</div>
                </>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div style={{ textAlign: "center", padding: 60, color: "var(--muted)", background: "var(--bg2)", borderRadius: 12 }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>⏳</div>
          <p>Carregando playlists...</p>
        </div>
      )}
    </div>
  );
}

// ── ALBUMS (Biblioteca Musical — apenas Master) ───────────────────────────────
function Albums({ token, user }) {
  const [albums, setAlbums]               = useState([]);
  const [categories, setCategories]       = useState([]);
  const [openCats, setOpenCats]           = useState({});          // acordeão
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [libTrackPopup, setLibTrackPopup] = useState(null);
  const libAudioRef = useRef(null);
  const [libPlaying, setLibPlaying] = useState(null);
  const [libProgress, setLibProgress] = useState(0);
  const [libTime, setLibTime] = useState(0);
  const [libDuration, setLibDuration] = useState(0);
  const libProgressInterval = useRef(null);
  const [libPreviewError, setLibPreviewError] = useState(false);
  const [libAlbumTracks, setLibAlbumTracks] = useState({});
  const [libOpenAlbums, setLibOpenAlbums] = useState({});
  const [libLastTrack, setLibLastTrack] = useState(null);
  const [libStoreId, setLibStoreId] = useState("");
  const [libPlaylists, setLibPlaylists] = useState([]);
  const [libAtpAdding, setLibAtpAdding] = useState(null);
  const [libAtpDone, setLibAtpDone] = useState(null);
  const [tracks, setTracks]               = useState([]);

  // formulário novo álbum
  const [showForm, setShowForm]   = useState(false);
  const [form, setForm]           = useState({ name: "", description: "", genre: "", mood: "", category_id: "" });
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState("");

  // formulário nova categoria
  const [showCatForm, setShowCatForm] = useState(false);
  const [catName, setCatName]         = useState("");
  const [savingCat, setSavingCat]     = useState(false);
  const [catError, setCatError]       = useState("");

  // upload
  const [uploading, setUploading]         = useState(false);
  const [uploadFiles, setUploadFiles]     = useState([]);
  const [uploadArtist, setUploadArtist]   = useState("");
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });
  const [showUpload, setShowUpload]       = useState(false);

  const [searchBib, setSearchBib] = useState("");
  const [editingAlbum, setEditingAlbum]   = useState(null); // album sendo editado
  const [editCatId, setEditCatId]         = useState("");
  const [savingEdit, setSavingEdit]       = useState(false);

  const isMaster = user?.plan === "master";

  // ── carregamento ─────────────────────────────────────────────
  const loadAlbums = () => {
    apiFetch("/api/albums", {}, token)
      .then(d => setAlbums((Array.isArray(d) ? d : []).filter(a => !a.is_playlist)))
      .catch(() => {});
  };

  const loadCategories = () => {
    apiFetch("/api/categories", {}, token)
      .then(d => setCategories(Array.isArray(d) ? d : []))
      .catch(() => {});
  };

  useEffect(() => {
    loadAlbums(); loadCategories();
    apiFetch("/api/stores", {}, token).then(d => {
      const list = Array.isArray(d) ? d : (d.stores || []);
      if (list.length > 0) {
        setLibStoreId(list[0].id);
        apiFetch(`/api/stores/${list[0].id}/playlists`, {}, token)
          .then(p => setLibPlaylists(Array.isArray(p) ? p : (p.playlists || [])))
          .catch(() => {});
      }
    }).catch(() => {});
  }, [token]);

  useEffect(() => {
    if (!selectedAlbum) return;
    apiFetch(`/api/albums/${selectedAlbum.id}/tracks`, {}, token)
      .then(d => setTracks(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, [selectedAlbum, token]);

  async function loadLibAlbumTracks(albumId) {
    if (libAlbumTracks[albumId]) return;
    const d = await apiFetch(`/api/albums/${albumId}/tracks`, {}, token).catch(() => []);
    setLibAlbumTracks(p => ({ ...p, [albumId]: Array.isArray(d) ? d : [] }));
  }

  function libPlayPreview(url, id) {
    const audio = libAudioRef.current;
    if (!audio) return;
    if (libPlaying === id) { audio.pause(); setLibPlaying(null); clearInterval(libProgressInterval.current); return; }
    setLibPreviewError(false);
    audio.src = url;
    audio.play().catch(() => { setLibPreviewError(true); setLibPlaying(null); });
    audio.onerror = () => { setLibPreviewError(true); setLibPlaying(null); clearInterval(libProgressInterval.current); };
    setLibPlaying(id); setLibProgress(0); setLibTime(0); setLibDuration(0);
    clearInterval(libProgressInterval.current);
    libProgressInterval.current = setInterval(() => {
      if (!audio.duration) return;
      setLibTime(audio.currentTime); setLibDuration(audio.duration);
      setLibProgress((audio.currentTime / audio.duration) * 100);
    }, 200);
    audio.onended = () => { setLibPlaying(null); setLibProgress(0); clearInterval(libProgressInterval.current); };
  }

  function seekLibPreview(pct) {
    const audio = libAudioRef.current;
    if (!audio || !audio.duration) return;
    audio.currentTime = (pct / 100) * audio.duration;
    setLibProgress(pct);
  }

  function openLibPopup(t, albumName, albumId) {
    libAudioRef.current?.pause();
    setLibPlaying(null); setLibProgress(0); setLibTime(0); setLibDuration(0);
    setLibPreviewError(false); setLibAtpDone(null);
    clearInterval(libProgressInterval.current);
    setLibLastTrack(t.id);
    setLibTrackPopup({ ...t, albumName, albumId });
    setTimeout(() => { if (t.bunny_url) libPlayPreview(t.bunny_url, t.id + "_popup"); }, 50);
  }

  function closeLibPopup() {
    libAudioRef.current?.pause();
    setLibPlaying(null); setLibProgress(0);
    clearInterval(libProgressInterval.current);
    setLibTrackPopup(null); setLibAtpDone(null);
  }

  function libNavigateTrack(dir) {
    if (!libTrackPopup?.albumId) return;
    const ts = libAlbumTracks[libTrackPopup.albumId] || [];
    const idx = ts.findIndex(t => t.id === libTrackPopup.id);
    const next = ts[idx + dir];
    if (!next) return;
    libAudioRef.current?.pause();
    setLibPlaying(null); clearInterval(libProgressInterval.current);
    setLibProgress(0); setLibTime(0); setLibDuration(0);
    setLibPreviewError(false); setLibAtpDone(null);
    setLibLastTrack(next.id);
    setLibTrackPopup({ ...next, albumName: libTrackPopup.albumName, albumId: libTrackPopup.albumId });
    setTimeout(() => { if (next.bunny_url) libPlayPreview(next.bunny_url, next.id + "_popup"); }, 50);
  }

  async function libAtpAdd(playlistId) {
    if (!libTrackPopup || !libStoreId) return;
    setLibAtpAdding(playlistId);
    try {
      await apiFetch(`/api/stores/${libStoreId}/playlists/${playlistId}/tracks`, {
        method: "POST", body: JSON.stringify({ track_id: libTrackPopup.id }),
      }, token);
      setLibAtpDone(playlistId);
    } catch { alert("Erro ao adicionar à playlist"); }
    setLibAtpAdding(null);
  }

  const fmtLibDur = (s) => { if (!s || isNaN(s)) return "0:00"; const m = Math.floor(s/60), sec = Math.floor(s%60); return `${m}:${String(sec).padStart(2,"0")}`; };

  // ── acordeão ─────────────────────────────────────────────────
  function toggleCat(catId) {
    setOpenCats(prev => ({ ...prev, [catId]: !prev[catId] }));
  }

  // ── criar categoria ──────────────────────────────────────────
  async function createCategory() {
    if (!catName.trim()) return setCatError("Nome é obrigatório");
    setSavingCat(true); setCatError("");
    try {
      await apiFetch("/api/categories", { method: "POST", body: JSON.stringify({ name: catName.trim() }) }, token);
      setCatName(""); setShowCatForm(false);
      loadCategories();
    } catch (e) { setCatError(e.message); }
    setSavingCat(false);
  }

  async function removeCategory(id) {
    if (!window.confirm("Apagar esta categoria? Os álbuns dentro dela ficam sem categoria.")) return;
    try {
      await apiFetch(`/api/categories/${id}`, { method: "DELETE" }, token);
      loadCategories(); loadAlbums();
    } catch { alert("Erro ao apagar categoria"); }
  }

  // ── criar álbum ──────────────────────────────────────────────
  async function createAlbum() {
    if (!form.name) return setError("Nome é obrigatório");
    setSaving(true); setError("");
    try {
      const body = { ...form, category_id: form.category_id || null };
      await apiFetch("/api/albums", { method: "POST", body: JSON.stringify(body) }, token);
      setForm({ name: "", description: "", genre: "", mood: "", category_id: "" });
      setShowForm(false);
      loadAlbums();
    } catch (e) { setError(e.message); }
    setSaving(false);
  }

  async function removeAlbum(id) {
    if (!window.confirm("Apagar este álbum e todas as suas músicas?")) return;
    try {
      await apiFetch(`/api/albums/${id}`, { method: "DELETE" }, token);
      if (selectedAlbum?.id === id) setSelectedAlbum(null);
      loadAlbums();
    } catch { alert("Erro ao apagar álbum"); }
  }

  async function saveAlbumCategory(album) {
    setSavingEdit(true);
    try {
      await apiFetch(`/api/albums/${album.id}`, {
        method: "PUT",
        body: JSON.stringify({ category_id: editCatId || null }),
      }, token);
      setEditingAlbum(null);
      loadAlbums();
    } catch { alert("Erro ao salvar categoria"); }
    setSavingEdit(false);
  }

  // ── upload de faixas ─────────────────────────────────────────
  async function uploadTracks() {
    if (uploadFiles.length === 0) return setError("Selecione pelo menos um arquivo");
    setUploading(true); setError("");
    setUploadProgress({ done: 0, total: uploadFiles.length });
    const token_ = localStorage.getItem("radioin_token");
    let done = 0;
    for (const file of uploadFiles) {
      try {
        const fd = new FormData();
        fd.append("audio", file);
        fd.append("title", file.name.replace(/\.[^/.]+$/, ""));
        if (uploadArtist) fd.append("artist", uploadArtist);
        const res = await fetch(`${API}/api/albums/${selectedAlbum.id}/tracks`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token_}` },
          body: fd,
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error);
      } catch (e) { setError(`Erro em ${file.name}: ${e.message}`); }
      done++;
      setUploadProgress({ done, total: uploadFiles.length });
    }
    setUploadFiles([]); setUploadArtist(""); setShowUpload(false);
    apiFetch(`/api/albums/${selectedAlbum.id}/tracks`, {}, token)
      .then(d => setTracks(Array.isArray(d) ? d : []));
    loadAlbums();
    setUploading(false);
  }

  async function removeTrack(trackId, albumId) {
    if (!window.confirm("Apagar esta faixa?")) return;
    try {
      const albumId_ = albumId || selectedAlbum?.id;
      await apiFetch(`/api/albums/${albumId_}/tracks/${trackId}`, { method: "DELETE" }, token);
      setTracks(prev => prev.filter(t => t.id !== trackId));
      if (albumId) setLibAlbumTracks(p => ({ ...p, [albumId]: (p[albumId] || []).filter(t => t.id !== trackId) }));
      loadAlbums();
    } catch { alert("Erro ao apagar faixa"); }
  }

  // ── render do álbum em acordeão (novo visual) ───────────────
  function LibAlbumAccordion({ a }) {
    const isOpen = libOpenAlbums[a.id] === true;
    return (
      <div style={{ marginBottom: 6 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 12px", background: "var(--bg3)", borderRadius: 8, border: `1px solid ${selectedAlbum?.id === a.id ? "#00ff7f40" : "var(--border)"}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, cursor: "pointer", overflow: "hidden" }}
            onClick={() => { setLibOpenAlbums(p => ({ ...p, [a.id]: !isOpen })); loadLibAlbumTracks(a.id); }}>
            <span style={{ fontSize: 13, flexShrink: 0 }}>💿</span>
            <div style={{ overflow: "hidden" }}>
              <div style={{ fontWeight: 600, fontSize: 12, color: selectedAlbum?.id === a.id ? "var(--green)" : "var(--text)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{a.name}</div>
              <div style={{ fontSize: 10, color: "var(--muted)" }}>{a.track_count || 0} faixas{a.genre ? ` · ${a.genre}` : ""}</div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
            <button onClick={e => { e.stopPropagation(); setSelectedAlbum(selectedAlbum?.id === a.id ? null : a); setShowUpload(true); }}
              title="Upload de músicas"
              style={{ background: "none", border: "none", color: selectedAlbum?.id === a.id ? "var(--green)" : "var(--muted)", fontSize: 14, cursor: "pointer", padding: "2px 4px" }}
              onMouseEnter={e => e.target.style.color = "var(--green)"}
              onMouseLeave={e => e.target.style.color = selectedAlbum?.id === a.id ? "var(--green)" : "var(--muted)"}>⬆</button>
            <button onClick={e => { e.stopPropagation(); if (editingAlbum?.id === a.id) { setEditingAlbum(null); } else { setEditingAlbum(a); setEditCatId(a.category_id || ""); } }}
              title="Mover para categoria"
              style={{ background: "none", border: "none", color: "var(--muted)", fontSize: 12, cursor: "pointer", padding: "2px 4px" }}
              onMouseEnter={e => e.target.style.color = "var(--green)"}
              onMouseLeave={e => e.target.style.color = "var(--muted)"}>✏️</button>
            <button onClick={e => { e.stopPropagation(); removeAlbum(a.id); }}
              style={{ background: "none", border: "none", color: "var(--muted)", fontSize: 13, cursor: "pointer", padding: "2px 4px" }}
              onMouseEnter={e => e.target.style.color = "var(--red)"}
              onMouseLeave={e => e.target.style.color = "var(--muted)"}>✕</button>
            <span onClick={() => { setLibOpenAlbums(p => ({ ...p, [a.id]: !isOpen })); loadLibAlbumTracks(a.id); }}
              style={{ color: "var(--muted)", fontSize: 11, cursor: "pointer", padding: "4px" }}>{isOpen ? "▲" : "▼"}</span>
          </div>
        </div>
        {editingAlbum?.id === a.id && (
          <div style={{ padding: "10px 12px", background: "var(--bg2)", border: "1px solid var(--border)", borderTop: "none", borderRadius: "0 0 8px 8px" }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 6 }}>Mover para categoria:</div>
            <select value={editCatId} onChange={e => setEditCatId(e.target.value)} style={{ width: "100%", marginBottom: 8, fontSize: 12, padding: "6px 10px" }}>
              <option value="">Sem categoria</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <div style={{ display: "flex", gap: 6 }}>
              <button onClick={() => saveAlbumCategory(a)} disabled={savingEdit}
                style={{ flex: 1, padding: "6px", background: "var(--green)", color: "#080d08", border: "none", borderRadius: 6, fontWeight: 700, fontSize: 12 }}>
                {savingEdit ? "..." : "Salvar"}
              </button>
              <button onClick={() => setEditingAlbum(null)}
                style={{ padding: "6px 10px", background: "var(--bg3)", color: "var(--muted)", border: "1px solid var(--border)", borderRadius: 6, fontSize: 12 }}>Cancelar</button>
            </div>
          </div>
        )}
        {isOpen && (
          <div style={{ paddingLeft: 8, paddingTop: 4, maxHeight: 300, overflowY: "auto" }}>
            {!(libAlbumTracks[a.id]) ? (
              <div style={{ fontSize: 11, color: "var(--muted)", padding: "8px 10px" }}>Carregando...</div>
            ) : libAlbumTracks[a.id].length === 0 ? (
              <div style={{ fontSize: 11, color: "var(--muted)", padding: "8px 10px" }}>Nenhuma faixa neste álbum</div>
            ) : libAlbumTracks[a.id].map(t => (
              <div key={t.id}
                onClick={() => openLibPopup(t, a.name, a.id)}
                style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 10px", background: libLastTrack === t.id ? "rgba(0,255,127,0.08)" : "rgba(255,255,255,0.02)", borderRadius: 6, marginBottom: 3, border: `1px solid ${libLastTrack === t.id ? "rgba(0,255,127,0.3)" : "rgba(255,255,255,0.05)"}`, cursor: "pointer" }}>
                <span style={{ fontSize: 11, color: "var(--muted)", flexShrink: 0 }}>🎵</span>
                <div style={{ flex: 1, overflow: "hidden" }}>
                  <div style={{ fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: libLastTrack === t.id ? "var(--green)" : "var(--text)" }}>{t.title}</div>
                  <div style={{ fontSize: 10, color: "var(--muted)" }}>{t.artist || "—"}{t.duration_seconds ? ` · ${fmtLibDur(t.duration_seconds)}` : ""}</div>
                </div>
                <button onClick={e => { e.stopPropagation(); removeTrack(t.id, a.id); }}
                  style={{ background: "none", border: "1px solid #ff446640", borderRadius: 5, color: "var(--red)", fontSize: 11, width: 24, height: 24, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}
                  onMouseEnter={e => e.currentTarget.style.background = "#ff446620"}
                  onMouseLeave={e => e.currentTarget.style.background = "none"}>✕</button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── guard ────────────────────────────────────────────────────
  if (!isMaster) return (
    <div className="fadeIn" style={{ padding: "32px", display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
      <div style={{ textAlign: "center", color: "var(--muted)" }}>
        <div style={{ fontSize: 40, marginBottom: 16 }}>♬</div>
        <p style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>Funcionalidade Master</p>
        <p style={{ fontSize: 13 }}>Apenas o plano Master pode gerenciar a biblioteca musical.</p>
      </div>
    </div>
  );

  // ── álbuns filtrados por busca ────────────────────────────────
  const q = searchBib.trim().toLowerCase();
  const filteredAlbums = q
    ? albums.filter(a => a.name.toLowerCase().includes(q) || (a.genre || "").toLowerCase().includes(q) || (a.category_name || "").toLowerCase().includes(q))
    : albums;

  // ── agrupar álbuns por categoria ─────────────────────────────
  const semCategoria = filteredAlbums.filter(a => !a.category_id);
  const porCategoria = categories.map(cat => ({
    ...cat,
    albums: filteredAlbums.filter(a => a.category_id === cat.id),
  })).filter(cat => cat.albums.length > 0 || !q);

  return (
    <>
    <audio ref={libAudioRef} style={{ display: "none" }} />

    <div className="fadeIn" style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>

      {/* ── Popup de preview de faixa ── */}
      {libTrackPopup && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={closeLibPopup}>
          <div style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 16, padding: "28px", width: 420, maxWidth: "90vw", boxShadow: "0 24px 60px rgba(0,0,0,0.6)" }}
            onClick={e => e.stopPropagation()}>
            {/* Cabeçalho com navegação */}
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 20 }}>
              <div style={{ flex: 1, overflow: "hidden", paddingRight: 12 }}>
                <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{libTrackPopup.title}</div>
                <div style={{ fontSize: 13, color: "var(--muted)" }}>{libTrackPopup.artist || "—"}</div>
                <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 4 }}>
                  💿 {libTrackPopup.albumName}{libTrackPopup.duration_seconds ? ` · ${fmtLibDur(libTrackPopup.duration_seconds)}` : ""}
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                {(() => {
                  const ts = libAlbumTracks[libTrackPopup.albumId] || [];
                  const idx = ts.findIndex(t => t.id === libTrackPopup.id);
                  return (<>
                    <button onClick={() => libNavigateTrack(-1)} disabled={idx <= 0}
                      style={{ background: "none", border: "1px solid var(--border)", borderRadius: 6, color: idx <= 0 ? "var(--bg3)" : "var(--muted)", fontSize: 12, width: 28, height: 28, cursor: idx <= 0 ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>&#9664;</button>
                    <span style={{ fontSize: 11, color: "var(--muted)", fontFamily: "var(--mono)", minWidth: 44, textAlign: "center" }}>{idx + 1}/{ts.length}</span>
                    <button onClick={() => libNavigateTrack(1)} disabled={idx >= ts.length - 1}
                      style={{ background: "none", border: "1px solid var(--border)", borderRadius: 6, color: idx >= ts.length - 1 ? "var(--bg3)" : "var(--muted)", fontSize: 12, width: 28, height: 28, cursor: idx >= ts.length - 1 ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>&#9654;</button>
                    <button onClick={closeLibPopup} style={{ background: "none", border: "none", color: "var(--muted)", fontSize: 20, cursor: "pointer", lineHeight: 1, marginLeft: 4 }}>✕</button>
                  </>);
                })()}
              </div>
            </div>

            {/* Player */}
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
              {(!libTrackPopup.bunny_url || libPreviewError) ? (
                <div style={{ flex: 1, padding: "10px 14px", background: "#ff446615", border: "1px solid #ff446640", borderRadius: 10, fontSize: 12, color: "#ff9999", textAlign: "center" }}>
                  ⚠️ {libPreviewError ? "Arquivo de áudio não encontrado no CDN" : "Áudio não disponível"}
                </div>
              ) : (<>
                <button onClick={() => libPlayPreview(libTrackPopup.bunny_url, libTrackPopup.id + "_popup")}
                  style={{ width: 48, height: 48, borderRadius: "50%", background: libPlaying === libTrackPopup.id + "_popup" ? "var(--green)" : "rgba(0,255,127,0.1)", border: "1px solid #00ff7f40", color: libPlaying === libTrackPopup.id + "_popup" ? "#080d08" : "var(--green)", fontSize: 18, cursor: "pointer", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {libPlaying === libTrackPopup.id + "_popup" ? "⏸" : "▶"}
                </button>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 11, color: "var(--muted)", fontFamily: "var(--mono)", minWidth: 32 }}>{fmtLibDur(libTime)}</span>
                    <div style={{ flex: 1, height: 20, display: "flex", alignItems: "center", cursor: "pointer" }}
                      onMouseDown={e => { e.preventDefault(); const r = e.currentTarget.getBoundingClientRect(); seekLibPreview(((e.clientX-r.left)/r.width)*100); }}
                      onTouchStart={e => { e.preventDefault(); const t = e.touches[0]; const r = e.currentTarget.getBoundingClientRect(); seekLibPreview(((t.clientX-r.left)/r.width)*100); }}>
                      <div style={{ width: "100%", height: 6, background: "rgba(255,255,255,0.12)", borderRadius: 3, overflow: "hidden" }}>
                        <div style={{ height: "100%", width: `${libPlaying === libTrackPopup.id + "_popup" ? libProgress : 0}%`, background: "var(--green)", borderRadius: 3, transition: "width 0.2s" }} />
                      </div>
                    </div>
                    <span style={{ fontSize: 11, color: "var(--muted)", fontFamily: "var(--mono)", minWidth: 32, textAlign: "right" }}>{fmtLibDur(libDuration)}</span>
                  </div>
                </div>
              </>)}
            </div>

            {/* + Playlist — lista de playlists da loja (sem seletor) */}
            <div>
              <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 8, fontWeight: 600 }}>Adicionar à playlist:</div>
              {libPlaylists.length === 0 ? (
                <div style={{ textAlign: "center", padding: "14px", color: "var(--muted)", fontSize: 12, background: "var(--bg3)", borderRadius: 10 }}>
                  Nenhuma playlist criada ainda.
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 200, overflowY: "auto" }}>
                  {libPlaylists.map(p => {
                    const done = libAtpDone === p.id;
                    const adding = libAtpAdding === p.id;
                    return (
                      <div key={p.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", borderRadius: 8, background: done ? "var(--green3)" : "var(--bg3)", border: `1px solid ${done ? "#00ff7f40" : "var(--border)"}`, transition: "all .2s" }}>
                        <div style={{ flex: 1, overflow: "hidden" }}>
                          <div style={{ fontWeight: 600, fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: done ? "var(--green)" : "var(--text)" }}>{p.name}</div>
                          <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 1 }}>{p.track_count || 0} faixas</div>
                        </div>
                        <button onClick={() => !done && !adding && libAtpAdd(p.id)} disabled={adding}
                          style={{ marginLeft: 10, padding: "5px 12px", background: done ? "var(--green)" : adding ? "var(--bg2)" : "rgba(0,255,127,0.12)", color: done ? "#080d08" : adding ? "var(--muted)" : "var(--green)", border: `1px solid ${done ? "var(--green)" : "#00ff7f40"}`, borderRadius: 8, fontWeight: 700, fontSize: 12, cursor: done || adding ? "default" : "pointer", flexShrink: 0, transition: "all .2s" }}>
                          {done ? "✓ Adicionado" : adding ? "..." : "+ Adicionar"}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Cabeçalho ── */}
      <div style={{ padding: "20px 28px 14px", borderBottom: "1px solid var(--border)", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 800 }}>Biblioteca Musical</h1>
            <p style={{ fontSize: 12, color: "var(--muted)", marginTop: 2 }}>{albums.length} álbuns · {categories.length} categorias</p>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => { setShowCatForm(!showCatForm); setShowForm(false); }}
              style={{ padding: "8px 14px", background: showCatForm ? "var(--bg3)" : "var(--bg2)", color: "var(--green)", border: "1px solid var(--border)", borderRadius: 8, fontWeight: 700, fontSize: 13 }}>
              + Categoria
            </button>
            <button onClick={() => { setShowForm(!showForm); setShowCatForm(false); }}
              style={{ padding: "8px 14px", background: "var(--green)", color: "#080d08", border: "none", borderRadius: 8, fontWeight: 700, fontSize: 13 }}>
              + Álbum
            </button>
          </div>
        </div>
        {/* busca */}
        <div style={{ position: "relative", maxWidth: 360 }}>
          <input placeholder="🔍 Buscar álbum..." value={searchBib} onChange={e => setSearchBib(e.target.value)}
            style={{ width: "100%", paddingRight: searchBib ? 32 : 14 }} />
          {searchBib && <button onClick={() => setSearchBib("")}
            style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "var(--muted)", cursor: "pointer", fontSize: 14 }}>✕</button>}
        </div>
      </div>

      {/* ── Forms ── */}
      {showCatForm && (
        <div style={{ padding: "12px 28px", borderBottom: "1px solid var(--border)", background: "var(--bg2)" }}>
          <input placeholder="Nome da categoria *" value={catName} onChange={e => setCatName(e.target.value)}
            style={{ width: 280, marginRight: 8 }} onKeyDown={e => e.key === "Enter" && createCategory()} />
          {catError && <span style={{ fontSize: 12, color: "var(--red)", marginRight: 8 }}>{catError}</span>}
          <button onClick={createCategory} disabled={savingCat}
            style={{ padding: "8px 16px", background: "var(--green)", color: "#080d08", border: "none", borderRadius: 6, fontWeight: 700, fontSize: 12, marginRight: 6 }}>
            {savingCat ? "..." : "Criar"}
          </button>
          <button onClick={() => { setShowCatForm(false); setCatError(""); }}
            style={{ padding: "8px 12px", background: "var(--bg3)", color: "var(--muted)", border: "1px solid var(--border)", borderRadius: 6, fontSize: 12 }}>Cancelar</button>
        </div>
      )}
      {showForm && (
        <div style={{ padding: "12px 28px", borderBottom: "1px solid var(--border)", background: "var(--bg2)", display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <input placeholder="Nome do álbum *" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={{ width: 200 }} />
          <select value={form.category_id} onChange={e => setForm(f => ({ ...f, category_id: e.target.value }))} style={{ width: 160 }}>
            <option value="">Sem categoria</option>
            {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <input placeholder="Gênero" value={form.genre} onChange={e => setForm(f => ({ ...f, genre: e.target.value }))} style={{ width: 120 }} />
          {error && <span style={{ fontSize: 12, color: "var(--red)" }}>{error}</span>}
          <button onClick={createAlbum} disabled={saving}
            style={{ padding: "8px 16px", background: "var(--green)", color: "#080d08", border: "none", borderRadius: 6, fontWeight: 700, fontSize: 12 }}>
            {saving ? "..." : "Criar"}
          </button>
          <button onClick={() => { setShowForm(false); setError(""); }}
            style={{ padding: "8px 12px", background: "var(--bg3)", color: "var(--muted)", border: "1px solid var(--border)", borderRadius: 6, fontSize: 12 }}>Cancelar</button>
        </div>
      )}

      {/* ── Acordeão principal ── */}
      <div style={{ flex: 1, overflowY: "auto", padding: "16px 28px" }}>
        {albums.length === 0 ? (
          <div style={{ textAlign: "center", padding: 60, color: "var(--muted)" }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>♬</div>
            <p>Nenhum álbum na biblioteca.</p>
          </div>
        ) : (
          <>
            {/* categorias */}
            {porCategoria.map(cat => {
              const isCatOpen = openCats["cat_" + cat.id] === true;
              return (
                <div key={cat.id} style={{ marginBottom: 8 }}>
                  <div onClick={() => setOpenCats(p => ({ ...p, ["cat_" + cat.id]: !isCatOpen }))}
                    style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px", background: "linear-gradient(90deg, rgba(0,255,127,0.08), rgba(0,255,127,0.03))", borderRadius: 8, border: "1px solid rgba(0,255,127,0.2)", cursor: "pointer", userSelect: "none", marginBottom: 6 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ fontSize: 15 }}>📁</span>
                      <span style={{ fontWeight: 800, fontSize: 13, letterSpacing: 1, color: "var(--green)", textTransform: "uppercase" }}>{cat.name}</span>
                      <span style={{ fontSize: 11, color: "var(--muted)", background: "rgba(0,255,127,0.1)", border: "1px solid rgba(0,255,127,0.2)", borderRadius: 10, padding: "1px 8px" }}>{cat.albums.length} álbun{cat.albums.length !== 1 ? "s" : ""}</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <button onClick={e => { e.stopPropagation(); removeCategory(cat.id); }}
                        style={{ background: "none", border: "none", color: "var(--muted)", fontSize: 13, cursor: "pointer", padding: "2px 4px" }}
                        onMouseEnter={e => e.target.style.color = "var(--red)"}
                        onMouseLeave={e => e.target.style.color = "var(--muted)"}>✕</button>
                      <span style={{ color: "var(--green)", fontSize: 11, opacity: 0.6 }}>{isCatOpen ? "▲" : "▼"}</span>
                    </div>
                  </div>
                  {isCatOpen && (
                    <div style={{ paddingLeft: 12, paddingTop: 4 }}>
                      {cat.albums.length === 0
                        ? <div style={{ fontSize: 11, color: "var(--muted)", padding: "8px 10px" }}>Nenhum álbum nesta categoria</div>
                        : cat.albums.map(a => <LibAlbumAccordion key={a.id} a={a} />)
                      }
                    </div>
                  )}
                </div>
              );
            })}

            {/* álbuns sem categoria */}
            {semCategoria.length > 0 && (
              <div style={{ marginTop: porCategoria.length > 0 ? 8 : 0 }}>
                {porCategoria.length > 0 && <div style={{ fontSize: 11, color: "var(--muted)", letterSpacing: 1, margin: "8px 0 6px", textTransform: "uppercase" }}>Sem categoria</div>}
                {semCategoria.map(a => <LibAlbumAccordion key={a.id} a={a} />)}
              </div>
            )}

            {/* Upload de músicas — abre ao selecionar álbum via ✏️ */}
            {selectedAlbum && (
              <div style={{ marginTop: 24, background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 12, padding: "20px" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 15 }}>⬆ Upload para: {selectedAlbum.name}</div>
                    <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 2 }}>{tracks.length} faixas já carregadas</div>
                  </div>
                  <button onClick={() => { setSelectedAlbum(null); setShowUpload(false); }}
                    style={{ background: "none", border: "none", color: "var(--muted)", fontSize: 20, cursor: "pointer" }}>✕</button>
                </div>
                <div style={{ marginBottom: 10 }}>
                  <input placeholder="Artista (opcional)" value={uploadArtist} onChange={e => setUploadArtist(e.target.value)} style={{ width: 280 }} />
                </div>
                <input type="file" accept=".mp3,.wav,.ogg,.aac,.flac,.m4a" multiple
                  onChange={e => setUploadFiles(Array.from(e.target.files))}
                  style={{ background: "var(--bg3)", border: "1px solid var(--border)", borderRadius: 8, padding: "8px 12px", width: "100%", color: "var(--text)", marginBottom: 10 }} />
                {uploadFiles.length > 0 && (
                  <div style={{ marginBottom: 10, display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {uploadFiles.map((f, i) => (
                      <span key={i} style={{ fontSize: 11, padding: "3px 10px", background: "var(--bg3)", border: "1px solid var(--border)", borderRadius: 20, color: "var(--muted)" }}>
                        {f.name.replace(/\.[^/.]+$/, "")}
                      </span>
                    ))}
                  </div>
                )}
                {uploading && (
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 4 }}>Enviando {uploadProgress.done}/{uploadProgress.total}...</div>
                    <div style={{ height: 4, background: "var(--bg3)", borderRadius: 2, overflow: "hidden" }}>
                      <div style={{ height: "100%", background: "var(--green)", borderRadius: 2, width: `${uploadProgress.total ? (uploadProgress.done / uploadProgress.total) * 100 : 0}%`, transition: "width 0.3s" }} />
                    </div>
                  </div>
                )}
                {error && <div style={{ fontSize: 12, color: "var(--red)", marginBottom: 8 }}>{error}</div>}
                <button onClick={uploadTracks} disabled={uploading || uploadFiles.length === 0}
                  style={{ padding: "10px 24px", background: uploading || uploadFiles.length === 0 ? "var(--bg3)" : "var(--green)", color: uploading || uploadFiles.length === 0 ? "var(--muted)" : "#080d08", border: "none", borderRadius: 8, fontWeight: 700 }}>
                  {uploading ? `Enviando ${uploadProgress.done}/${uploadProgress.total}...` : `⬆ Enviar ${uploadFiles.length > 0 ? uploadFiles.length + " música(s)" : "Músicas"}`}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
    </>
  );
}



// ── ADMIN (Master — gerencia clientes) ───────────────────────────────────────
function Admin({ token, onImpersonate }) {
  const [impersonating, setImpersonating] = useState(null);

  async function enterAsClient(u) {
    setImpersonating(u.id);
    try {
      const d = await apiFetch(`/api/admin/users/${u.id}/impersonate`, { method: "POST" }, token);
      if (d && d.token && onImpersonate) onImpersonate(d.token, d.user);
    } catch (e) {
      alert("Não foi possível entrar como cliente: " + e.message);
    }
    setImpersonating(null);
  }

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", plan: "starter", whatsapp: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [backingUp, setBackingUp] = useState(false);
  const [backupResult, setBackupResult] = useState(null);

  // ── Aviso de manutenção no remoto (abas TTS / Veículo) ──
  const [rNotice, setRNotice] = useState(null);
  const [rNoticeMsg, setRNoticeMsg] = useState("");
  useEffect(() => {
    fetch(`${API}/api/remote/notice`).then(r => r.json()).then(setRNotice).catch(() => {});
  }, []);
  async function saveRNotice(next) {
    setRNoticeMsg("");
    try {
      const d = await apiFetch("/api/admin/remote-notice", { method: "PUT", body: JSON.stringify(next) }, token);
      setRNotice(d); setRNoticeMsg("✓ Salvo — aparece no remoto ao abrir a aba");
      setTimeout(() => setRNoticeMsg(""), 4000);
    } catch (e) { setRNoticeMsg("✗ " + e.message); }
  }

  // ── Áudios do anúncio de veículos (gerados uma vez por IA) ──
  const [carSeg, setCarSeg] = useState(null);       // { total, ready, missing }
  const [carSegBusy, setCarSegBusy] = useState(false);
  const [carSegMsg, setCarSegMsg] = useState("");
  const [carSegReview, setCarSegReview] = useState(false);   // lista para ouvir/refazer
  const [carSegSel, setCarSegSel] = useState([]);             // chaves marcadas p/ refazer
  const [carSegVer, setCarSegVer] = useState(Date.now());     // força recarregar o áudio após refazer
  // ── Modelos de veículos (cadastro + gravação do locutor) ──
  const [carModels, setCarModels] = useState(null);   // { brands, models }
  const [carModelsOpen, setCarModelsOpen] = useState(false);
  const [newModel, setNewModel] = useState({ brand: "volkswagen", name: "" });
  const [carModelsMsg, setCarModelsMsg] = useState("");
  function loadCarModels() {
    apiFetch("/api/admin/car-models", {}, token).then(setCarModels).catch(() => {});
  }
  useEffect(() => { if (carModelsOpen && !carModels) loadCarModels(); }, [carModelsOpen]); // eslint-disable-line
  async function addCarModel() {
    if (!newModel.name.trim()) return;
    setCarModelsMsg("");
    try {
      await apiFetch("/api/admin/car-models", { method: "POST", body: JSON.stringify(newModel) }, token);
      setNewModel(m => ({ ...m, name: "" }));
      setCarModelsMsg("✓ Modelo cadastrado — agora envie a gravação (⬆)");
      loadCarModels();
    } catch (e) { setCarModelsMsg("✗ " + e.message); }
  }
  async function delCarModel(m) {
    if (!window.confirm(`Remover o modelo "${m.name}"? O áudio dele também será apagado.`)) return;
    try { await apiFetch(`/api/admin/car-models/${m.id}`, { method: "DELETE" }, token); loadCarModels(); }
    catch (e) { setCarModelsMsg("✗ " + e.message); }
  }

  // Gravações do locutor: nome do arquivo = chave (ex.: marca_fiat.mp3), ou 1 arquivo por linha
  async function uploadCarSeg(fileList, key = null) {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setCarSegBusy(true); setCarSegMsg("");
    try {
      const fd = new FormData();
      files.forEach(f => fd.append("files", f));
      if (key) fd.append("key", key);
      const res = await fetch(`${API}/api/admin/car-segments/upload`, { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || "Erro no envio");
      const bad = (d.results || []).filter(r => !r.ok);
      setCarSegMsg(`Gravações salvas: ${d.saved}.` + (bad.length ? ` Parou: ${bad.map(r => `${r.file} (${r.error})`).join("; ")}` : ""));
      setCarSegVer(Date.now());
    } catch (e) { setCarSegMsg("Erro: " + e.message); }
    setCarSegBusy(false);
    loadCarSeg();
    if (carModelsOpen) loadCarModels();
  }

  async function regenCarSeg() {
    if (!carSegSel.length) return;
    setCarSegBusy(true); setCarSegMsg("");
    try {
      const d = await apiFetch("/api/admin/car-segments/regenerate", { method: "POST", body: JSON.stringify({ keys: carSegSel }) }, token);
      if (d.busy) setCarSegMsg("Já está gerando, aguarde...");
      else setCarSegMsg(`Refeitos: ${d.generatedNow}.` + (d.error ? ` Parou: ${d.error}` : ""));
      setCarSegSel([]); setCarSegVer(Date.now());
    } catch (e) { setCarSegMsg("Erro: " + e.message); }
    setCarSegBusy(false);
    loadCarSeg();
  }
  function loadCarSeg() {
    apiFetch("/api/admin/car-segments", {}, token).then(setCarSeg).catch(() => {});
  }
  useEffect(() => { loadCarSeg(); }, []); // eslint-disable-line
  async function generateCarSeg() {
    setCarSegBusy(true); setCarSegMsg("");
    try {
      const d = await apiFetch("/api/admin/car-segments/generate", { method: "POST", body: JSON.stringify({ max: 30 }) }, token);
      if (d.busy) setCarSegMsg("Já está gerando, aguarde...");
      else setCarSegMsg(`Gerados agora: ${d.generatedNow}.` + (d.error ? ` Parou: ${d.error}` : ""));
    } catch (e) { setCarSegMsg("Erro: " + e.message); }
    setCarSegBusy(false);
    loadCarSeg();
  }

  // ── Categorias de locuções ──
  const [annCats, setAnnCats] = useState([]);
  const [catForm, setCatForm] = useState({ name: "", icon: "🎙", is_shared: false, client_user_id: "" });
  const [editingCat, setEditingCat] = useState(null);
  const [catError, setCatError] = useState("");
  const [savingCat, setSavingCat] = useState(false);
  const [showCatForm, setShowCatForm] = useState(false);

  function loadAnnCats() {
    apiFetch("/api/announcement-categories", {}, token)
      .then(d => setAnnCats(d.categories || [])).catch(() => {});
  }

  async function saveCat() {
    if (!catForm.name) return setCatError("Nome é obrigatório");
    setSavingCat(true); setCatError("");
    try {
      if (editingCat) {
        await apiFetch(`/api/announcement-categories/${editingCat.id}`, { method: "PUT", body: JSON.stringify(catForm) }, token);
      } else {
        await apiFetch("/api/announcement-categories", { method: "POST", body: JSON.stringify(catForm) }, token);
      }
      setCatForm({ name: "", icon: "🎙", is_shared: false, client_user_id: "" }); setEditingCat(null); setShowCatForm(false);
      loadAnnCats();
    } catch (e) { setCatError(e.message); }
    setSavingCat(false);
  }

  async function removeCat(id, name) {
    if (!window.confirm(`Remover categoria "${name}"? As locuções vinculadas ficarão sem categoria.`)) return;
    try {
      await apiFetch(`/api/announcement-categories/${id}`, { method: "DELETE" }, token);
      setAnnCats(prev => prev.filter(c => c.id !== id));
    } catch { alert("Erro ao remover categoria"); }
  }

  const ICONS = ["🎙", "📢", "🏪", "ℹ️", "⚠️", "🎵", "📣", "🔔", "💬", "🎯"];

  const PLANS = ["starter", "pro", "enterprise"];

  function loadUsers() {
    apiFetch("/api/admin/users", {}, token)
      .then(d => { setUsers(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  }

  useEffect(() => { loadUsers(); loadAnnCats(); }, [token]);

  async function doBackup() {
    setBackingUp(true); setBackupResult(null);
    try {
      const API_URL = "https://radioin-backend-production.up.railway.app";
      const response = await fetch(`${API_URL}/api/admin/backup`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (!response.ok) throw new Error("Erro ao gerar backup");
      const data = await response.json();

      // Valida o backup antes de baixar
      const { meta } = data;
      if (!meta?.valid) throw new Error("Backup inválido — não baixar!");

      // Dispara o download
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `radioin-backup-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);

      setBackupResult({
        ok: true,
        tables: meta.tables.length,
        records: meta.total_records,
        date: new Date().toLocaleString("pt-BR"),
      });
    } catch (e) {
      setBackupResult({ ok: false, error: e.message });
    }
    setBackingUp(false);
  }

  async function createUser() {
    if (!form.name || !form.email || !form.password) return setError("Preencha todos os campos");
    setSaving(true); setError("");
    try {
      await apiFetch("/api/admin/users", { method: "POST", body: JSON.stringify(form) }, token);
      setForm({ name: "", email: "", password: "", plan: "starter", whatsapp: "" });
      setShowForm(false);
      loadUsers();
    } catch (e) { setError(e.message); }
    setSaving(false);
  }

  async function changePlan(id, plan) {
    try {
      await apiFetch(`/api/admin/users/${id}`, { method: "PUT", body: JSON.stringify({ plan }) }, token);
      setUsers(prev => prev.map(u => u.id === id ? { ...u, plan } : u));
    } catch { alert("Erro ao alterar plano"); }
  }

  async function toggleActive(id, is_active) {
    try {
      await apiFetch(`/api/admin/users/${id}`, { method: "PUT", body: JSON.stringify({ is_active }) }, token);
      setUsers(prev => prev.map(u => u.id === id ? { ...u, is_active } : u));
    } catch { alert("Erro ao alterar status"); }
  }

  async function removeUser(id, name) {
    if (!window.confirm(`Remover cliente "${name}"? Isso apagará todas as lojas e dados dele.`)) return;
    try {
      await apiFetch(`/api/admin/users/${id}`, { method: "DELETE" }, token);
      setUsers(prev => prev.filter(u => u.id !== id));
    } catch { alert("Erro ao remover cliente"); }
  }

  const planColor = { starter: "var(--muted)", pro: "var(--green)", enterprise: "#00d4ff" };

  // ── Modal de edição completa do cliente ───────────────────────────────────
  const [editingUser, setEditingUser] = useState(null);
  const [editUserForm, setEditUserForm] = useState({});
  const [savingUser, setSavingUser] = useState(false);
  const [editUserError, setEditUserError] = useState("");

  function openEditUser(u) {
    setEditingUser(u);
    setEditUserError("");
    setEditUserForm({
      name: u.name || "",
      email: u.email || "",
      password: "",
      plan: u.plan || "starter",
      is_active: u.is_active !== false,
      max_stores: u.max_stores || 1,
      expires_at: u.expires_at ? u.expires_at.slice(0, 10) : "",
      notes: u.notes || "",
      max_announcements: u.max_announcements || "",
      whatsapp: u.whatsapp || "",
    });
  }

  async function saveUserEdit() {
    setSavingUser(true); setEditUserError("");
    try {
      const body = {
        name: editUserForm.name,
        email: editUserForm.email,
        plan: editUserForm.plan,
        is_active: editUserForm.is_active,
        max_stores: Number(editUserForm.max_stores) || 1,
        expires_at: editUserForm.expires_at || null,
        notes: editUserForm.notes || null,
        max_announcements: editUserForm.max_announcements ? Number(editUserForm.max_announcements) : null,
        whatsapp: editUserForm.whatsapp || null,
      };
      if (editUserForm.password) body.password = editUserForm.password;
      await apiFetch(`/api/admin/users/${editingUser.id}`, { method: "PUT", body: JSON.stringify(body) }, token);
      setEditingUser(null);
      loadUsers();
    } catch (e) { setEditUserError(e.message); }
    setSavingUser(false);
  }

  return (
    <div className="fadeIn" style={{ padding: "32px", overflowY: "auto", height: "100%" }}>

      {/* ── Modal de edição do cliente ── */}
      {editingUser && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.78)", zIndex: 10000, display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={() => setEditingUser(null)}>
          <div style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 16, padding: "28px", width: 480, maxWidth: "92vw", boxShadow: "0 24px 60px rgba(0,0,0,0.6)", maxHeight: "90vh", overflowY: "auto" }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
              <div>
                <div style={{ fontSize: 12, color: "var(--green)", fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", marginBottom: 4 }}>Editar Cliente</div>
                <div style={{ fontWeight: 700, fontSize: 16 }}>{editingUser.name}</div>
              </div>
              <button onClick={() => setEditingUser(null)} style={{ background: "none", border: "none", color: "var(--muted)", fontSize: 20, cursor: "pointer" }}>✕</button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <div>
                  <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 4 }}>Nome</div>
                  <input value={editUserForm.name} onChange={e => setEditUserForm(f => ({ ...f, name: e.target.value }))} style={{ width: "100%" }} />
                </div>
                <div>
                  <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 4 }}>Email</div>
                  <input type="email" value={editUserForm.email} onChange={e => setEditUserForm(f => ({ ...f, email: e.target.value }))} style={{ width: "100%" }} />
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 4 }}>Nova Senha <span style={{ fontStyle: "italic" }}>(deixe em branco para não alterar)</span></div>
                <input type="password" placeholder="••••••••" value={editUserForm.password} onChange={e => setEditUserForm(f => ({ ...f, password: e.target.value }))} style={{ width: "100%" }} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <div>
                  <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 4 }}>Plano</div>
                  <select value={editUserForm.plan} onChange={e => setEditUserForm(f => ({ ...f, plan: e.target.value }))} style={{ width: "100%" }}>
                    {PLANS.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
                  </select>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 4 }}>Status</div>
                  <select value={editUserForm.is_active ? "true" : "false"} onChange={e => setEditUserForm(f => ({ ...f, is_active: e.target.value === "true" }))} style={{ width: "100%" }}>
                    <option value="true">✅ Ativo</option>
                    <option value="false">🔴 Suspenso</option>
                  </select>
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <div>
                  <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 4 }}>Limite de Lojas</div>
                  <input type="number" min="1" max="99" value={editUserForm.max_stores} onChange={e => setEditUserForm(f => ({ ...f, max_stores: e.target.value }))} style={{ width: "100%" }} />
                </div>
                <div>
                  <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 4 }}>Limite de Locuções/mês <span style={{ fontStyle: "italic" }}>(vazio = usa plano)</span></div>
                  <input type="number" min="0" placeholder="Ex: 150" value={editUserForm.max_announcements} onChange={e => setEditUserForm(f => ({ ...f, max_announcements: e.target.value }))} style={{ width: "100%" }} />
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 4 }}>Data de Vencimento <span style={{ fontStyle: "italic" }}>(só referência visual)</span></div>
                <input type="date" value={editUserForm.expires_at} onChange={e => setEditUserForm(f => ({ ...f, expires_at: e.target.value }))} style={{ width: "100%" }} />
              </div>
              <div>
                <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 4 }}>Observações internas</div>
                <textarea value={editUserForm.notes} onChange={e => setEditUserForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="Ex: pagou via PIX, contato: (43)..." rows={3}
                  style={{ width: "100%", resize: "vertical" }} />
              </div>
              <div>
                <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 4 }}>WhatsApp</div>
                <input placeholder="Ex: (43) 99999-9999" value={editUserForm.whatsapp} onChange={e => setEditUserForm(f => ({ ...f, whatsapp: e.target.value }))} style={{ width: "100%" }} />
              </div>
            </div>
            {editUserError && <div style={{ marginTop: 12, fontSize: 13, color: "var(--red)", padding: "8px 12px", background: "#ff446615", borderRadius: 8 }}>{editUserError}</div>}
            <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
              <button onClick={saveUserEdit} disabled={savingUser}
                style={{ flex: 1, padding: "12px", background: savingUser ? "var(--bg3)" : "var(--green)", color: savingUser ? "var(--muted)" : "#080d08", border: "none", borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: savingUser ? "not-allowed" : "pointer" }}>
                {savingUser ? "Salvando..." : "Salvar Alterações"}
              </button>
              <button onClick={() => setEditingUser(null)}
                style={{ padding: "12px 20px", background: "var(--bg3)", color: "var(--muted)", border: "1px solid var(--border)", borderRadius: 10, fontWeight: 600, fontSize: 14, cursor: "pointer" }}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 28 }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 800, marginBottom: 4 }}>Clientes</h1>
          <p style={{ color: "var(--muted)", fontSize: 14 }}>{users.length} clientes cadastrados</p>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={doBackup} disabled={backingUp} style={{
            padding: "10px 18px", background: "var(--bg2)", color: backingUp ? "var(--muted)" : "var(--text)",
            border: "1px solid var(--border)", borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: backingUp ? "not-allowed" : "pointer"
          }}>
            {backingUp ? "⏳ Gerando..." : "📦 Backup do Banco"}
          </button>
          <button onClick={() => setShowForm(!showForm)} style={{ padding: "10px 20px", background: "var(--green)", color: "#080d08", border: "none", borderRadius: 10, fontWeight: 700, fontSize: 13 }}>
            + Novo Cliente
          </button>
        </div>
      </div>

      {/* Resultado do backup */}
      {backupResult && (
        <div className="fadeIn" style={{ marginBottom: 20, padding: "12px 16px", borderRadius: 10, background: backupResult.ok ? "var(--green3)" : "#ff446615", border: `1px solid ${backupResult.ok ? "#00ff7f30" : "#ff446640"}` }}>
          {backupResult.ok ? (
            <div style={{ fontSize: 13, color: "var(--green)", display: "flex", alignItems: "center", gap: 8 }}>
              <span>✅</span>
              <span>Backup válido gerado em <strong>{backupResult.date}</strong> — <strong>{backupResult.tables} tabelas</strong> · <strong>{backupResult.records} registros</strong>. Salve o arquivo no Google Drive!</span>
            </div>
          ) : (
            <div style={{ fontSize: 13, color: "var(--red)" }}>❌ Erro: {backupResult.error}</div>
          )}
        </div>
      )}

      {/* ── Aviso de manutenção no remoto ── */}
      {rNotice && (
        <div style={{ marginBottom: 20, padding: "14px 18px", borderRadius: 12, background: "var(--bg2)", border: `1px solid ${rNotice.tts || rNotice.car ? "#ffbb3360" : "var(--border)"}` }}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8 }}>🛠 Aviso de manutenção no remoto {(rNotice.tts || rNotice.car) && <span style={{ fontSize: 11, color: "#ffbb33" }}>● LIGADO</span>}</div>
          <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
            {[["tts", "🎙 Aba TTS"], ["car", "🚗 Aba Veículo"]].map(([k, label]) => (
              <label key={k} style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 13, cursor: "pointer" }}>
                <input type="checkbox" checked={!!rNotice[k]} onChange={e => saveRNotice({ ...rNotice, [k]: e.target.checked })} /> {label}
              </label>
            ))}
            <input value={rNotice.message || ""} onChange={e => setRNotice(n => ({ ...n, message: e.target.value }))}
              onBlur={() => saveRNotice(rNotice)} maxLength={200}
              style={{ flex: 1, minWidth: 260, fontSize: 13 }} placeholder="Mensagem que aparece no remoto" />
          </div>
          <div style={{ fontSize: 11, color: rNoticeMsg.startsWith("✗") ? "var(--red)" : "var(--muted)", marginTop: 6 }}>
            {rNoticeMsg || "Marcada, a aba mostra a mensagem e o botão de enviar fica desativado. As outras abas continuam normais."}
          </div>
        </div>
      )}

      {/* ── Áudios do anúncio de veículos (remoto → aba 🚗 Veículo) ── */}
      {carSeg && (
        <div style={{ marginBottom: 20, padding: "14px 18px", borderRadius: 12, background: "var(--bg2)", border: "1px solid var(--border)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 220 }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>🚗 Áudios do anúncio de veículos: {carSeg.ready} de {carSeg.total} prontos</div>
              <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 3 }}>
                {carSeg.ready < carSeg.total
                  ? "Gerados uma vez só por IA (30 por clique; cada clique leva cerca de 4 minutos)."
                  : "Todos prontos. Use \"Revisar\" para ouvir e refazer algum que ficou com pronúncia errada."}
                {" "}Cada geração conta no limite diário do Google (100/dia, somando as locuções dos clientes).
              </div>
              {carSegMsg && <div style={{ fontSize: 12, marginTop: 6, color: carSegMsg.startsWith("Erro") || carSegMsg.includes("Parou") ? "var(--red)" : "var(--green)" }}>{carSegMsg}</div>}
            </div>
            {carSeg.ready < carSeg.total && (
              <button onClick={generateCarSeg} disabled={carSegBusy} style={{ padding: "9px 16px", background: carSegBusy ? "var(--bg3)" : "var(--green3)", border: "1px solid #00ff7f30", borderRadius: 8, color: carSegBusy ? "var(--muted)" : "var(--green)", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                {carSegBusy ? "⏳ Gerando (cerca de 4 min)..." : "Gerar próximos 30"}
              </button>
            )}
            <button onClick={() => setCarSegReview(v => !v)} style={{ padding: "9px 14px", background: "var(--bg3)", border: "1px solid var(--border)", borderRadius: 8, color: "var(--text)", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
              {carSegReview ? "Fechar revisão" : "🔎 Revisar áudios"}
            </button>
          </div>
          {carSegReview && carSeg.items && (
            <div style={{ marginTop: 14 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
                <label style={{ padding: "7px 14px", background: "#3b82f620", border: "1px solid #3b82f640", borderRadius: 8, color: "#60a5fa", fontWeight: 800, fontSize: 12, cursor: carSegBusy ? "default" : "pointer" }}>
                  {carSegBusy ? "⏳ Enviando..." : "⬆ Enviar gravações do locutor"}
                  <input type="file" multiple accept="audio/*,.mp3,.wav,.m4a" disabled={carSegBusy} style={{ display: "none" }}
                    onChange={e => { uploadCarSeg(e.target.files); e.target.value = ""; }} />
                </label>
                <span style={{ fontSize: 12, color: "var(--muted)" }}>O nome de cada arquivo deve ser o código da linha (ex.: <b>marca_fiat.mp3</b>). Ou refaça por IA:</span>
                <button onClick={regenCarSeg} disabled={carSegBusy || !carSegSel.length} style={{ padding: "7px 14px", background: carSegSel.length ? "var(--green)" : "var(--bg3)", border: "none", borderRadius: 8, color: carSegSel.length ? "#080d08" : "var(--muted)", fontWeight: 800, fontSize: 12, cursor: carSegSel.length ? "pointer" : "default" }}>
                  {carSegBusy ? "⏳ Refazendo..." : `🔄 Refazer marcados (${carSegSel.length})`}
                </button>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 6, maxHeight: 420, overflowY: "auto" }}>
                {carSeg.items.map(it => (
                  <div key={it.key} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", background: "var(--bg3)", border: `1px solid ${carSegSel.includes(it.key) ? "#ffd93d60" : "var(--border)"}`, borderRadius: 8, fontSize: 12 }}>
                    <input type="checkbox" checked={carSegSel.includes(it.key)} disabled={!it.ready}
                      onChange={e => setCarSegSel(sel => e.target.checked ? [...sel, it.key].slice(0, 30) : sel.filter(k => k !== it.key))} />
                    <span style={{ width: 110, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={`${it.text} — arquivo: ${it.key}.mp3`}>
                      {it.text}<br /><span style={{ fontSize: 10, color: "var(--muted)", fontFamily: "var(--mono)" }}>{it.key}</span>
                    </span>
                    {it.ready
                      ? <audio controls preload="none" src={`${API}/api/remote/car-seg/${it.key}.mp3?v=${carSegVer}`} style={{ height: 26, flex: 1, minWidth: 0 }} />
                      : <span style={{ color: "var(--yellow)" }}>ainda não gerado</span>}
                    <span title="Enviar gravação só para esta linha" style={{ flexShrink: 0 }}>
                      <label style={{ cursor: "pointer", fontSize: 15, padding: "0 4px" }}>⬆
                        <input type="file" accept="audio/*,.mp3,.wav,.m4a" style={{ display: "none" }}
                          onChange={e => { uploadCarSeg(e.target.files, it.key); e.target.value = ""; }} />
                      </label>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Modelos de veículos ── */}
      <div style={{ marginBottom: 20, padding: "14px 18px", borderRadius: 12, background: "var(--bg2)", border: "1px solid var(--border)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ fontWeight: 700, fontSize: 14 }}>🚘 Modelos de veículos
              {carModels && <span style={{ fontWeight: 400, fontSize: 12, color: "var(--muted)" }}> — {carModels.models.filter(m => m.ready).length} de {carModels.models.length} com gravação</span>}
            </div>
            <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 3 }}>Aparecem no remoto (aba Veículo) depois da marca, só os que já têm gravação do locutor.</div>
          </div>
          <button onClick={() => setCarModelsOpen(v => !v)} style={{ padding: "9px 14px", background: "var(--bg3)", border: "1px solid var(--border)", borderRadius: 8, color: "var(--text)", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
            {carModelsOpen ? "Fechar" : "Gerenciar modelos"}
          </button>
        </div>
        {carModelsOpen && carModels && (
          <div style={{ marginTop: 14 }}>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 10 }}>
              <select value={newModel.brand} onChange={e => setNewModel(m => ({ ...m, brand: e.target.value }))} style={{ fontSize: 13 }}>
                {carModels.brands.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
              </select>
              <input value={newModel.name} onChange={e => setNewModel(m => ({ ...m, name: e.target.value }))} onKeyDown={e => e.key === "Enter" && addCarModel()}
                placeholder="Nome do modelo (ex.: Gol)" maxLength={60} style={{ fontSize: 13, width: 200 }} />
              <button onClick={addCarModel} style={{ padding: "7px 14px", background: "var(--green)", border: "none", borderRadius: 8, color: "#080d08", fontWeight: 800, fontSize: 12, cursor: "pointer" }}>+ Adicionar</button>
              <label style={{ padding: "7px 14px", background: "#3b82f620", border: "1px solid #3b82f640", borderRadius: 8, color: "#60a5fa", fontWeight: 800, fontSize: 12, cursor: "pointer" }}>
                {carSegBusy ? "⏳ Enviando..." : "⬆ Enviar várias gravações"}
                <input type="file" multiple accept="audio/*,.mp3,.wav,.m4a" disabled={carSegBusy} style={{ display: "none" }}
                  onChange={e => { uploadCarSeg(e.target.files); e.target.value = ""; }} />
              </label>
            </div>
            <div style={{ fontSize: 11, color: carModelsMsg.startsWith("✗") ? "var(--red)" : "var(--muted)", marginBottom: 10 }}>
              {carModelsMsg || <>Em lote, o nome de cada arquivo deve ser o código do modelo (ex.: <b>modelo_volkswagen_gol.mp3</b>). Por linha, use o ⬆ (qualquer nome). {carSegMsg && <span style={{ color: carSegMsg.includes("Parou") || carSegMsg.startsWith("Erro") ? "var(--red)" : "var(--green)" }}> {carSegMsg}</span>}</>}
            </div>
            <div style={{ maxHeight: 420, overflowY: "auto" }}>
              {carModels.brands.filter(b => carModels.models.some(m => m.brand_key === b.value)).map(b => (
                <div key={b.value} style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 11, color: "var(--muted)", letterSpacing: 1, marginBottom: 4 }}>{b.label.toUpperCase()}</div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 6 }}>
                    {carModels.models.filter(m => m.brand_key === b.value).map(m => (
                      <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", background: "var(--bg3)", border: `1px solid ${m.ready ? "var(--border)" : "#ffd93d40"}`, borderRadius: 8, fontSize: 12 }}>
                        <span style={{ width: 110, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={m.audio_key}>
                          {m.name}<br /><span style={{ fontSize: 10, color: "var(--muted)", fontFamily: "var(--mono)" }}>{m.audio_key}</span>
                        </span>
                        {m.ready
                          ? <audio controls preload="none" src={`${API}/api/remote/car-seg/${m.audio_key}.mp3?v=${carSegVer}`} style={{ height: 26, flex: 1, minWidth: 0 }} />
                          : <span style={{ flex: 1, color: "var(--yellow)" }}>sem gravação</span>}
                        <label title="Enviar gravação deste modelo" style={{ cursor: "pointer", fontSize: 15, padding: "0 4px" }}>⬆
                          <input type="file" accept="audio/*,.mp3,.wav,.m4a" style={{ display: "none" }}
                            onChange={e => { uploadCarSeg(e.target.files, m.audio_key); e.target.value = ""; }} />
                        </label>
                        <button onClick={() => delCarModel(m)} title="Remover modelo" style={{ background: "none", border: "none", color: "var(--muted)", cursor: "pointer", fontSize: 14 }}>🗑</button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── Monitoramento de Players ── */}
      <div style={{ marginBottom: 32, background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 14, padding: "20px 24px" }}>
        <PlayerMonitor token={token} isMaster={true} />
      </div>

      {showForm && (
        <div style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 12, padding: 20, marginBottom: 24 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 16 }}>Criar Cliente Manualmente</h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
            <input placeholder="Nome *" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            <input placeholder="Email *" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
            <input placeholder="Senha *" type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} />
            <select value={form.plan} onChange={e => setForm(f => ({ ...f, plan: e.target.value }))}>
              {PLANS.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
            </select>
            <input placeholder="WhatsApp (opcional)" value={form.whatsapp} onChange={e => setForm(f => ({ ...f, whatsapp: e.target.value }))} style={{ gridColumn: "span 2" }} />
          </div>
          {error && <div style={{ fontSize: 12, color: "var(--red)", marginBottom: 10 }}>{error}</div>}
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={createUser} disabled={saving} style={{ padding: "8px 20px", background: "var(--green)", color: "#080d08", border: "none", borderRadius: 8, fontWeight: 700 }}>
              {saving ? "Criando..." : "Criar"}
            </button>
            <button onClick={() => { setShowForm(false); setError(""); }} style={{ padding: "8px 16px", background: "var(--bg3)", color: "var(--muted)", border: "1px solid var(--border)", borderRadius: 8 }}>
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* ── Categorias de Locuções ── */}
      <div style={{ marginBottom: 32, background: "var(--bg2)", border: "1px solid #ffd93d30", borderRadius: 12, padding: "20px 24px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <div>
            <h2 style={{ fontSize: 16, fontWeight: 700, color: "var(--yellow)", marginBottom: 2 }}>🎙 Categorias de Locuções</h2>
            <p style={{ fontSize: 12, color: "var(--muted)" }}>Cada categoria vira um bloco independente na grade do player</p>
          </div>
          <button onClick={() => { setShowCatForm(!showCatForm); setEditingCat(null); setCatForm({ name: "", icon: "🎙" }); setCatError(""); }}
            style={{ padding: "7px 16px", background: showCatForm ? "var(--bg3)" : "#ffd93d20", color: "var(--yellow)", border: "1px solid #ffd93d40", borderRadius: 8, fontWeight: 700, fontSize: 12, cursor: "pointer" }}>
            {showCatForm ? "Cancelar" : "+ Nova Categoria"}
          </button>
        </div>

        {showCatForm && (
          <div className="fadeIn" style={{ background: "var(--bg3)", border: "1px solid #ffd93d30", borderRadius: 10, padding: "16px", marginBottom: 16 }}>
            <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <label style={{ fontSize: 11, color: "var(--muted)", fontWeight: 600 }}>ÍCONE</label>
                <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                  {ICONS.map(ic => (
                    <button key={ic} onClick={() => setCatForm(f => ({ ...f, icon: ic }))}
                      style={{ width: 32, height: 32, fontSize: 16, background: catForm.icon === ic ? "#ffd93d30" : "var(--bg2)", border: `1px solid ${catForm.icon === ic ? "#ffd93d60" : "var(--border)"}`, borderRadius: 6, cursor: "pointer" }}>
                      {ic}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1, minWidth: 160 }}>
                <label style={{ fontSize: 11, color: "var(--muted)", fontWeight: 600 }}>NOME</label>
                <input placeholder="Ex: Chamadas, Comerciais..." value={catForm.name}
                  onChange={e => setCatForm(f => ({ ...f, name: e.target.value }))}
                  style={{ padding: "8px 12px" }} />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <label style={{ fontSize: 11, color: "var(--muted)", fontWeight: 600 }}>TIPO</label>
                <button onClick={() => setCatForm(f => ({ ...f, is_shared: !f.is_shared }))}
                  style={{ padding: "8px 12px", background: catForm.is_shared ? "#3b82f620" : "var(--bg2)", border: `1px solid ${catForm.is_shared ? "#3b82f660" : "var(--border)"}`, borderRadius: 8, color: catForm.is_shared ? "#60a5fa" : "var(--muted)", fontWeight: 700, fontSize: 12, cursor: "pointer", height: 38 }}>
                  {catForm.is_shared ? "🔗 Compartilhada" : "🔒 Normal"}
                </button>
              </div>
              <button onClick={saveCat} disabled={savingCat}
                style={{ padding: "8px 20px", background: "var(--yellow)", color: "#080d08", border: "none", borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: "pointer", height: 38 }}>
                {savingCat ? "..." : editingCat ? "Salvar" : "Criar"}
              </button>
            </div>
            {catForm.is_shared && <div style={{ fontSize: 11, color: "#60a5fa", marginTop: 8 }}>🔗 Locuções desta categoria serão visíveis em todas as lojas do cliente automaticamente.</div>}
            {catForm.is_shared && (
              <div style={{ marginTop: 10 }}>
                <label style={{ fontSize: 11, color: "var(--muted)", fontWeight: 600, display: "block", marginBottom: 4 }}>CLIENTE</label>
                <select value={catForm.client_user_id} onChange={e => setCatForm(f => ({ ...f, client_user_id: e.target.value }))}
                  style={{ width: "100%", padding: "8px 12px" }}>
                  <option value="">Selecione o cliente...</option>
                  {users.filter(u => u.plan !== "master").map(u => (
                    <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                  ))}
                </select>
              </div>
            )}
            {catError && <div style={{ fontSize: 12, color: "var(--red)", marginTop: 8 }}>{catError}</div>}
          </div>
        )}

        {annCats.length === 0 ? (
          <p style={{ fontSize: 13, color: "var(--muted)", textAlign: "center", padding: "16px 0" }}>
            Nenhuma categoria criada. Crie categorias para organizar as locuções na grade.
          </p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {annCats.map(c => (
              <div key={c.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: "var(--bg3)", borderRadius: 8, border: `1px solid ${c.is_shared ? "#3b82f630" : "var(--border)"}` }}>
                <span style={{ fontSize: 18 }}>{c.icon}</span>
                <span style={{ flex: 1, fontWeight: 600, fontSize: 14 }}>{c.name}</span>
                {c.is_shared && (
                  <span style={{ fontSize: 10, padding: "1px 7px", background: "#3b82f620", border: "1px solid #3b82f640", borderRadius: 10, color: "#60a5fa", fontWeight: 700 }}>
                    🔗 {c.user_id ? (users.find(u => u.id === c.user_id)?.name || "Cliente") : "Todos"}
                  </span>
                )}
                <button onClick={() => { setEditingCat(c); setCatForm({ name: c.name, icon: c.icon, is_shared: c.is_shared || false, client_user_id: c.user_id || "" }); setShowCatForm(true); setCatError(""); }}
                  style={{ background: "none", border: "1px solid var(--border)", borderRadius: 6, color: "var(--muted)", fontSize: 13, width: 28, height: 28, cursor: "pointer" }}
                  onMouseEnter={e => { e.currentTarget.style.background = "var(--bg2)"; e.currentTarget.style.color = "var(--green)"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "none"; e.currentTarget.style.color = "var(--muted)"; }}>✏️</button>
                <button onClick={() => removeCat(c.id, c.name)}
                  style={{ background: "none", border: "1px solid #ff446640", borderRadius: 6, color: "var(--red)", fontSize: 13, width: 28, height: 28, cursor: "pointer" }}
                  onMouseEnter={e => e.currentTarget.style.background = "#ff446620"}
                  onMouseLeave={e => e.currentTarget.style.background = "none"}>✕</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 40, color: "var(--muted)" }}>Carregando...</div>
      ) : users.length === 0 ? (
        <div style={{ textAlign: "center", padding: 60, color: "var(--muted)" }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>⊕</div>
          <p>Nenhum cliente cadastrado ainda.</p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {users.map(u => (
            <div key={u.id} style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 12, padding: "16px 20px", display: "flex", alignItems: "center", gap: 16, opacity: u.is_active === false ? 0.5 : 1 }}>
              <div style={{ width: 40, height: 40, borderRadius: "50%", background: "var(--bg3)", border: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 16, color: "var(--green)", flexShrink: 0 }}>
                {u.name?.charAt(0).toUpperCase()}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 14 }}>{u.name}</div>
                <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 2 }}>
                  {u.email} · {u.store_count || 0}/{u.max_stores || 1} lojas
                  {u.expires_at && (
                    <span style={{ marginLeft: 8, color: new Date(u.expires_at) < new Date() ? "var(--red)" : "var(--yellow)" }}>
                      · vence {new Date(u.expires_at).toLocaleDateString("pt-BR")}
                    </span>
                  )}
                </div>
                <div style={{ display: "flex", gap: 8, marginTop: 3, flexWrap: "wrap" }}>
                  {u.whatsapp && (
                    <a href={`https://wa.me/55${u.whatsapp.replace(/\D/g,"")}`} target="_blank" rel="noopener noreferrer"
                      style={{ fontSize: 11, color: "#25d366", textDecoration: "none", display: "flex", alignItems: "center", gap: 3 }}>
                      💬 {u.whatsapp}
                    </a>
                  )}
                  {u.notes && <span style={{ fontSize: 11, color: "var(--muted)", fontStyle: "italic", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 200 }}>📝 {u.notes}</span>}
                </div>
              </div>
              <select value={u.plan} onChange={e => changePlan(u.id, e.target.value)}
                style={{ fontSize: 12, padding: "4px 8px", color: planColor[u.plan] || "var(--muted)", fontWeight: 700, minWidth: 100 }}>
                {PLANS.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
              </select>
              <button onClick={() => enterAsClient(u)} disabled={impersonating === u.id}
                title="Entrar no painel deste cliente sem sair do Master"
                style={{ padding: "4px 10px", background: "var(--green3)", border: "1px solid #00ff7f30", borderRadius: 6, color: "var(--green)", fontSize: 11, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>
                {impersonating === u.id ? "..." : "🔑 Entrar"}
              </button>
              <button onClick={() => openEditUser(u)}
                style={{ padding: "4px 10px", background: "var(--bg3)", border: "1px solid var(--border)", borderRadius: 6, color: "var(--muted)", fontSize: 13, cursor: "pointer" }}
                onMouseEnter={e => e.currentTarget.style.borderColor = "var(--green)"}
                onMouseLeave={e => e.currentTarget.style.borderColor = "var(--border)"}>✏️</button>
              <button onClick={() => toggleActive(u.id, u.is_active === false ? true : false)}
                style={{ fontSize: 11, padding: "4px 10px", background: u.is_active === false ? "var(--green3)" : "var(--bg3)", color: u.is_active === false ? "var(--green)" : "var(--muted)", border: `1px solid ${u.is_active === false ? "#00ff7f30" : "var(--border)"}`, borderRadius: 6, fontWeight: 600 }}>
                {u.is_active === false ? "Ativar" : "Suspender"}
              </button>
              <button onClick={() => removeUser(u.id, u.name)}
                style={{ background: "none", border: "1px solid #ff446640", borderRadius: 6, color: "var(--red)", fontSize: 13, width: 28, height: 28, cursor: "pointer", flexShrink: 0 }}
                onMouseEnter={e => e.currentTarget.style.background = "#ff446620"}
                onMouseLeave={e => e.currentTarget.style.background = "none"}>✕</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── PLAYER GRADE ──────────────────────────────────────────────────────────────
function PlayerGrade({ token }) {
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState("");
  const [albums, setAlbums] = useState([]);
  const [categories, setCategories] = useState([]);
  const [annCats, setAnnCats] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [manualPlaylists, setManualPlaylists] = useState([]);
  const [aiAlbums, setAiAlbums] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [sharedAnns, setSharedAnns] = useState([]); // locuções de categorias compartilhadas em outras lojas
  const [blocks, setBlocks] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [openSections, setOpenSections] = useState({ manualPlaylists: true, announcements: true, masterLib: true });
  const [masterAlbums, setMasterAlbums] = useState([]);
  const [masterPlaylists, setMasterPlaylists] = useState([]);

  // ── Múltiplas grades ──────────────────────────────────────────
  const [schedules, setSchedules] = useState([]);
  const [activeScheduleId, setActiveScheduleId] = useState(null);
  const [editingScheduleId, setEditingScheduleId] = useState(null); // qual grade está sendo editada
  const [newScheduleName, setNewScheduleName] = useState("");
  const [showNewSchedule, setShowNewSchedule] = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const [renamingValue, setRenamingValue] = useState("");

  useEffect(() => {
    apiFetch("/api/stores", {}, token).then(d => {
      const list = Array.isArray(d) ? d : [];
      setStores(list);
      if (list.length > 0) setSelectedStore(list[0].id);
    }).catch(() => {});
    apiFetch("/api/master-library", {}, token).then(d => { setMasterAlbums(d.albums || []); setMasterPlaylists(d.playlists || []); }).catch(() => {});
    apiFetch("/api/albums", {}, token).then(d => {
      const all = Array.isArray(d) ? d : [];
      setAlbums(all.filter(a => !a.is_playlist));
      setAiAlbums(all.filter(a => a.is_playlist));
    }).catch(() => {});
    apiFetch("/api/categories", {}, token).then(d => setCategories(Array.isArray(d) ? d : [])).catch(() => {});
    apiFetch("/api/announcement-categories", {}, token).then(d => setAnnCats(d.categories || [])).catch(() => {});
  }, [token]);

  const loadSchedules = useCallback(() => {
    if (!selectedStore) return;
    apiFetch(`/api/stores/${selectedStore}/player-schedule`, {}, token).then(d => {
      const list = Array.isArray(d) ? d : [];
      setSchedules(list);
      const active = list.find(s => s.is_active);
      if (active) {
        setActiveScheduleId(active.id);
        // Se não está editando outra, carrega a ativa
        if (!editingScheduleId) {
          setEditingScheduleId(active.id);
          setBlocks(active.blocks || []);
        }
      } else if (list.length > 0) {
        setEditingScheduleId(list[0].id);
        setBlocks(list[0].blocks || []);
      }
    }).catch(() => {});
  }, [selectedStore, token]);

  useEffect(() => {
    if (!selectedStore) return;
    apiFetch(`/api/stores/${selectedStore}/announcements?player=1`, {}, token)
      .then(d => { const list = Array.isArray(d) ? d : (d.announcements || []); setAnnouncements(list.filter(a => a.status === "ready")); setSharedAnns(Array.isArray(d?.shared_announcements) ? d.shared_announcements : []); })
      .catch(() => {});
    apiFetch(`/api/stores/${selectedStore}/playlists`, {}, token)
      .then(d => {
        const all = Array.isArray(d) ? d : [];
        setPlaylists(all.filter(p => p.created_by_ai));
        setManualPlaylists(all.filter(p => !p.created_by_ai));
      }).catch(() => {});
    loadSchedules();
  }, [selectedStore, token]);

  function selectScheduleToEdit(s) {
    setEditingScheduleId(s.id);
    setBlocks(s.blocks || []);
  }

  async function activateSchedule(scheduleId) {
    try {
      await apiFetch(`/api/stores/${selectedStore}/player-schedule/${scheduleId}/activate`, { method: "PUT" }, token);
      setActiveScheduleId(scheduleId);
      setSchedules(prev => prev.map(s => ({ ...s, is_active: s.id === scheduleId })));
    } catch { alert("Erro ao ativar grade"); }
  }

  async function createSchedule() {
    if (!newScheduleName.trim()) return;
    try {
      await apiFetch(`/api/stores/${selectedStore}/player-schedule`, { method: "POST", body: JSON.stringify({ blocks: [], name: newScheduleName.trim() }) }, token);
      setNewScheduleName(""); setShowNewSchedule(false);
      loadSchedules();
    } catch { alert("Erro ao criar grade"); }
  }

  async function renameSchedule(id) {
    if (!renamingValue.trim()) return;
    try {
      const s = schedules.find(s => s.id === id);
      await apiFetch(`/api/stores/${selectedStore}/player-schedule`, { method: "POST", body: JSON.stringify({ blocks: s.blocks || [], name: renamingValue.trim(), scheduleId: id }) }, token);
      setRenamingId(null);
      loadSchedules();
    } catch { alert("Erro ao renomear grade"); }
  }

  async function deleteSchedule(id) {
    if (!window.confirm("Apagar esta grade?")) return;
    try {
      await apiFetch(`/api/stores/${selectedStore}/player-schedule/${id}`, { method: "DELETE" }, token);
      loadSchedules();
    } catch { alert("Erro ao apagar grade"); }
  }

  function addBlock(type, id, name, qty = 1) {
    setBlocks(prev => [...prev, {
      type,
      ...(type === "album" ? { album_id: id } : { announcement_id: id }),
      name, qty,
      valid_from: "", valid_until: "",
    }]);
  }

  function updateValidity(i, field, value) {
    setBlocks(prev => prev.map((b, idx) => idx === i ? { ...b, [field]: value } : b));
  }

  function removeBlock(i) {
    setBlocks(prev => prev.filter((_, idx) => idx !== i));
  }

  function moveBlock(i, dir) {
    setBlocks(prev => {
      const arr = [...prev];
      const j = i + dir;
      if (j < 0 || j >= arr.length) return arr;
      [arr[i], arr[j]] = [arr[j], arr[i]];
      return arr;
    });
  }

  function updateQty(i, qty) {
    setBlocks(prev => prev.map((b, idx) => idx === i ? { ...b, qty: Math.max(1, parseInt(qty) || 1) } : b));
  }

  async function save() {
    if (!selectedStore || !editingScheduleId) return;
    setSaving(true);
    try {
      const s = schedules.find(s => s.id === editingScheduleId);
      await apiFetch(`/api/stores/${selectedStore}/player-schedule`, {
        method: "POST",
        body: JSON.stringify({ blocks, name: s?.name || "Principal", scheduleId: editingScheduleId })
      }, token);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      loadSchedules();
    } catch { alert("Erro ao salvar grade"); }
    setSaving(false);
  }

  const playerUrl = selectedStore ? `${window.location.origin}/player?store=${selectedStore}` : null;

  return (
    <div className="fadeIn" style={{ padding: "32px", overflowY: "auto", height: "100%" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 800, marginBottom: 4 }}>Grade do Player</h1>
          <p style={{ color: "var(--muted)", fontSize: 14 }}>Monte as sequências de músicas e locuções</p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {stores.length > 1 && (
            <select value={selectedStore} onChange={e => {
              setSelectedStore(e.target.value);
              setEditingScheduleId(null);
              setBlocks([]);
              setSchedules([]);
            }} style={{ padding: "8px 12px", fontSize: 14, minWidth: 160 }}>
              {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          )}
          {selectedStore && (
            <a href={`${window.location.origin}/player?store=${selectedStore}`} target="_blank" rel="noreferrer"
              style={{ padding: "10px 18px", background: "var(--bg3)", color: "var(--green)", border: "1px solid var(--green)", borderRadius: 10, fontWeight: 700, fontSize: 13, textDecoration: "none" }}>
              ▶ Abrir Player
            </a>
          )}
        </div>
      </div>

      {/* ── Abas de grades ── */}
      {selectedStore && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            {schedules.map(s => (
              <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 0 }}>
                {renamingId === s.id ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <input value={renamingValue} onChange={e => setRenamingValue(e.target.value)}
                      onKeyDown={e => { if (e.key === "Enter") renameSchedule(s.id); if (e.key === "Escape") setRenamingId(null); }}
                      style={{ padding: "6px 10px", fontSize: 13, width: 120 }} autoFocus />
                    <button onClick={() => renameSchedule(s.id)}
                      style={{ padding: "6px 10px", background: "var(--green)", color: "#080d08", border: "none", borderRadius: 6, fontWeight: 700, fontSize: 12, cursor: "pointer" }}>✓</button>
                    <button onClick={() => setRenamingId(null)}
                      style={{ padding: "6px 8px", background: "var(--bg3)", color: "var(--muted)", border: "1px solid var(--border)", borderRadius: 6, fontSize: 12, cursor: "pointer" }}>✕</button>
                  </div>
                ) : (
                  <div style={{ display: "flex", alignItems: "center", gap: 0, background: editingScheduleId === s.id ? "var(--bg2)" : "var(--bg3)", border: `1px solid ${editingScheduleId === s.id ? "var(--green)" : "var(--border)"}`, borderRadius: 8, overflow: "hidden" }}>
                    <button onClick={() => selectScheduleToEdit(s)}
                      style={{ padding: "8px 14px", background: "none", border: "none", color: editingScheduleId === s.id ? "var(--green)" : "var(--muted)", fontWeight: editingScheduleId === s.id ? 700 : 500, fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
                      {s.is_active && <span style={{ width: 7, height: 7, borderRadius: "50%", background: "var(--green)", boxShadow: "0 0 6px var(--green)", display: "inline-block", animation: "pulse 2s infinite" }} />}
                      {s.name}
                    </button>
                    {!s.is_active && (
                      <button onClick={() => activateSchedule(s.id)} title="Ativar esta grade"
                        style={{ padding: "8px 8px", background: "none", border: "none", borderLeft: "1px solid var(--border)", color: "var(--muted)", fontSize: 11, cursor: "pointer" }}
                        onMouseEnter={e => e.currentTarget.style.color = "var(--green)"}
                        onMouseLeave={e => e.currentTarget.style.color = "var(--muted)"}>▶</button>
                    )}
                    <button onClick={() => { setRenamingId(s.id); setRenamingValue(s.name); }} title="Renomear"
                      style={{ padding: "8px 6px", background: "none", border: "none", borderLeft: "1px solid var(--border)", color: "var(--muted)", fontSize: 11, cursor: "pointer" }}
                      onMouseEnter={e => e.currentTarget.style.color = "var(--green)"}
                      onMouseLeave={e => e.currentTarget.style.color = "var(--muted)"}>✏️</button>
                    {schedules.length > 1 && (
                      <button onClick={() => deleteSchedule(s.id)} title="Apagar grade"
                        style={{ padding: "8px 6px", background: "none", border: "none", borderLeft: "1px solid var(--border)", color: "var(--muted)", fontSize: 11, cursor: "pointer" }}
                        onMouseEnter={e => e.currentTarget.style.color = "var(--red)"}
                        onMouseLeave={e => e.currentTarget.style.color = "var(--muted)"}>✕</button>
                    )}
                  </div>
                )}
              </div>
            ))}

            {/* Botão nova grade */}
            {showNewSchedule ? (
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <input placeholder="Nome da grade..." value={newScheduleName} onChange={e => setNewScheduleName(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") createSchedule(); if (e.key === "Escape") { setShowNewSchedule(false); setNewScheduleName(""); } }}
                  style={{ padding: "6px 10px", fontSize: 13, width: 140 }} autoFocus />
                <button onClick={createSchedule}
                  style={{ padding: "6px 12px", background: "var(--green)", color: "#080d08", border: "none", borderRadius: 6, fontWeight: 700, fontSize: 12, cursor: "pointer" }}>Criar</button>
                <button onClick={() => { setShowNewSchedule(false); setNewScheduleName(""); }}
                  style={{ padding: "6px 10px", background: "var(--bg3)", color: "var(--muted)", border: "1px solid var(--border)", borderRadius: 6, fontSize: 12, cursor: "pointer" }}>✕</button>
              </div>
            ) : (
              <button onClick={() => setShowNewSchedule(true)}
                style={{ padding: "8px 14px", background: "var(--bg3)", border: "1px dashed var(--border)", borderRadius: 8, color: "var(--muted)", fontSize: 13, cursor: "pointer" }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = "var(--green)"; e.currentTarget.style.color = "var(--green)"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--border)"; e.currentTarget.style.color = "var(--muted)"; }}>
                + Nova Grade
              </button>
            )}
          </div>
          {schedules.length > 0 && (
            <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 8 }}>
              Grade ativa: <span style={{ color: "var(--green)", fontWeight: 700 }}>{schedules.find(s => s.is_active)?.name || "—"}</span>
              {" "}· Clique em ▶ para ativar outra grade · O remoto também permite trocar
            </div>
          )}
        </div>
      )}

      {/* ── Botão salvar + grid ── */}
      {selectedStore && editingScheduleId && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
          <button onClick={save} disabled={saving} style={{ padding: "10px 20px", background: saved ? "#00cc66" : "var(--green)", color: "#080d08", border: "none", borderRadius: 10, fontWeight: 700, fontSize: 13 }}>
            {saved ? "✓ Salvo!" : saving ? "Salvando..." : "Salvar Grade"}
          </button>
        </div>
      )}

      {selectedStore && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>

          {/* Coluna esquerda: blocos disponíveis */}
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>

            {/* ── PLAYLISTS MANUAIS recolhível ── */}
            {manualPlaylists.length > 0 && (
              <div style={{ background: "var(--bg2)", border: "1px solid #38bdf830", borderRadius: 12, overflow: "hidden" }}>
                <div onClick={() => setOpenSections(s => ({ ...s, manualPlaylists: !s.manualPlaylists }))}
                  style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px", cursor: "pointer", userSelect: "none" }}>
                  <h3 style={{ fontSize: 14, fontWeight: 700, color: "#38bdf8", letterSpacing: 1, margin: 0 }}>📋 PLAYLISTS MANUAIS</h3>
                  <span style={{ color: "var(--muted)", fontSize: 12 }}>{openSections.manualPlaylists ? "▲" : "▼"}</span>
                </div>
                {openSections.manualPlaylists && (
                  <div style={{ padding: "0 16px 16px", maxHeight: 320, overflowY: "auto", display: "flex", flexDirection: "column", gap: 6 }}>
                    {manualPlaylists.map(p => (
                      <div key={p.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 10px", background: "var(--bg3)", borderRadius: 8, border: "1px solid var(--border)" }}>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>{p.name}</div>
                          <div style={{ fontSize: 11, color: "var(--muted)" }}>{p.track_count > 0 ? `${p.track_count} faixas` : ""}{p.genre ? ` · ${p.genre}` : ""}</div>
                        </div>
                        <button onClick={() => addBlock("album", p.album_id || p.id, p.name)} style={{ padding: "5px 12px", background: "#38bdf820", border: "1px solid #38bdf840", borderRadius: 6, color: "#38bdf8", fontSize: 12, fontWeight: 700, cursor: "pointer", flexShrink: 0 }}>
                          + Add
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}


            {/* ── BIBLIOTECA RADIOIN (Master) recolhível ── */}
            {masterPlaylists.length > 0 && (
              <div style={{ background: "var(--bg2)", border: "1px solid #a78bfa40", borderRadius: 12, overflow: "hidden" }}>
                <div onClick={() => setOpenSections(s => ({ ...s, masterLib: !s.masterLib }))}
                  style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px", cursor: "pointer", userSelect: "none", background: "#a78bfa08" }}>
                  <h3 style={{ fontSize: 14, fontWeight: 700, color: "#a78bfa", letterSpacing: 1, margin: 0 }}>📻 PLAYLISTS RADIOIN</h3>
                  <span style={{ color: "var(--muted)", fontSize: 12 }}>{openSections.masterLib ? "▲" : "▼"}</span>
                </div>
                {openSections.masterLib && (
                  <div style={{ padding: "0 16px 16px", display: "flex", flexDirection: "column", gap: 6 }}>
                    {masterPlaylists.map(a => (
                      <div key={a.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 10px", background: "var(--bg3)", borderRadius: 8, border: "1px solid #a78bfa20" }}>
                        <div style={{ overflow: "hidden", flex: 1, paddingRight: 8 }}>
                          <div style={{ fontWeight: 600, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.name}</div>
                          <div style={{ fontSize: 11, color: "var(--muted)" }}>{a.track_count || 0} faixas{a.genre ? ` · ${a.genre}` : ""}</div>
                        </div>
                        <button onClick={() => addBlock("album", a.album_id || a.id, a.name)} style={{ padding: "5px 12px", background: "#a78bfa20", border: "1px solid #a78bfa40", borderRadius: 6, color: "#a78bfa", fontSize: 12, fontWeight: 700, cursor: "pointer", flexShrink: 0 }}>+ Add</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            {/* ── LOCUÇÕES por categoria ── */}
            <div style={{ background: "var(--bg2)", border: "1px solid #ffd93d30", borderRadius: 12, overflow: "hidden" }}>
              <div onClick={() => setOpenSections(s => ({ ...s, announcements: !s.announcements }))}
                style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px", cursor: "pointer", userSelect: "none" }}>
                <h3 style={{ fontSize: 14, fontWeight: 700, color: "var(--yellow)", letterSpacing: 1, margin: 0 }}>🎙 LOCUÇÕES</h3>
                <span style={{ color: "var(--muted)", fontSize: 12 }}>{openSections.announcements ? "▲" : "▼"}</span>
              </div>
              {openSections.announcements && (
                <div style={{ padding: "0 16px 16px", display: "flex", flexDirection: "column", gap: 10 }}>

                  {annCats.length === 0 ? (
                    /* Sem categorias — bloco pool geral (comportamento anterior) */
                    <div style={{ padding: "10px 12px", background: "#ffd93d10", border: "1px dashed #ffd93d60", borderRadius: 8 }}>
                      <div style={{ fontSize: 12, color: "var(--yellow)", fontWeight: 700, marginBottom: 4 }}>🔄 Bloco LOCUÇÕES (rodízio automático)</div>
                      <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 8 }}>
                        Toca uma locução por vez em sequência. Crie categorias no Admin para ter blocos separados.
                      </div>
                      <button
                        onClick={() => {
                          setBlocks(prev => [...prev, { type: "announcement_pool", name: "🎙 LOCUÇÕES (rodízio)", qty: 1, category_id: null }]);
                        }}
                        style={{ padding: "5px 14px", background: "#ffd93d25", border: "1px solid #ffd93d50", borderRadius: 6, color: "var(--yellow)", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                        + Adicionar à grade
                      </button>
                      {announcements.length > 0 && (
                        <div style={{ marginTop: 10, fontSize: 11, color: "var(--muted)" }}>
                          {announcements.length} locução{announcements.length > 1 ? "ões" : ""} no rodízio
                        </div>
                      )}
                    </div>
                  ) : (
                    /* Com categorias — um bloco por categoria */
                    annCats.map(cat => {
                      const catAnns = announcements.filter(a => a.announcement_category_id === cat.id);
                      const semCat = announcements.filter(a => !a.announcement_category_id);
                      const jaTemBloco = false; // permite múltiplos blocos da mesma categoria
                      return (
                        <div key={cat.id} style={{ padding: "10px 12px", background: "#ffd93d08", border: "1px dashed #ffd93d40", borderRadius: 8 }}>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                            <div>
                              <div style={{ fontSize: 12, color: "var(--yellow)", fontWeight: 700 }}>{cat.icon} {cat.name}</div>
                              <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>
                                {catAnns.length} locução{catAnns.length !== 1 ? "ões" : ""} nesta categoria
                              </div>
                            </div>
                            <button
                              onClick={() => {
                                setBlocks(prev => [...prev, { type: "announcement_pool", name: `${cat.icon} ${cat.name}`, qty: 1, category_id: cat.id }]);
                              }}
                              style={{ padding: "4px 12px", background: "#ffd93d20", border: "1px solid #ffd93d40", borderRadius: 6, color: "var(--yellow)", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
                              + Adicionar
                            </button>
                          </div>
                        </div>
                      );
                    })
                  )}

                  {/* Locuções sem categoria (quando há categorias criadas) */}
                  {annCats.length > 0 && (() => {
                    const semCat = announcements.filter(a => !a.announcement_category_id);
                    if (semCat.length === 0) return null;
                    const jaTemBloco = false; // permite múltiplos
                    return (
                      <div style={{ padding: "10px 12px", background: "#ffd93d05", border: "1px dashed #ffd93d25", borderRadius: 8 }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                          <div>
                            <div style={{ fontSize: 12, color: "var(--muted)", fontWeight: 700 }}>🎙 Sem categoria</div>
                            <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>{semCat.length} locução{semCat.length !== 1 ? "ões" : ""}</div>
                          </div>
                          <button
                            onClick={() => setBlocks(prev => [...prev, { type: "announcement_pool", name: "🎙 Locuções gerais", qty: 1, category_id: null }])}
                            style={{ padding: "4px 12px", background: "#ffd93d10", border: "1px solid #ffd93d30", borderRadius: 6, color: "var(--yellow)", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
                            + Adicionar
                          </button>
                        </div>
                      </div>
                    );
                  })()}

                </div>
              )}
            </div>
          </div>

          {/* Coluna direita: grade montada */}
          <div style={{ position: "sticky", top: "16px", alignSelf: "flex-start", maxHeight: "calc(100vh - 120px)", overflowY: "auto" }}>
            <div style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 12, padding: "20px", minHeight: 300 }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 14, letterSpacing: 1 }}>⬡ SEQUÊNCIA EM LOOP</h3>
              {(() => {
                const allItems = [...albums, ...playlists, ...manualPlaylists, ...aiAlbums, ...masterAlbums, ...masterPlaylists];
                const emptyBlocks = blocks.filter(b => {
                  if (b.type !== "album") return false;
                  const info = allItems.find(a => a.id === b.album_id);
                  return info !== undefined && (info?.track_count || 0) === 0;
                });
                return emptyBlocks.length > 0 ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: "rgba(255,68,68,0.1)", border: "1px solid #ff446640", borderRadius: 8, marginBottom: 12 }}>
                    <span>⚠️</span>
                    <span style={{ fontSize: 12, color: "#ff9999" }}>
                      {emptyBlocks.length} bloco{emptyBlocks.length > 1 ? "s" : ""} sem faixas — o player vai ignorá-{emptyBlocks.length > 1 ? "los" : "lo"} e pode causar comportamento inesperado
                    </span>
                  </div>
                ) : null;
              })()}
              {blocks.length === 0 ? (
                <div style={{ textAlign: "center", padding: "40px 20px", color: "var(--muted)" }}>
                  <div style={{ fontSize: 32, marginBottom: 12 }}>⬡</div>
                  <p style={{ fontSize: 13 }}>Adicione álbuns e locuções<br />para montar a grade</p>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {blocks.map((b, i) => {
                    const now = new Date();
                    const from = b.valid_from ? new Date(b.valid_from) : null;
                    const until = b.valid_until ? new Date(b.valid_until) : null;
                    const expired = until && now > until;
                    const notStarted = from && now < from;
                    const hasValidity = b.type === "announcement" && (b.valid_from || b.valid_until);

                    // ── Bloco especial: pool de locuções em rodízio ──
                    if (b.type === "announcement_pool") {
                      const cat = annCats.find(c => c.id === b.category_id);
                      // Conta só o que entra no rodízio: sem horário fixo (⏰) + compartilhadas de outras lojas
                      const catAnns = (b.category_id
                        ? [...announcements, ...sharedAnns].filter(a => a.announcement_category_id === b.category_id)
                        : announcements.filter(a => !a.announcement_category_id)
                      ).filter(a => !a.schedule_times);
                      return (
                        <div key={i} style={{ borderRadius: 8, background: "var(--bg3)", border: "1px solid #ffd93d50", overflow: "hidden" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 12px" }}>
                            <span style={{ fontSize: 16 }}>{cat ? cat.icon : "🔄"}</span>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 700, fontSize: 13, color: "var(--yellow)" }}>{b.name || "LOCUÇÕES (rodízio)"}</div>
                              <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>
                                {catAnns.length === 0
                                  ? "⚠️ Nenhuma locução disponível — será pulado"
                                  : `${catAnns.length} locução${catAnns.length > 1 ? "ões" : ""} no rodízio`}
                              </div>
                            </div>
                            <button onClick={() => moveBlock(i, -1)} disabled={i === 0} style={{ background: "none", border: "none", color: "var(--muted)", cursor: "pointer", fontSize: 14, padding: "2px 4px" }}>▲</button>
                            <button onClick={() => moveBlock(i, 1)} disabled={i === blocks.length - 1} style={{ background: "none", border: "none", color: "var(--muted)", cursor: "pointer", fontSize: 14, padding: "2px 4px" }}>▼</button>
                            <button onClick={() => removeBlock(i)} style={{ background: "none", border: "1px solid #ff446640", borderRadius: 5, color: "var(--red)", fontSize: 13, width: 26, height: 26, cursor: "pointer" }}
                              onMouseEnter={e => e.currentTarget.style.background = "#ff446620"}
                              onMouseLeave={e => e.currentTarget.style.background = "none"}>✕</button>
                          </div>
                        </div>
                      );
                    }

                    // Detecta álbum sem faixas — o player vai ignorá-lo silenciosamente
                    const allItems = [...albums, ...playlists, ...manualPlaylists, ...aiAlbums, ...masterAlbums, ...masterPlaylists];
                    const albumInfo = b.type === "album" ? allItems.find(a => a.id === b.album_id) : null;
                    const isEmpty = b.type === "album" && albumInfo !== undefined && (albumInfo?.track_count || 0) === 0;

                    return (
                    <div key={i} style={{ borderRadius: 8, background: "var(--bg3)", border: `1px solid ${isEmpty ? "#ff446680" : expired ? "#ff446640" : b.type === "album" ? "#00ff7f30" : "#ffd93d30"}`, overflow: "hidden", opacity: expired ? 0.5 : 1 }}>
                      {isEmpty && (
                        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 12px", background: "rgba(255,68,68,0.12)", borderBottom: "1px solid #ff446640" }}>
                          <span style={{ fontSize: 12 }}>⚠️</span>
                          <span style={{ fontSize: 11, color: "#ff6666" }}>Álbum sem faixas — será ignorado pelo player</span>
                        </div>
                      )}
                      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 12px" }}>
                        <span style={{ fontSize: 16 }}>{b.type === "album" ? "🎵" : "🎙"}</span>
                        <div style={{ flex: 1, overflow: "hidden" }}>
                          <div style={{ fontWeight: 600, fontSize: 13, color: b.type === "album" ? "var(--green)" : "var(--yellow)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{b.name}</div>
                          <div style={{ fontSize: 11, color: "var(--muted)", display: "flex", gap: 8, marginTop: 2 }}>
                            <span>{b.type === "album" ? "álbum" : "locução"}</span>
                            {expired && <span style={{ color: "var(--red)" }}>● expirada</span>}
                            {notStarted && <span style={{ color: "var(--yellow)" }}>● agendada</span>}
                            {hasValidity && !expired && !notStarted && <span style={{ color: "var(--green)" }}>● ativa</span>}
                          </div>
                        </div>
                        {b.type === "album" && (
                          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                            <span style={{ fontSize: 11, color: "var(--muted)" }}>qtd:</span>
                            <input type="number" min="1" max="20" value={b.qty} onChange={e => updateQty(i, e.target.value)}
                              style={{ width: 46, padding: "3px 6px", fontSize: 12, textAlign: "center" }} />
                          </div>
                        )}
                        <button onClick={() => moveBlock(i, -1)} disabled={i === 0} style={{ background: "none", border: "none", color: "var(--muted)", cursor: "pointer", fontSize: 14, padding: "2px 4px" }}>▲</button>
                        <button onClick={() => moveBlock(i, 1)} disabled={i === blocks.length - 1} style={{ background: "none", border: "none", color: "var(--muted)", cursor: "pointer", fontSize: 14, padding: "2px 4px" }}>▼</button>
                        <button onClick={() => removeBlock(i)} style={{ background: "none", border: "1px solid #ff446640", borderRadius: 5, color: "var(--red)", fontSize: 13, width: 26, height: 26, cursor: "pointer" }}
                          onMouseEnter={e => e.currentTarget.style.background = "#ff446620"}
                          onMouseLeave={e => e.currentTarget.style.background = "none"}>✕</button>
                      </div>
                      {b.type === "announcement" && (
                        <div style={{ padding: "8px 12px 10px", borderTop: "1px solid var(--border)", background: "rgba(0,0,0,0.2)", display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                          <span style={{ fontSize: 11, color: "var(--muted)", minWidth: 60 }}>⏰ Válido:</span>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <span style={{ fontSize: 11, color: "var(--muted)" }}>de</span>
                            <input type="datetime-local" value={b.valid_from || ""} onChange={e => updateValidity(i, "valid_from", e.target.value)}
                              style={{ fontSize: 11, padding: "3px 6px", background: "var(--bg3)", border: "1px solid var(--border)", borderRadius: 6, color: "var(--text)" }} />
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <span style={{ fontSize: 11, color: "var(--muted)" }}>até</span>
                            <input type="datetime-local" value={b.valid_until || ""} onChange={e => updateValidity(i, "valid_until", e.target.value)}
                              style={{ fontSize: 11, padding: "3px 6px", background: "var(--bg3)", border: "1px solid var(--border)", borderRadius: 6, color: "var(--text)" }} />
                          </div>
                          {(b.valid_from || b.valid_until) && (
                            <button onClick={() => { updateValidity(i, "valid_from", ""); updateValidity(i, "valid_until", ""); }}
                              style={{ fontSize: 11, padding: "2px 8px", background: "none", border: "1px solid var(--border)", borderRadius: 6, color: "var(--muted)", cursor: "pointer" }}>
                              limpar
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                    );
                  })}
                  <div style={{ marginTop: 8, padding: "8px 12px", background: "var(--bg3)", borderRadius: 8, border: "1px dashed var(--border)", textAlign: "center" }}>
                    <span style={{ fontSize: 11, color: "var(--muted)" }}>↩ volta ao início em loop</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── APP PRINCIPAL ─────────────────────────────────────────────────────────────
export default function App() {
  const [token, setToken] = useState(() => localStorage.getItem("radioin_token"));
  const [user, setUser] = useState(() => { try { return JSON.parse(localStorage.getItem("radioin_user")); } catch { return null; } });
  const [page, setPage] = useState("overview");
  const [newClientsCount, setNewClientsCount] = useState(0);
  const [showNewClientsBanner, setShowNewClientsBanner] = useState(false);
  const [newClientsList, setNewClientsList] = useState([]);

  // Busca dados atualizados do usuário sempre que o token existir
  useEffect(() => {
    if (!token) return;
    apiFetch("/api/auth/me", {}, token)
      .then(u => { setUser(u); localStorage.setItem("radioin_user", JSON.stringify(u)); })
      .catch(() => {});
  }, [token]);

  // Verifica novos clientes (só para Master)
  useEffect(() => {
    if (!token || !user || user.plan !== "master") return;
    const lastSeen = localStorage.getItem("radioin_admin_last_seen") || new Date(0).toISOString();
    apiFetch("/api/admin/users", {}, token).then(d => {
      const list = Array.isArray(d) ? d : [];
      const novos = list.filter(u => new Date(u.created_at) > new Date(lastSeen));
      setNewClientsCount(novos.length);
      setNewClientsList(novos);
      if (novos.length > 0) setShowNewClientsBanner(true);
    }).catch(() => {});
  }, [token, user]);

  function handleNav(id) {
    setPage(id);
    // Ao entrar em Clientes, marca como visto
    if (id === "admin") {
      localStorage.setItem("radioin_admin_last_seen", new Date().toISOString());
      setNewClientsCount(0);
      setNewClientsList([]);
      setShowNewClientsBanner(false);
    }
  }

  function handleLogin(t, u) {
    setToken(t); setUser(u);
    localStorage.setItem("radioin_token", t);
    localStorage.setItem("radioin_user", JSON.stringify(u));
  }

  // ── Entrar como cliente (Master) ─────────────────────────────
  // Guarda a sessão do Master no navegador e troca pra sessão do cliente.
  const [masterBackup, setMasterBackup] = useState(() => localStorage.getItem("radioin_master_token"));

  function handleImpersonate(clientToken, clientUser) {
    localStorage.setItem("radioin_master_token", token);
    localStorage.setItem("radioin_master_user", JSON.stringify(user));
    setMasterBackup(token);
    handleLogin(clientToken, clientUser);
    setPage("overview");
  }

  function handleBackToMaster() {
    const mt = localStorage.getItem("radioin_master_token");
    let mu = null;
    try { mu = JSON.parse(localStorage.getItem("radioin_master_user")); } catch {}
    localStorage.removeItem("radioin_master_token");
    localStorage.removeItem("radioin_master_user");
    setMasterBackup(null);
    if (mt) { handleLogin(mt, mu); setPage("admin"); }
    else { setToken(null); setUser(null); localStorage.removeItem("radioin_token"); localStorage.removeItem("radioin_user"); }
  }

  function handleLogout() {
    // Se está acessando como cliente, "Sair" volta pro Master
    if (localStorage.getItem("radioin_master_token")) { handleBackToMaster(); return; }
    setToken(null); setUser(null);
    localStorage.removeItem("radioin_token");
    localStorage.removeItem("radioin_user");
  }

  // Rota pública do player
  if (window.location.pathname === "/player") return <><G /><Player /></>;
  // Rota pública do controle remoto
  if (window.location.pathname === "/remote") return <><G /><Remote /></>;

  if (!token) return <><G /><Login onLogin={handleLogin} /></>;

  const pages = { overview: <Overview token={token} user={user} onNav={handleNav} />, stores: <Stores token={token} />, playlists: <Playlists token={token} user={user} />, announcements: <Announcements token={token} user={user} />, albums: <Albums token={token} user={user} />, playergrade: <PlayerGrade token={token} />, admin: <Admin token={token} onImpersonate={handleImpersonate} /> };

  return (
    <>
      <G />
      <div style={{ display: "flex", height: "100vh", overflow: "hidden" }}>
        <Sidebar active={page} onNav={handleNav} user={user} onLogout={handleLogout} newClientsCount={newClientsCount} />
        <main style={{ flex: 1, overflow: "hidden", background: "var(--bg)", display: "flex", flexDirection: "column" }}>
          {/* ── Faixa: acessando como cliente ── */}
          {masterBackup && (
            <div style={{ padding: "10px 24px", background: "linear-gradient(90deg, #ffb02e30, #ffb02e12)", borderBottom: "1px solid #ffb02e60", display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
              <span style={{ fontSize: 16 }}>👁</span>
              <div style={{ flex: 1, fontSize: 13 }}>
                <span style={{ color: "#ffb02e", fontWeight: 700 }}>Você está acessando como: {user?.name || "cliente"}</span>
                <span style={{ color: "var(--muted)", marginLeft: 8 }}>{user?.email}</span>
              </div>
              <button onClick={handleBackToMaster}
                style={{ padding: "6px 16px", background: "#ffb02e", color: "#080d08", border: "none", borderRadius: 8, fontWeight: 700, fontSize: 12, cursor: "pointer" }}>
                ← Voltar ao Master
              </button>
            </div>
          )}
          {/* ── Banner de novos clientes ── */}
          {showNewClientsBanner && user?.plan === "master" && newClientsList.length > 0 && (
            <div className="fadeIn" style={{ padding: "10px 24px", background: "linear-gradient(90deg, rgba(0,255,127,0.12), rgba(0,255,127,0.06))", borderBottom: "1px solid #00ff7f30", display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
              <span style={{ fontSize: 16 }}>🎉</span>
              <div style={{ flex: 1 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: "var(--green)" }}>
                  {newClientsList.length === 1
                    ? `Novo cliente cadastrado: ${newClientsList[0].name}`
                    : `${newClientsList.length} novos clientes cadastrados`}
                </span>
                {newClientsList.length > 1 && (
                  <span style={{ fontSize: 12, color: "var(--muted)", marginLeft: 8 }}>
                    {newClientsList.map(c => c.name).join(", ")}
                  </span>
                )}
              </div>
              <button onClick={() => handleNav("admin")}
                style={{ padding: "5px 14px", background: "var(--green)", color: "#080d08", border: "none", borderRadius: 8, fontWeight: 700, fontSize: 12, cursor: "pointer" }}>
                Ver clientes
              </button>
              <button onClick={() => setShowNewClientsBanner(false)}
                style={{ background: "none", border: "none", color: "var(--muted)", fontSize: 18, cursor: "pointer", lineHeight: 1 }}>✕</button>
            </div>
          )}
          <div style={{ flex: 1, overflow: "hidden" }}>
            {pages[page]}
          </div>
        </main>
      </div>
    </>
  );
}
